import './player.css';
import { bindMediaLoading } from '../../shared/media-loading';
import { playerCopy } from '../../shared/player-copy';
import { browserSessionId } from '../../shared/browser-compat';
import { resolveScrollDock } from '../../shared/scroll-dock';
import { hasLauncherMedia, resolveWidgetStart } from '../../shared/initial-widget-state';
import { advisorFilterSteps, advisorSelection, catalogMatchesFacets, portfolioFallbackNote, rankPortfolioWithFallback } from '../../shared/portfolio-matching';
import { acceptsPortfolioMessage, readPortfolioGallery, readPortfolioAnswers, portfolioGoalMatches } from '../../shared/portfolio-bridge';
import { PORTFOLIO_MANUAL_CONTROLS, PortfolioFilterController, normalizePortfolioFilterRule, type PortfolioFilterBridge, type PortfolioGalleryHandle } from '../../shared/portfolio-filter-rule';
import { calculateNeedProfile, evidenceFromMutations, rankRecommendations, resolveChoiceTransition } from '../../shared/need-engine';
import type {
  ChoiceOption,
  FlowGraph,
  FlowNode,
  FramefieldReference,
  HistoryEntry,
  NeedEvidence,
  NeedMutation,
  NeedProfile,
  PlayerSession,
  Recommendation,
} from '../../shared/types';
import { repairFlowGraphStart } from '../../shared/graph-integrity';
import { resolveInteractionLayout } from '../../shared/interaction-layout';
import { isLiteEnvironment, playerDesignVars, resolvePlayerDesign, SPRING_SETTLE, type PlayerDesign } from '../../shared/player-design';
import { LIQUID_GLASS_STYLE } from './designs/liquid-glass';

interface PageCompanionState {
  nodeId: string;
  targetPath: string;
  title: string;
  eyebrow: string;
  message: string;
  video: string;
  poster: string;
  highlightMinimize: boolean;
  createdAt: number;
}

const SOUND_ICON = '<svg class="sound-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M4.5 9.25h3.25L12 5.75v12.5l-4.25-3.5H4.5z"></path><path class="sound-icon__wave" d="M15.25 9a4.1 4.1 0 0 1 0 6"></path><path class="sound-icon__wave" d="M18 6.75a7.3 7.3 0 0 1 0 10.5"></path><path class="sound-icon__slash" d="m5 5 14 14"></path></svg>';

const STYLE = `
  :host([data-portfolio-foreground]) { position:fixed!important; inset:0!important; width:0!important; height:0!important; z-index:2147483647!important; }
  :host { --accent:#48d7ee; --bg:#050a12; --surface:#101c2e; --text:#f4f8fb; --muted:#94a6b8; --radius:22px; --font:Inter,ui-sans-serif,system-ui,sans-serif; --heading-font:var(--font); --body-font:var(--font); --button-font:var(--font); --border-width:1px; color:var(--text); font-family:var(--body-font); }
  * { box-sizing:border-box; }
  button,a { font:inherit; }
  button { color:inherit; }
  .shell { position:relative; width:100%; container-type:inline-size; }
  :host([data-scroll-dock]) { display:block; }
  :host([data-scroll-dock]) .stage { min-height:0; }
  :host([data-scroll-floating]) .shell { position:fixed!important; inset:auto 12px max(12px,env(safe-area-inset-bottom)) auto!important; width:min(300px,40vh,calc(100vw - 24px))!important; z-index:2147483646; }
  :host([data-scroll-floating]) .stage { aspect-ratio:9/16!important; min-height:0!important; height:auto!important; }
  :host([data-scroll-floating]) .content { max-height:64%!important; overflow-y:auto; }
  :host([data-scroll-floating]) h2 { font-size:clamp(16px,6cqw,22px)!important; }
  :host([data-scroll-floating]) .body { font-size:clamp(11px,4cqw,14px)!important; }
  :host([data-scroll-floating]) .choice { min-height:40px!important; padding:8px!important; font-size:clamp(11px,4cqw,14px)!important; }
  .scroll-dock-controls { display:none; }
  :host([data-scroll-floating]) .scroll-dock-controls { display:flex; gap:8px; justify-content:space-between; padding:6px; background:var(--bg); border-radius:10px; }
  .scroll-dock-controls button { padding:7px; border:1px solid var(--accent); border-radius:8px; color:var(--text); background:var(--surface); cursor:pointer; font-size:12px; }
  .stage { position:relative; width:100%; overflow:hidden; isolation:isolate; container-type:inline-size; border:1px solid color-mix(in srgb,var(--accent),transparent 78%); border-radius:var(--radius); background:#02060b; box-shadow:0 28px 80px rgba(1,7,16,.28),0 0 var(--glow-blur,18px) color-mix(in srgb,var(--accent),transparent 82%); aspect-ratio:var(--ratio,16/9); min-height:340px; }
  .stage.frame-neon { border-color:color-mix(in srgb,var(--accent),transparent 34%); box-shadow:0 28px 80px rgba(1,7,16,.3),0 0 0 1px color-mix(in srgb,var(--accent-2),transparent 76%),0 0 calc(var(--glow-blur,18px) * 1.45) color-mix(in srgb,var(--accent),transparent 62%); }
  .stage.frame-glass { border-color:rgba(255,255,255,.44); box-shadow:inset 0 1px 0 rgba(255,255,255,.18),0 28px 80px rgba(1,7,16,.25),0 0 calc(var(--glow-blur,18px) * .55) color-mix(in srgb,var(--accent),transparent 82%); }
  .stage.frame-minimal { border-color:rgba(255,255,255,.08); box-shadow:0 22px 58px rgba(1,7,16,.24); }
  :host([design-mode]) .stage { cursor:pointer; }
  :host([design-mode]) .choice,:host([design-mode]) .primary,:host([design-mode]) .secondary,:host([design-mode]) .content,:host([design-mode]) .interaction-teaser,:host([design-mode]) .recommendation,:host([design-mode]) .portfolio-peek,:host([design-mode]) .timed-page,:host([design-mode]) h2,:host([design-mode]) .body,:host([design-mode]) .eyebrow { cursor:crosshair; }
  :host([design-mode]) .choice:hover,:host([design-mode]) .primary:hover,:host([design-mode]) .secondary:hover,:host([design-mode]) .content:hover,:host([design-mode]) .recommendation:hover,:host([design-mode]) .portfolio-peek:hover,:host([design-mode]) .timed-page:hover,:host([design-mode]) h2:hover,:host([design-mode]) .body:hover,:host([design-mode]) .eyebrow:hover { outline:2px solid color-mix(in srgb,var(--accent),#fff 25%); outline-offset:3px; }
  .stage::after { content:""; position:absolute; inset:0; z-index:1; background:linear-gradient(180deg,rgba(2,6,11,.22),transparent 27%,transparent 55%,rgba(2,6,11,.48)); pointer-events:none; transition:background .4s ease; }
  .stage.is-interaction-revealed::after { background:linear-gradient(180deg,rgba(2,6,11,.22),transparent 35%,rgba(2,6,11,.26) 68%,rgba(2,6,11,.72)); }
  .media { position:absolute; inset:0; z-index:0; width:100%; height:100%; object-fit:cover; object-position:var(--media-focus-x,50%) center; opacity:1; background:#02060b; }
  .media-placeholder { position:absolute; inset:0; z-index:0; background:radial-gradient(circle at 72% 36%,color-mix(in srgb,var(--accent),transparent 55%),transparent 24%),linear-gradient(120deg,#152a43,#050a12 66%); }
  .media-status { position:absolute; left:50%; top:50%; z-index:5; display:flex; align-items:center; gap:9px; max-width:calc(100% - 40px); padding:10px 14px; transform:translate(-50%,-50%); border:1px solid rgba(255,255,255,.16); border-radius:999px; background:rgba(2,8,15,.76); backdrop-filter:blur(16px); color:#fff; font-size:12px; font-weight:800; opacity:0; visibility:hidden; pointer-events:none; transition:.2s ease; }
  .media-status.is-visible { opacity:1; visibility:visible; }
  .media-status.is-loading::before { content:""; width:14px; height:14px; flex:0 0 14px; border:2px solid rgba(255,255,255,.28); border-top-color:var(--accent); border-radius:50%; animation:spin .8s linear infinite; }
  .timed-page { position:relative; z-index:1; display:grid; grid-template-columns:52px minmax(0,1fr) auto; align-items:center; gap:10px; width:100%; max-height:0; margin:0; padding:0 12px; overflow:hidden; border:0 solid color-mix(in srgb,var(--accent),#fff 18%); border-radius:14px; background:linear-gradient(125deg,rgba(8,18,31,.9),rgba(10,26,43,.82)); box-shadow:0 12px 32px rgba(0,0,0,.2),0 0 calc(var(--glow-blur,18px) * .55) color-mix(in srgb,var(--accent),transparent 84%); opacity:0; visibility:hidden; transform:translateY(8px); pointer-events:none; transition:max-height .42s cubic-bezier(.22,1,.36,1),margin .42s cubic-bezier(.22,1,.36,1),padding .42s cubic-bezier(.22,1,.36,1),opacity .28s ease,transform .42s cubic-bezier(.22,1,.36,1),visibility 0s linear .42s,border-width .2s ease; }
  .timed-page--no-media { grid-template-columns:minmax(0,1fr) auto; }
  .timed-page.is-visible { max-height:104px; margin:10px 0 2px; padding:10px 12px; border-width:1px; opacity:1; visibility:visible; transform:translateY(0); pointer-events:auto; transition-delay:0s; }
  .timed-page__media { display:block; width:56px; height:56px; overflow:hidden; border-radius:11px; background:linear-gradient(135deg,color-mix(in srgb,var(--accent),#17324a 70%),#0b1828); }.timed-page__media img { display:block; width:100%; height:100%; object-fit:cover; }
  .timed-page__copy { min-width:0; color:inherit; text-decoration:none; }.timed-page__copy small,.timed-page__copy strong,.timed-page__copy span { display:block; }.timed-page__copy small { color:var(--accent); font-size:8px; font-weight:900; letter-spacing:.11em; text-transform:uppercase; }.timed-page__copy strong { margin-top:4px; color:#f5f9ff; font-family:var(--heading-font); font-size:14px; line-height:1.16; }.timed-page__copy span { display:-webkit-box; margin-top:4px; overflow:hidden; color:color-mix(in srgb,var(--text),transparent 34%); font-size:10px; line-height:1.3; -webkit-box-orient:vertical; -webkit-line-clamp:2; }
  .timed-page__arrow { color:var(--accent); font-size:18px; text-decoration:none; transition:transform .18s ease; }.timed-page:hover .timed-page__arrow { transform:translateX(3px); }
  .timed-page__dismiss { position:absolute; top:4px; right:4px; display:grid; place-items:center; width:20px; height:20px; padding:0; border:0; border-radius:50%; background:transparent; color:#9cb0c2; cursor:pointer; }.timed-page__dismiss:hover,.timed-page__dismiss:focus-visible { background:rgba(255,255,255,.08); color:var(--accent); outline:none; }
  .topbar { position:absolute; top:0; left:0; right:0; z-index:8; display:flex; justify-content:space-between; align-items:center; gap:14px; padding:20px 22px; pointer-events:none; }
  .topbar>div { display:flex; gap:9px; pointer-events:auto; }
  .widget-drag-handle { display:none; }
  .widget-size-button { display:none; }
  .player-brand { display:block; width:auto; max-width:120px; height:34px; object-fit:contain; object-position:left center; filter:drop-shadow(0 4px 14px rgba(0,0,0,.35)); }
  .journey-timeline { position:absolute; left:0; right:0; bottom:0; z-index:10; display:block; height:4px; color:rgba(255,255,255,.76); pointer-events:none; }
  .journey-timeline__track { position:absolute; inset:0; height:4px; overflow:visible; background:rgba(255,255,255,.2); box-shadow:0 -1px 8px rgba(0,0,0,.22); }
  .journey-timeline__fill { display:block; width:var(--overall-progress,0%); height:100%; overflow:hidden; border-radius:inherit; background:var(--accent); box-shadow:0 0 12px color-mix(in srgb,var(--accent),transparent 54%); transition:width .12s linear; }
  .journey-timeline__divider { position:absolute; top:50%; width:2px; height:8px; transform:translate(-50%,-50%); border-radius:99px; background:rgba(3,10,18,.66); box-shadow:0 0 0 1px rgba(255,255,255,.16); }
  .journey-timeline__marker { position:absolute; top:50%; left:var(--interaction-point,0%); width:9px; height:9px; transform:translate(-50%,-50%); border:2px solid rgba(3,10,18,.75); border-radius:50%; background:var(--accent); box-shadow:0 0 0 3px color-mix(in srgb,var(--accent),transparent 72%); opacity:var(--marker-visible,0); }
  .end-screen { position:absolute; inset:0; z-index:2; background-position:center; background-size:cover; opacity:0; visibility:hidden; pointer-events:none; transition:opacity .45s ease,visibility .45s ease; }
  .end-screen::before { content:""; position:absolute; inset:0; background:linear-gradient(180deg,rgba(2,7,13,.22),rgba(2,7,13,.62)); }
  .stage.is-video-ended .end-screen { opacity:1; visibility:visible; }
  .stage.is-video-ended.end-mode-hold-frame .end-screen { background:transparent!important; }
  .stage.is-video-ended.end-mode-hold-frame .end-screen::before { background:linear-gradient(180deg,transparent 45%,rgba(2,7,13,.3)); }
  .stage.is-video-ended.end-mode-poster .media,.stage.is-video-ended.end-mode-soft-wait .media { opacity:0; }
  .stage.is-video-ended.end-mode-soft-wait .end-screen { background-position:var(--media-focus-x,50%) center!important; background-size:cover!important; transform:none; animation:none; }
  .icon-btn { display:grid; place-items:center; width:var(--control-size,50px); height:var(--control-size,50px); min-height:var(--control-size,50px); padding:0; border:1px solid rgba(255,255,255,.18); border-radius:50%; background:rgba(2,8,15,.48); backdrop-filter:blur(16px); font-size:var(--control-icon-size,22px); cursor:pointer; transition:.2s ease; }
  .icon-btn:hover,.icon-btn:focus-visible,.media-toggle:hover,.media-toggle:focus-visible,.sound-toggle:hover,.sound-toggle:focus-visible { border-color:var(--accent); transform:translateY(-1px); outline:none; }
  .stage.has-page-companion.highlight-minimize [data-action="close"] { animation:minimize-hint 1.8s ease-in-out .8s 2; }
  @keyframes minimize-hint { 0%,100% { border-color:rgba(255,255,255,.18); box-shadow:none; } 50% { border-color:var(--accent); box-shadow:0 0 0 3px color-mix(in srgb,var(--accent),transparent 82%),0 0 20px color-mix(in srgb,var(--accent),transparent 62%); } }
  .media-toggle,.sound-toggle { position:relative; display:grid; place-items:center; width:var(--control-size,50px); height:var(--control-size,50px); min-height:var(--control-size,50px); padding:0; border:1px solid rgba(255,255,255,.18); border-radius:50%; background:rgba(2,8,15,.56); backdrop-filter:blur(16px); color:var(--text); font-size:0; font-weight:800; cursor:pointer; transition:.2s ease; }
  .media-toggle::before { content:""; position:absolute; top:50%; left:50%; width:0; height:0; transform:translate(-42%,-50%); border-top:6px solid transparent; border-bottom:6px solid transparent; border-left:9px solid currentColor; }
  .media-toggle[data-playing="true"]::before { box-sizing:border-box; width:10px; height:14px; transform:translate(-50%,-50%); border:0; border-right:3px solid currentColor; border-left:3px solid currentColor; }
  .sound-toggle { width:var(--control-sound-size,44px); height:var(--control-sound-size,44px); min-height:var(--control-sound-size,44px); border-color:rgba(255,255,255,.13); background:linear-gradient(145deg,rgba(16,28,42,.62),rgba(2,8,15,.36)); box-shadow:inset 0 1px 0 rgba(255,255,255,.09),0 8px 24px rgba(0,0,0,.12); }
  .sound-icon { width:21px; height:21px; overflow:visible; fill:none; stroke:currentColor; stroke-width:1.8; stroke-linecap:round; stroke-linejoin:round; }
  .sound-icon__wave,.sound-icon__slash { transition:opacity .2s ease,transform .25s cubic-bezier(.22,1,.36,1); transform-origin:center; }
  .sound-icon__slash { opacity:0; transform:scale(.72); stroke:var(--accent); stroke-width:2.2; }
  [data-muted="true"] .sound-icon__wave { opacity:0; transform:scale(.7); }
  [data-muted="true"] .sound-icon__slash { opacity:1; transform:scale(1); }
  .sound-toggle:hover,.sound-toggle:focus-visible { color:#fff; box-shadow:inset 0 1px 0 rgba(255,255,255,.12),0 0 0 1px color-mix(in srgb,var(--accent),transparent 58%),0 0 20px color-mix(in srgb,var(--accent),transparent 76%); }
  .control-style-line .topbar .icon-btn,.control-style-line .topbar .media-toggle,.control-style-line .topbar .sound-toggle { background:transparent; backdrop-filter:none; }
  .control-style-solid .topbar .icon-btn,.control-style-solid .topbar .media-toggle,.control-style-solid .topbar .sound-toggle { border-color:color-mix(in srgb,var(--accent),transparent 60%); background:color-mix(in srgb,var(--surface),#000 12%); backdrop-filter:none; }
  .control-shape-rounded .topbar .icon-btn,.control-shape-rounded .topbar .media-toggle,.control-shape-rounded .topbar .sound-toggle { border-radius:12px; }
  .control-shape-minimal .topbar .icon-btn,.control-shape-minimal .topbar .media-toggle,.control-shape-minimal .topbar .sound-toggle { border-color:transparent; border-radius:8px; background:rgba(2,8,15,.22); box-shadow:none; }
  :host([design-mode]) .design-element-selected { outline:2px solid var(--accent); outline-offset:4px; border-radius:7px; box-shadow:0 0 0 5px color-mix(in srgb,var(--accent),transparent 88%); }
  .start-gate { position:absolute; inset:0; z-index:7; display:grid; place-items:center; padding:24px; background:rgba(2,6,11,.18); opacity:0; visibility:hidden; pointer-events:none; transition:.25s ease; }
  .start-gate.is-visible { opacity:1; visibility:visible; pointer-events:auto; }
  .start-gate__card { display:grid; justify-items:center; gap:14px; width:min(440px,100%); padding:24px; border:1px solid rgba(255,255,255,.2); border-radius:22px; background:rgba(3,10,18,.78); backdrop-filter:blur(18px); box-shadow:0 20px 60px rgba(0,0,0,.34); text-align:center; }
  .start-gate__greeting { margin:0; color:var(--text); font-size:clamp(25px,4vw,38px); font-weight:850; line-height:1.05; letter-spacing:-.035em; text-wrap:balance; }
  .start-gate button { display:flex; align-items:center; gap:12px; min-height:58px; padding:14px 22px; border:1px solid rgba(255,255,255,.28); border-radius:999px; background:rgba(3,10,18,.74); backdrop-filter:blur(18px); color:#fff; font-weight:900; cursor:pointer; box-shadow:0 16px 50px rgba(0,0,0,.3); }
  .start-gate__card button { background:var(--accent); color:#031019; }
  .start-gate i { position:relative; display:block; width:34px; height:34px; border-radius:50%; background:var(--accent); color:#031019; font-style:normal; }
  .start-gate i::before { content:""; position:absolute; top:50%; left:50%; width:0; height:0; transform:translate(-42%,-50%); border-top:6px solid transparent; border-bottom:6px solid transparent; border-left:9px solid currentColor; }
  .location-prelude { position:absolute; inset:0; z-index:12; display:grid; place-items:center; padding:24px; background:linear-gradient(145deg,rgba(3,10,18,.74),rgba(3,10,18,.34)); backdrop-filter:blur(9px); }
  .location-prelude__card { width:min(440px,100%); padding:clamp(22px,4vw,34px); border:1px solid rgba(255,255,255,.17); border-radius:22px; background:rgba(6,15,26,.9); box-shadow:0 24px 80px rgba(0,0,0,.4); text-align:center; }
  .location-prelude__card>small:first-child { color:var(--accent); font-size:10px; font-weight:850; letter-spacing:.14em; text-transform:uppercase; }
  .location-prelude h2 { margin-top:9px; font-size:clamp(24px,4vw,34px); }
  .location-prelude p { margin:10px auto 0; color:color-mix(in srgb,var(--text),transparent 20%); font-size:14px; line-height:1.45; }
  .location-prelude__actions { display:grid; gap:9px; margin-top:21px; }
  .location-prelude .primary,.location-prelude .secondary { width:100%; min-height:48px; }
  .location-prelude__privacy { display:block; margin-top:14px; color:var(--muted); font-size:10px; line-height:1.4; }
  .interaction-teaser { position:absolute; left:0; right:0; bottom:0; z-index:7; display:grid; place-items:center; width:100%; height:34px; padding:0; overflow:hidden; border:0; border-top:1px solid rgba(255,255,255,.09); border-radius:13px 13px 0 0; background:linear-gradient(180deg,rgba(7,14,23,.12),rgba(7,14,23,.56)); backdrop-filter:blur(9px); color:var(--accent); cursor:pointer; transition:height .42s cubic-bezier(.22,1,.36,1),opacity .25s ease,transform .45s cubic-bezier(.22,1,.36,1),background .25s ease; }
  .interaction-teaser i { position:relative; width:24px; height:18px; opacity:.78; transition:transform .35s cubic-bezier(.22,1,.36,1); }
  .interaction-teaser i::before,.interaction-teaser i::after { content:""; position:absolute; left:6px; width:9px; height:9px; border-top:2px solid currentColor; border-left:2px solid currentColor; transform:rotate(45deg); }
  .interaction-teaser i::before { top:6px; }
  .interaction-teaser i::after { top:0; }
  .interaction-teaser span { position:absolute!important; width:1px!important; height:1px!important; overflow:hidden!important; clip:rect(1px,1px,1px,1px)!important; white-space:nowrap!important; }
  .interaction-preview { position:absolute; top:50px; left:24px; right:24px; display:-webkit-box; max-height:2.3em; overflow:hidden; opacity:0; transform:translateY(8px); color:var(--text); font-size:clamp(15px,1.8vw,23px); line-height:1.12; text-align:center; overflow-wrap:anywhere; -webkit-box-orient:vertical; -webkit-line-clamp:2; transition:.3s ease; }
  .stage.is-interaction-revealed .interaction-teaser { opacity:0; transform:translateY(24px); pointer-events:none; }
  .content { position:absolute; left:0; right:0; bottom:0; z-index:6; display:flex; flex-direction:column; justify-content:flex-start; width:100%; max-height:52%; min-height:0; padding:44px clamp(20px,3vw,38px) clamp(21px,3vw,29px); overflow-x:hidden; overflow-y:auto; overscroll-behavior-y:contain; touch-action:pan-y; scrollbar-width:thin; scrollbar-color:color-mix(in srgb,var(--accent),transparent 38%) transparent; scrollbar-gutter:stable; border:1px solid color-mix(in srgb,var(--accent),transparent 64%); border-bottom:0; border-radius:24px 24px 0 0; background:linear-gradient(145deg,rgba(7,14,23,.93),rgba(3,9,16,.97)); backdrop-filter:blur(20px); box-shadow:0 -14px 48px rgba(0,0,0,.22),0 -4px var(--glow-blur,18px) color-mix(in srgb,var(--accent),transparent 78%); opacity:.98; transform:translateY(calc(100% + 8px)); pointer-events:none; transition:transform .58s cubic-bezier(.22,1,.36,1); }
  .content::-webkit-scrollbar { width:6px; }.content::-webkit-scrollbar-track { background:transparent; }.content::-webkit-scrollbar-thumb { border:2px solid transparent; border-radius:999px; background:color-mix(in srgb,var(--accent),transparent 38%); background-clip:padding-box; }
  .content>* { flex-shrink:0; }
  .pattern-grid .content { background-image:linear-gradient(color-mix(in srgb,var(--accent),transparent 91%) 1px,transparent 1px),linear-gradient(90deg,color-mix(in srgb,var(--accent-2),transparent 92%) 1px,transparent 1px),linear-gradient(115deg,transparent 34%,color-mix(in srgb,var(--accent),transparent 94%) 48%,transparent 62%),radial-gradient(circle at 18% 0,color-mix(in srgb,var(--accent),transparent 78%),transparent 38%),linear-gradient(145deg,rgba(7,14,23,.94),rgba(3,9,16,.98)); background-size:26px 26px,26px 26px,220% 220%,auto,auto; background-position:0 0,0 0,100% 100%,0 0,0 0; animation:grid-drift 22s linear infinite; }
  .pattern-lines .content { background-image:repeating-linear-gradient(115deg,color-mix(in srgb,var(--accent),transparent 94%) 0 1px,transparent 1px 18px),radial-gradient(circle at 18% 0,color-mix(in srgb,var(--accent),transparent 80%),transparent 40%),linear-gradient(145deg,rgba(7,14,23,.95),rgba(3,9,16,.98)); }
  .pattern-dots .content { background-image:radial-gradient(circle,color-mix(in srgb,var(--accent),transparent 82%) 1px,transparent 1.5px),radial-gradient(circle at 18% 0,color-mix(in srgb,var(--accent),transparent 80%),transparent 40%),linear-gradient(145deg,rgba(7,14,23,.95),rgba(3,9,16,.98)); background-size:18px 18px,auto,auto; }
  .panel-solid .content { background:color-mix(in srgb,var(--surface),var(--bg) 18%); backdrop-filter:none; }
  .panel-gradient .content { background-image:radial-gradient(circle at 10% 0,color-mix(in srgb,var(--accent),transparent 72%),transparent 42%),linear-gradient(135deg,color-mix(in srgb,var(--surface),var(--accent-2) 7%),color-mix(in srgb,var(--bg),#000 8%)); backdrop-filter:blur(18px); }
  .panel-solid.pattern-grid .content,.panel-gradient.pattern-grid .content { background-image:linear-gradient(color-mix(in srgb,var(--accent),transparent 91%) 1px,transparent 1px),linear-gradient(90deg,color-mix(in srgb,var(--accent-2),transparent 92%) 1px,transparent 1px),linear-gradient(135deg,color-mix(in srgb,var(--surface),var(--accent) 9%),color-mix(in srgb,var(--bg),#000 8%)); background-size:26px 26px,26px 26px,auto; }
  .layout-bottom.pattern-grid .content { border-top-color:color-mix(in srgb,var(--accent),transparent 38%); border-right-color:color-mix(in srgb,var(--accent),transparent 58%); border-left-color:color-mix(in srgb,var(--accent),transparent 58%); background-clip:padding-box; box-shadow:0 -14px 48px rgba(0,0,0,.22),0 -4px var(--glow-blur,18px) color-mix(in srgb,var(--accent),transparent 78%),inset 0 1px 0 color-mix(in srgb,var(--accent),transparent 45%); }
  @keyframes grid-drift { to { background-position:26px 26px,-26px 26px,0 0,0 0,0 0; } }
  .stage.is-interaction-revealed .content { transform:translateY(0); pointer-events:auto; }
  .content::before { display:none; }
  .collapse-interaction { position:absolute; top:5px; left:50%; z-index:2; display:grid; place-items:center; width:52px; height:34px; min-height:34px; margin:0; padding:0; transform:translateX(-50%); border:0; background:transparent; color:rgba(255,255,255,.64); font-size:0; cursor:pointer; transition:.2s ease; }
  .collapse-interaction span { position:relative; width:26px; height:22px; font-size:0; transform:scale(.72); }
  .collapse-interaction span::before,.collapse-interaction span::after { content:""; position:absolute; left:6px; width:11px; height:11px; border-right:3px solid currentColor; border-bottom:3px solid currentColor; transform:rotate(45deg); }
  .collapse-interaction span::before { top:0; }
  .collapse-interaction span::after { top:8px; }
  .collapse-interaction:hover,.collapse-interaction:focus-visible { color:var(--accent); transform:translate(-50%,-1px); outline:none; }
  .wait-hint { position:absolute; left:clamp(9px,2.4cqw,16px); bottom:calc(var(--interaction-panel-height,0px) + 6px); z-index:7; display:flex; align-items:center; width:max-content; max-width:calc(100% - 48px); min-height:29px; box-sizing:border-box; padding:5px; overflow:hidden; border:1px solid color-mix(in srgb,var(--accent),transparent 68%); border-radius:999px; background:rgba(3,10,18,.78); backdrop-filter:blur(14px); color:color-mix(in srgb,var(--text),transparent 12%); font-size:10px; font-weight:750; line-height:1.25; opacity:0; transform:translateY(5px) scale(.94); transform-origin:left center; pointer-events:none; will-change:opacity,transform; transition:background .24s ease,box-shadow .24s ease; }
  .wait-hint i { display:grid; place-items:center; width:17px; height:17px; flex:0 0 17px; border:1px solid color-mix(in srgb,var(--accent),transparent 28%); border-radius:50%; background:color-mix(in srgb,var(--accent),transparent 84%); color:var(--accent); font:800 10px/1 Georgia,serif; }
  .wait-hint span { display:block; max-width:0; margin-left:0; overflow:hidden; opacity:0; transform:translateX(-4px); text-overflow:ellipsis; white-space:nowrap; will-change:max-width,margin-left,opacity,transform; transition:max-width .7s cubic-bezier(.22,1,.36,1),margin-left .7s cubic-bezier(.22,1,.36,1),opacity .28s ease,transform .48s cubic-bezier(.22,1,.36,1); }
  .stage.is-video-ended .wait-hint { pointer-events:auto; animation:wait-island-arrive 6.2s cubic-bezier(.22,1,.36,1) .45s 1 both; }
  .stage.is-video-ended .wait-hint span { animation:wait-island-copy 6.2s cubic-bezier(.22,1,.36,1) .45s 1 both; }
  .stage:not(.is-interaction-revealed) .wait-hint { opacity:0!important; transform:translateY(5px) scale(.94)!important; pointer-events:none!important; animation:none!important; }
  .stage:not(.is-interaction-revealed) .wait-hint span { max-width:0!important; margin-left:0!important; opacity:0!important; animation:none!important; }
  .stage.is-video-ended .wait-hint:hover,.stage.is-video-ended .wait-hint:focus-visible { align-items:flex-start; max-width:min(440px,calc(100% - 48px)); border-radius:15px; background:rgba(3,10,18,.94); box-shadow:0 12px 34px rgba(0,0,0,.26),0 0 var(--glow-blur,18px) color-mix(in srgb,var(--accent),transparent 78%); outline:none; animation:none; opacity:1; transform:none; }
  .stage.is-video-ended .wait-hint:hover span,.stage.is-video-ended .wait-hint:focus-visible span { max-width:380px; margin-left:7px; overflow:visible; opacity:1; transform:none; text-overflow:clip; white-space:normal; animation:none; }
  @keyframes wait-island-arrive { 0% { opacity:0; transform:translateY(5px) scale(.94); box-shadow:none; } 11%,100% { opacity:1; transform:none; } 31%,67% { box-shadow:0 10px 30px rgba(0,0,0,.22),0 0 var(--glow-blur,18px) color-mix(in srgb,var(--accent),transparent 80%); } 84%,100% { box-shadow:none; } }
  @keyframes wait-island-copy { 0%,20% { max-width:0; margin-left:0; opacity:0; transform:translateX(-4px); } 37%,64% { max-width:320px; margin-left:7px; opacity:1; transform:none; } 82%,100% { max-width:0; margin-left:0; opacity:0; transform:translateX(-4px); } }
  .layout-side .content { top:0; bottom:0; left:auto; right:0; width:min(440px,42%); max-height:none; justify-content:flex-start; border-top:0; border-right:0; border-bottom:0; border-radius:26px 0 0 26px; transform:translateX(calc(100% + 8px)); }
  .layout-side .content::before { display:none; }
  .layout-side .interaction-teaser { left:auto; right:12px; bottom:50%; width:auto; padding:16px 9px; transform:translateY(50%); border-radius:16px 8px 8px 16px; writing-mode:vertical-rl; }
  .layout-side .interaction-teaser i { width:3px; height:36px; }
  .layout-side.is-interaction-revealed .interaction-teaser { transform:translate(24px,50%); }
  .layout-side .wait-hint { left:auto; right:12px; bottom:12px; transform-origin:right center; }
  .eyebrow { display:flex; align-items:center; gap:9px; margin:0 0 15px; color:var(--accent); font-size:12px; font-weight:800; letter-spacing:.14em; text-transform:uppercase; }
  .eyebrow::before { content:""; width:28px; height:2px; background:currentColor; }
  .content:has(.choices) .eyebrow { display:none; }
  h2 { max-width:900px; margin:0 auto; font-family:var(--heading-font); font-size:clamp(var(--heading-min,24px),var(--heading-fluid,2.8vw),var(--heading-max,38px)); line-height:1.06; letter-spacing:-.035em; text-align:center; text-wrap:balance; }
  .body { max-width:720px; margin:10px auto 0; color:color-mix(in srgb,var(--text),transparent 18%); font-family:var(--body-font); font-size:clamp(var(--body-min,13px),var(--body-fluid,1.25vw),var(--body-max,16px)); line-height:1.42; text-align:center; }
  .context-summary { display:flex; align-items:center; justify-content:space-between; gap:16px; width:min(760px,100%); margin:17px auto 0; padding:11px 13px 11px 15px; border:1px solid rgba(255,255,255,.11); border-radius:14px; background:rgba(255,255,255,.055); }
  .context-summary>div { min-width:0; }
  .context-summary small { display:block; margin-bottom:6px; color:var(--muted); font-size:9px; font-weight:850; letter-spacing:.1em; text-transform:uppercase; }
  .context-summary p { display:flex; flex-wrap:wrap; gap:6px; margin:0; }
  .context-summary p span { padding:5px 8px; border-radius:999px; background:color-mix(in srgb,var(--surface),var(--accent) 10%); color:var(--text); font-size:11px; font-weight:750; line-height:1; }
  .context-summary button { flex:0 0 auto; padding:8px 10px; border:0; background:transparent; color:var(--accent); font-size:10px; font-weight:800; cursor:pointer; }
  .context-summary button:hover,.context-summary button:focus-visible { text-decoration:underline; outline:none; }
  .choices { display:grid; grid-template-columns:repeat(12,minmax(0,1fr)); grid-auto-flow:row dense; gap:12px; margin-top:17px; perspective:900px; }
  .choice-swipe-shell { position:relative; width:100%; margin-top:17px; }
  .choice-swipe-shell::before,.choice-swipe-shell::after { content:""; position:absolute; z-index:3; top:0; bottom:34px; width:34px; pointer-events:none; opacity:1; transition:opacity .22s ease; }
  .choice-swipe-shell::before { left:0; background:linear-gradient(90deg,color-mix(in srgb,var(--surface),transparent 30%) 0,color-mix(in srgb,var(--surface),transparent 67%) 46%,transparent 100%); }
  .choice-swipe-shell::after { right:0; background:linear-gradient(270deg,color-mix(in srgb,var(--surface),transparent 30%) 0,color-mix(in srgb,var(--surface),transparent 67%) 46%,transparent 100%); }
  .choice-swipe-shell.is-start::before,.choice-swipe-shell.is-end::after { opacity:0; }
  .choices--swipe { display:flex; gap:10px; width:100%; margin-top:0; padding:2px 38px 10px; overflow-x:auto; overscroll-behavior-inline:contain; scroll-behavior:smooth; scroll-padding-inline:38px; scroll-snap-type:x mandatory; scrollbar-width:none; mask-image:linear-gradient(90deg,transparent 0,#000 18px,#000 calc(100% - 18px),transparent 100%); }
  .choices--swipe::-webkit-scrollbar { display:none; }
  .choices--swipe.is-peeking { scroll-snap-type:none; }
  .choices--swipe .choice { flex:0 0 min(360px,46%); grid-column:auto; scroll-snap-align:start; }
  .choice-swipe-controls { display:flex; align-items:center; justify-content:center; gap:11px; min-height:30px; margin-top:2px; }
  .choice-swipe-page { min-width:42px; color:color-mix(in srgb,var(--text),transparent 34%); font-size:11px; font-variant-numeric:tabular-nums; font-weight:750; letter-spacing:.08em; text-align:center; }
  .choice-swipe-page b { color:var(--text); font-size:12px; }
  .choice-swipe-arrow { display:grid; place-items:center; width:30px; height:30px; min-height:30px; padding:0; border:1px solid color-mix(in srgb,var(--accent),transparent 50%); border-radius:999px; background:color-mix(in srgb,var(--surface),var(--accent) 9%); color:var(--accent); font-size:17px; line-height:1; cursor:pointer; box-shadow:0 0 16px color-mix(in srgb,var(--accent),transparent 84%); transition:opacity .18s ease,transform .18s ease,border-color .18s ease,box-shadow .18s ease; }
  .choice-swipe-arrow:hover,.choice-swipe-arrow:focus-visible { transform:scale(1.08); border-color:var(--accent); box-shadow:0 0 20px color-mix(in srgb,var(--accent),transparent 68%); outline:none; }
  .choice-swipe-arrow:disabled { opacity:.2; cursor:default; transform:none; box-shadow:none; }
  .stage.is-interaction-revealed .choice-swipe-shell.is-start:not(.is-follow-up) .choice-swipe-arrow--next:not(:disabled) { animation:swipe-nudge .5s cubic-bezier(.4,0,.2,1) .65s 2; }
  .stage.is-interaction-revealed .choice-swipe-shell.is-start.is-follow-up .choice-swipe-arrow--next:not(:disabled) { animation:swipe-pulse 3.8s ease-in-out .75s infinite; }
  @keyframes swipe-nudge { 0%,100% { transform:translateX(0); border-color:color-mix(in srgb,var(--accent),transparent 50%); box-shadow:0 0 16px color-mix(in srgb,var(--accent),transparent 84%); } 45% { transform:translateX(4px) scale(1.07); border-color:#9cf9ff; color:#b9fbff; box-shadow:0 0 8px color-mix(in srgb,var(--accent),transparent 35%),0 0 25px color-mix(in srgb,var(--accent),transparent 52%); } }
  @keyframes swipe-pulse { 0%,16%,100% { opacity:.72; border-color:color-mix(in srgb,var(--accent),transparent 58%); box-shadow:0 0 10px color-mix(in srgb,var(--accent),transparent 90%); } 8% { opacity:1; border-color:color-mix(in srgb,var(--accent),transparent 35%); box-shadow:0 0 14px color-mix(in srgb,var(--accent),transparent 78%); } }
  .choices--select { display:block; width:min(720px,100%); margin-inline:auto; }
  .choice-select__label { display:block; margin-bottom:7px; color:color-mix(in srgb,var(--text),transparent 20%); font-size:12px; font-weight:750; }
  .choice-select { width:100%; min-height:58px; padding:0 46px 0 16px; border:1px solid color-mix(in srgb,var(--accent),transparent 52%); border-radius:14px; background:color-mix(in srgb,var(--surface),#fff 4%); color:var(--text); font:inherit; font-weight:750; cursor:pointer; outline:none; box-shadow:0 10px 28px rgba(0,0,0,.16),0 0 calc(var(--glow-blur,18px) * .45) color-mix(in srgb,var(--accent),transparent 86%); }
  .choice-select:hover,.choice-select:focus-visible { border-color:var(--accent); box-shadow:0 12px 32px rgba(0,0,0,.2),0 0 calc(var(--glow-blur,18px) * .9) color-mix(in srgb,var(--accent),transparent 72%); }
  .buttons-light .choice-select { background:#f1f6f8; color:#111d2a; color-scheme:light; }
  .choice { position:relative; display:flex; grid-column:span var(--choice-span,6); justify-content:center; align-items:center; gap:10px; min-height:var(--choice-min-height,56px); padding:var(--choice-pad-y,12px) var(--choice-pad-x,16px); border:1px solid rgba(255,255,255,.13); border-radius:13px; background:color-mix(in srgb,var(--surface),transparent 14%); backdrop-filter:blur(18px); text-align:center; cursor:pointer; transition:transform .18s ease,border-color .18s ease,background .18s ease,box-shadow .18s ease; }
  .choice:hover,.choice:focus-visible { transform:translateY(-2px); border-color:var(--button-hover,var(--accent)); background:color-mix(in srgb,var(--surface),var(--button-hover,var(--accent)) 10%); box-shadow:0 10px 30px rgba(0,0,0,.18),0 0 calc(var(--glow-blur,18px) * .55) color-mix(in srgb,var(--button-hover,var(--accent)),transparent 82%); outline:none; }
  .choice,.primary,.secondary,.location,.inquiry-submit,.quick-link,.choice-select { font-family:var(--button-font); }
  .choice>span:first-child { min-width:0; font-size:var(--button-size,15px); line-height:1.2; }
  .choice b { display:block; font-size:var(--button-size,15px); line-height:1.2; }
  .choice small { display:block; margin-top:3px; color:rgba(255,255,255,.72); font-size:12px; }
  @property --choice-neon-angle { syntax:"<angle>"; inherits:false; initial-value:0deg; }
  @property --choice-neon-tail { syntax:"<percentage>"; inherits:false; initial-value:10%; }
  .buttons-light .choice { --choice-neon-angle:0deg; --choice-neon-tail:10%; isolation:isolate; border:1px solid transparent; background:linear-gradient(145deg,rgba(232,240,244,.97),color-mix(in srgb,#e4ebf0,var(--accent) 6%)) padding-box,linear-gradient(115deg,color-mix(in srgb,var(--accent),transparent 76%),color-mix(in srgb,var(--accent-2),transparent 62%),color-mix(in srgb,var(--accent),transparent 80%)) border-box; color:#111d2a; box-shadow:0 7px 22px rgba(0,0,0,.14),0 0 calc(var(--glow-blur,18px) * .55) color-mix(in srgb,var(--accent),transparent 90%); }
  .buttons-light .choice small { color:#526375; }
  .button-motion-orbit.buttons-light .choice:hover,.button-motion-orbit.buttons-light .choice:focus-visible { border-color:transparent; background:linear-gradient(110deg,#f3f7f9 0 27%,color-mix(in srgb,#eaf4f8,var(--button-hover,var(--accent)) 27%) 43%,color-mix(in srgb,#edf1fb,var(--accent-2) 20%) 54%,#f7fafb 70% 100%) padding-box,conic-gradient(from var(--choice-neon-angle),transparent 0 calc(69% - var(--choice-neon-tail)),color-mix(in srgb,var(--button-hover,var(--accent)),transparent 92%) calc(74% - var(--choice-neon-tail)),color-mix(in srgb,var(--button-hover,var(--accent)),transparent 60%) calc(79% - var(--choice-neon-tail)),#d6fcff 76%,var(--button-hover,var(--accent)) 79%,var(--accent-2) 82%,color-mix(in srgb,var(--button-hover,var(--accent)),transparent 78%) 86%,transparent 91% 100%) border-box; background-size:240% 100%,100% 100%; background-position:100% 0,0 0; color:#07111f; animation:choice-neon-orbit 4.2s linear infinite,choice-neon-breathe 4.2s cubic-bezier(.45,0,.55,1) infinite,choice-surface-sweep 3.4s ease-in-out infinite alternate,choice-neon-glow 4.2s ease-in-out infinite; }
  .button-motion-sweep .choice:hover,.button-motion-sweep .choice:focus-visible { background-image:linear-gradient(110deg,color-mix(in srgb,var(--surface),#fff 84%) 0 28%,color-mix(in srgb,#eaf4f8,var(--button-hover,var(--accent)) 34%) 45%,color-mix(in srgb,#edf1fb,var(--accent-2) 25%) 55%,color-mix(in srgb,var(--surface),#fff 88%) 72% 100%); background-size:240% 100%; color:#07111f; animation:choice-surface-sweep 2.8s ease-in-out infinite alternate; }
  .button-motion-lift .choice:hover,.button-motion-lift .choice:focus-visible { transform:translateY(-4px) scale(1.012); box-shadow:0 16px 34px rgba(0,0,0,.24),0 0 calc(var(--glow-blur,18px) * .8) color-mix(in srgb,var(--accent),transparent 76%); animation:none; }
  .button-motion-none .choice:hover,.button-motion-none .choice:focus-visible { transform:none; animation:none; }
  .button-shape-pill .choice,.button-shape-pill .primary,.button-shape-pill .secondary,.button-shape-pill .inquiry-submit,.button-shape-pill .quick-link { border-radius:999px; }
  .button-shape-compact .choice,.button-shape-compact .primary,.button-shape-compact .secondary,.button-shape-compact .inquiry-submit,.button-shape-compact .quick-link { border-radius:7px; }
  .stage { border-width:var(--border-width,1px); }
  .content { border-top-width:var(--border-width,1px); border-right-width:var(--border-width,1px); border-left-width:var(--border-width,1px); }
  .choice,.recommendation,.portfolio-peek,.quick-link,.secondary,.inquiry-submit { border-width:var(--border-width,1px); }
  .density-compact .content { padding-top:38px; padding-bottom:18px; }
  .density-compact .choices,.density-compact .choice-swipe-shell { gap:8px; margin-top:12px; }
  .density-compact .actions,.density-compact .recommendations { margin-top:17px; }
  .density-airy .content { padding-top:52px; padding-bottom:34px; }
  .density-airy .choices,.density-airy .choice-swipe-shell { margin-top:23px; }
  .density-airy .body { margin-top:14px; line-height:1.55; }
  @keyframes choice-neon-orbit { from { --choice-neon-angle:0deg; } to { --choice-neon-angle:360deg; } }
  @keyframes choice-neon-breathe { 0%,100% { --choice-neon-tail:10%; } 50% { --choice-neon-tail:20%; } }
  @keyframes choice-surface-sweep { from { background-position:100% 0,0 0; } to { background-position:-100% 0,0 0; } }
  @keyframes choice-neon-glow { 0%,100% { box-shadow:0 10px 28px rgba(0,0,0,.18),0 0 4px color-mix(in srgb,var(--button-hover,var(--accent)),transparent 62%),0 0 calc(var(--glow-blur,18px) * .7) color-mix(in srgb,var(--button-hover,var(--accent)),transparent 82%); } 50% { box-shadow:0 12px 32px rgba(0,0,0,.2),0 0 9px color-mix(in srgb,var(--button-hover,var(--accent)),transparent 42%),0 0 calc(var(--glow-blur,18px) * 1.25) color-mix(in srgb,var(--button-hover,var(--accent)),transparent 70%); } }
  .arrow { display:none; }
  .stage.aspect-16-9 .content { max-height:min(38%,266px); padding:29px clamp(17px,2.8vw,34px) 12px; scroll-padding:29px 0 12px; }
  .stage.aspect-16-9 .wait-hint { left:clamp(9px,2.4cqw,14px); bottom:calc(var(--interaction-panel-height,0px) + 5px); }
  .stage.aspect-16-9 h2 { font-size:clamp(17px,1.9vw,26px); }
  .stage.aspect-16-9 .body { margin-top:4px; font-size:clamp(10px,.9vw,13px); line-height:1.28; }
  .stage.aspect-16-9 .context-summary { margin-top:7px; padding:6px 9px; }
  .stage.aspect-16-9 .choices { gap:6px; margin-top:7px; }
  .stage.aspect-16-9 .choice { min-height:34px; padding:5px 10px; border-radius:12px; }
  .stage.aspect-16-9 .choice b { font-size:clamp(10px,.9vw,13px); line-height:1.12; }
  .stage.aspect-16-9 .timed-page.is-visible { max-height:88px; margin-top:7px; padding:7px 10px; }
  .stage.aspect-16-9 .recommendations { grid-template-columns:repeat(3,minmax(0,1fr)); gap:7px; margin-top:10px; }
  .stage.aspect-16-9 .recommendation { min-height:112px; padding:0 12px 10px; }
  .stage.aspect-16-9 .recommendation__media { height:58px; margin:0 -12px 9px; }
  .stage.aspect-16-9 .recommendation strong { margin-top:5px; font-size:14px; }
  .stage.aspect-16-9 .recommendation span:not(.recommendation__media) { display:-webkit-box; margin-top:4px; overflow:hidden; font-size:11px; -webkit-box-orient:vertical; -webkit-line-clamp:2; }
  .stage.aspect-16-9 .recommendation em { overflow:hidden; padding-top:6px; font-size:9px; text-overflow:ellipsis; white-space:nowrap; }
  .stage.aspect-1-1 .content { max-height:60%; }
  .stage.aspect-4-5 .content,.stage.aspect-9-16 .content { max-height:64%; }
  .recommendations { display:grid; grid-template-columns:repeat(auto-fit,minmax(min(190px,100%),1fr)); gap:10px; margin-top:25px; }
  .recommendation { display:flex; flex-direction:column; min-height:142px; overflow:hidden; padding:0 17px 17px; border:1px solid rgba(255,255,255,.13); border-radius:16px; background:rgba(11,25,42,.82); color:inherit; text-decoration:none; transition:.2s ease; }
  .recommendation:hover,.recommendation:focus-visible { transform:translateY(-2px); border-color:var(--accent); outline:none; }
  .recommendation__media { display:block; height:88px; margin:0 -17px 14px; overflow:hidden; background:linear-gradient(135deg,color-mix(in srgb,var(--surface),var(--accent) 16%),var(--surface)); }
  .recommendation__media img { display:block; width:100%; height:100%; object-fit:cover; transition:transform .5s cubic-bezier(.22,1,.36,1); }
  .recommendation:hover .recommendation__media img,.recommendation:focus-visible .recommendation__media img { transform:scale(1.035); }
  .recommendation small { color:var(--accent); font-weight:800; letter-spacing:.08em; text-transform:uppercase; }
  .recommendation strong { margin-top:9px; font-size:17px; line-height:1.15; }
  .recommendation span { margin-top:8px; color:var(--muted); font-size:13px; line-height:1.35; }
  .recommendation em { margin-top:auto; padding-top:12px; color:var(--muted); font-size:11px; font-style:normal; }
  .quick-links { display:flex; align-items:center; gap:9px; width:100%; margin:18px auto 0; padding-top:13px; overflow-x:auto; overscroll-behavior-inline:contain; border-top:1px solid rgba(255,255,255,.1); scrollbar-width:thin; }
  .quick-links>small { flex:0 0 auto; color:var(--muted); font-size:9px; font-weight:850; letter-spacing:.1em; text-transform:uppercase; }
  .quick-link { display:inline-flex; align-items:center; gap:8px; min-height:38px; padding:9px 12px; flex:0 0 auto; border:1px solid color-mix(in srgb,var(--accent),transparent 58%); border-radius:999px; background:rgba(255,255,255,.075); color:var(--text); font-size:11px; font-weight:800; text-decoration:none; box-shadow:0 0 var(--glow-blur,18px) color-mix(in srgb,var(--accent),transparent 88%); transition:.18s ease; }
  .quick-link::after { content:'→'; color:var(--accent); }
  .quick-link:hover,.quick-link:focus-visible { border-color:var(--accent); background:rgba(255,255,255,.12); transform:translateY(-1px); outline:none; }
  .portfolio-peek-slot { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:9px; width:100%; margin:16px auto 0; }
  .portfolio-peek-slot:empty { display:none; }
  .portfolio-peek-slot::before { content:attr(data-label); grid-column:1/-1; color:var(--accent); font-size:8px; font-weight:850; letter-spacing:.1em; text-transform:uppercase; }
  .portfolio-peek-note { margin:0 0 6px; color:var(--muted); font-size:12.5px; line-height:1.4; }
  .portfolio-peek { display:grid; grid-template-columns:68px minmax(0,1fr) 24px; align-items:center; gap:9px; width:100%; min-width:0; min-height:62px; margin:0; padding:6px 8px 6px 6px; border:1px solid color-mix(in srgb,var(--accent),transparent 62%); border-radius:15px; background:linear-gradient(120deg,rgba(255,255,255,.075),rgba(255,255,255,.035)); color:var(--text); text-align:left; cursor:pointer; box-shadow:0 10px 28px rgba(0,0,0,.14),0 0 var(--glow-blur,18px) color-mix(in srgb,var(--accent),transparent 90%); transition:transform .22s ease,border-color .22s ease,background .22s ease,box-shadow .22s ease; }
  .portfolio-peek:only-child { grid-column:1/-1; }
  .portfolio-peek:hover,.portfolio-peek:focus-visible { transform:translateY(-2px); border-color:var(--accent); background:linear-gradient(120deg,color-mix(in srgb,var(--surface),var(--accent) 10%),rgba(255,255,255,.055)); box-shadow:0 14px 34px rgba(0,0,0,.2),0 0 calc(var(--glow-blur,18px) * 1.1) color-mix(in srgb,var(--accent),transparent 76%); outline:none; }
  .portfolio-peek__media { position:relative; display:block; width:68px; height:50px; overflow:hidden; border-radius:10px; background:linear-gradient(135deg,color-mix(in srgb,var(--surface),var(--accent) 16%),var(--surface)); }
  .portfolio-peek__media img,.portfolio-peek__media video { display:block; width:100%; height:100%; object-fit:cover; transition:transform .5s cubic-bezier(.22,1,.36,1),filter .3s ease; }
  .portfolio-peek:hover .portfolio-peek__media img,.portfolio-peek:focus-visible .portfolio-peek__media img { transform:scale(1.045); filter:saturate(.9) brightness(.84); }
  .portfolio-peek__media i { position:absolute; left:50%; top:50%; display:grid; place-items:center; width:24px; height:24px; transform:translate(-50%,-50%); border-radius:50%; background:var(--accent); color:#06111c; font-size:10px; font-style:normal; box-shadow:0 5px 16px rgba(0,0,0,.25); }
  .portfolio-peek__copy { display:grid; min-width:0; gap:2px; }
  .portfolio-peek__copy small { color:var(--accent); font-size:8px; font-weight:850; letter-spacing:.1em; text-transform:uppercase; }
  .portfolio-peek__copy strong { overflow:hidden; font-size:12px; line-height:1.15; text-overflow:ellipsis; white-space:nowrap; }
  .portfolio-peek__copy em { overflow:hidden; color:var(--muted); font-size:9px; font-style:normal; text-overflow:ellipsis; white-space:nowrap; }
  .portfolio-peek__arrow { color:var(--accent); font-size:18px; text-align:center; }
  .actions { display:flex; flex-wrap:wrap; gap:10px; margin-top:27px; }
  .primary,.secondary,.location { display:inline-flex; align-items:center; justify-content:center; min-height:52px; padding:13px 20px; border-radius:999px; font-weight:800; text-decoration:none; cursor:pointer; }
  .primary { border:0; background:var(--accent); color:#041019; }
  .secondary,.location { border:1px solid rgba(255,255,255,.2); background:rgba(6,14,24,.6); color:var(--text); }
  .buttons-light .actions .primary,.buttons-light .actions .secondary { border:1px solid rgba(255,255,255,.82); background:#fff; color:#07111f; box-shadow:0 0 var(--glow-blur,18px) color-mix(in srgb,var(--accent),transparent 82%); }
  .status { margin-top:12px; color:var(--muted); font-size:13px; }
  .stage.has-rich-content .content { max-height:86%; }
  .stage.aspect-16-9.has-rich-content .content { max-height:min(52%,366px); }
  .stage.has-inquiry-form .content { max-height:calc(100% - 12px); overscroll-behavior-y:contain; scrollbar-gutter:stable both-edges; }
  .stage.has-inquiry-form .inquiry-wrap { width:100%; min-height:max-content; padding-bottom:4px; }
  .inquiry-form { width:min(900px,100%); margin:18px auto 0; }
  .inquiry-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:11px; }
  .inquiry-field { display:grid; gap:6px; color:color-mix(in srgb,var(--text),transparent 26%); font-size:11px; font-weight:800; letter-spacing:.04em; text-transform:uppercase; }
  .inquiry-field--wide { grid-column:1/-1; }
  .inquiry-field input,.inquiry-field textarea { width:100%; min-height:46px; margin:0; padding:11px 13px; border:1px solid rgba(255,255,255,.16); border-radius:11px; outline:none; background:rgba(255,255,255,.07); color:var(--text); font:500 14px/1.35 var(--font); letter-spacing:0; text-transform:none; transition:.18s ease; }
  .inquiry-field textarea { min-height:72px; resize:vertical; }
  .inquiry-field input::placeholder,.inquiry-field textarea::placeholder { color:color-mix(in srgb,var(--muted),transparent 14%); }
  .inquiry-field input:focus,.inquiry-field textarea:focus { border-color:var(--accent); background:rgba(255,255,255,.1); box-shadow:0 0 0 3px color-mix(in srgb,var(--accent),transparent 82%); }
  .inquiry-consent { display:flex; align-items:flex-start; gap:9px; margin:12px 0 0; color:color-mix(in srgb,var(--text),transparent 20%); font-size:11px; line-height:1.45; }
  .inquiry-consent input { width:17px; height:17px; flex:0 0 17px; margin:1px 0 0; accent-color:var(--accent); }
  .inquiry-consent a { color:var(--accent); }
  .inquiry-submit { display:flex; align-items:center; justify-content:center; width:100%; min-height:52px; margin-top:13px; padding:13px 20px; border:0; border-radius:999px; background:var(--accent); color:#041019; font-weight:900; cursor:pointer; transition:.2s ease; }
  .inquiry-submit:hover,.inquiry-submit:focus-visible { transform:translateY(-1px); box-shadow:0 12px 32px color-mix(in srgb,var(--accent),transparent 72%); outline:none; }
  .inquiry-submit:disabled { cursor:wait; opacity:.7; transform:none; }
  .inquiry-status { min-height:18px; margin:9px 0 0; color:#ffb9c0; font-size:12px; text-align:center; }
  .inquiry-trap { position:absolute!important; left:-10000px!important; width:1px!important; height:1px!important; overflow:hidden!important; }
  .inquiry-success { display:none; width:min(620px,100%); margin:22px auto 0; padding:24px; border:1px solid color-mix(in srgb,var(--accent),transparent 55%); border-radius:18px; background:color-mix(in srgb,var(--surface),var(--accent) 8%); text-align:center; }
  .inquiry-success i { display:grid; place-items:center; width:48px; height:48px; margin:0 auto 14px; border-radius:50%; background:var(--accent); color:#041019; font-size:24px; font-style:normal; font-weight:900; }
  .inquiry-success h3 { margin:0; font-size:clamp(22px,3vw,32px); line-height:1.08; }
  .inquiry-success p { margin:10px 0 0; color:color-mix(in srgb,var(--text),transparent 20%); }
  .inquiry-wrap.is-complete .inquiry-form { display:none; }
  .inquiry-wrap.is-complete .inquiry-success { display:block; }
  .launcher { position:fixed; right:max(18px,env(safe-area-inset-right)); bottom:max(18px,env(safe-area-inset-bottom)); z-index:99990; display:flex; align-items:center; gap:13px; max-width:min(330px,calc(100vw - 36px)); padding:9px 15px 9px 9px; border:1px solid rgba(255,255,255,.16); border-radius:999px; background:#07111f; color:#fff; box-shadow:0 18px 56px rgba(0,0,0,.35); cursor:pointer; }
  .launcher.widget-bottom-left,.launcher.widget-top-left { right:auto; left:max(18px,env(safe-area-inset-left)); }
  .launcher.widget-top-right,.launcher.widget-top-left { top:max(18px,env(safe-area-inset-top)); bottom:auto; }
  .launcher-orb { position:relative; display:block; width:52px; height:52px; flex:0 0 52px; border-radius:50%; background:linear-gradient(145deg,var(--accent),#4189ff); color:#06121f; }
  .launcher-orb::before { content:""; position:absolute; top:50%; left:50%; width:0; height:0; transform:translate(-42%,-50%); border-top:7px solid transparent; border-bottom:7px solid transparent; border-left:11px solid currentColor; }
  .launcher-copy { min-width:0; text-align:left; }
  .launcher-copy small { display:block; color:var(--accent); font-size:10px; font-weight:800; letter-spacing:.1em; text-transform:uppercase; }
  .launcher-copy strong { display:block; overflow:hidden; font-size:14px; text-overflow:ellipsis; white-space:nowrap; }
  .launcher-shell { position:fixed; right:max(18px,env(safe-area-inset-right)); bottom:max(18px,env(safe-area-inset-bottom)); z-index:99990; display:flex; align-items:center; animation:launcher-unfold .52s cubic-bezier(.22,1,.36,1) both; }
  .launcher-shell.widget-bottom-left,.launcher-shell.widget-top-left { right:auto; left:max(18px,env(safe-area-inset-left)); flex-direction:row-reverse; }
  .launcher-shell.widget-top-right,.launcher-shell.widget-top-left { top:max(18px,env(safe-area-inset-top)); bottom:auto; }
  .launcher-shell .launcher { position:relative; inset:auto; padding-right:30px; }
  .launcher-collapse { position:absolute; right:7px; z-index:2; display:grid; place-items:center; width:25px; height:34px; padding:0; border:0; border-radius:999px; background:transparent; color:#9fb2c4; font-size:22px; cursor:pointer; }
  .launcher-shell.widget-bottom-left .launcher-collapse,.launcher-shell.widget-top-left .launcher-collapse { right:auto; left:7px; transform:rotate(180deg); }
  .launcher-collapse:hover,.launcher-collapse:focus-visible { color:#fff; background:rgba(255,255,255,.08); outline:none; }
  .launcher-side-tab,.companion-side-tab { position:fixed; right:0; bottom:max(64px,env(safe-area-inset-bottom)); z-index:99991; display:grid; grid-template-columns:50px 15px; align-items:center; width:66px; min-height:74px; padding:5px 0 5px 6px; overflow:hidden; border:1px solid color-mix(in srgb,var(--accent),transparent 45%); border-right:0; border-radius:18px 0 0 18px; background:linear-gradient(145deg,rgba(7,17,31,.98),rgba(10,28,45,.96)); color:#fff; box-shadow:0 16px 48px rgba(0,0,0,.35),0 0 18px color-mix(in srgb,var(--accent),transparent 76%); cursor:pointer; animation:side-tab-arrive .42s cubic-bezier(.22,1,.36,1) both; }
  .launcher-side-tab.widget-bottom-left,.launcher-side-tab.widget-top-left { right:auto; left:0; grid-template-columns:15px 50px; padding:5px 6px 5px 0; border-right:1px solid color-mix(in srgb,var(--accent),transparent 45%); border-left:0; border-radius:0 18px 18px 0; }
  .launcher-side-tab.widget-top-right,.launcher-side-tab.widget-top-left { top:max(64px,env(safe-area-inset-top)); bottom:auto; }
  .launcher-side-tab .launcher-orb { width:46px; height:46px; }
  .launcher-side-tab i,.companion-side-tab i { font-style:normal; color:var(--accent); font-size:22px; }
  .launcher-side-tab.widget-bottom-left i,.launcher-side-tab.widget-top-left i { order:-1; transform:rotate(180deg); }
  .launcher-side-tab--mobile-start { grid-template-columns:1fr; width:34px; min-height:72px; padding:0; border-radius:16px 0 0 16px; background:linear-gradient(155deg,rgba(9,22,37,.98),rgba(5,13,24,.98)); box-shadow:-10px 14px 38px rgba(0,0,0,.32),0 0 19px color-mix(in srgb,var(--accent),transparent 74%); animation:mobile-side-arrive .56s cubic-bezier(.22,1,.36,1) both,mobile-side-breathe 3.8s ease-in-out .8s infinite; }
  .launcher-side-tab--mobile-start.widget-bottom-left,.launcher-side-tab--mobile-start.widget-top-left { grid-template-columns:1fr; width:34px; padding:0; border-radius:0 16px 16px 0; }
  .launcher-side-tab--mobile-start .launcher-orb { display:none; }
  .launcher-side-tab--mobile-start i { --mobile-chevron-rotation:0deg; display:grid; place-items:center; width:100%; height:100%; color:#dffcff; font-size:25px; text-shadow:0 0 13px color-mix(in srgb,var(--accent),transparent 16%); animation:mobile-side-chevron 2.1s ease-in-out infinite; }
  .launcher-side-tab--mobile-start.widget-bottom-left i,.launcher-side-tab--mobile-start.widget-top-left i { --mobile-chevron-rotation:180deg; order:initial; }
  .launcher-side-tab__thumb { position:relative; display:block; width:46px; height:46px; overflow:hidden; border-radius:50%; background:#0c2234; box-shadow:inset 0 0 0 1px rgba(255,255,255,.16); }
  .launcher-side-tab__thumb img,.launcher-side-tab__thumb video { display:block; width:100%; height:100%; object-fit:cover; object-position:var(--media-focus-x,50%) 30%; pointer-events:none; }
  .launcher-side-tab--media.widget-bottom-left i,.launcher-side-tab--media.widget-top-left i { order:-1; }
  @media (max-width:600px) { .launcher-side-tab--media { grid-template-columns:40px 13px; width:58px; min-height:62px; padding:4px 0 4px 5px; } .launcher-side-tab--media.widget-bottom-left,.launcher-side-tab--media.widget-top-left { grid-template-columns:13px 40px; padding:4px 5px 4px 0; } .launcher-side-tab--media .launcher-side-tab__thumb { width:40px; height:40px; } }
  .companion-side-tab { grid-template-columns:72px 16px; width:89px; min-height:72px; padding-left:5px; }
  .companion-side-tab img { display:block; width:72px; height:62px; object-fit:cover; border-radius:12px; }
  @keyframes side-tab-arrive { from { opacity:0; transform:translateX(22px); } to { opacity:1; transform:none; } }
  @keyframes mobile-side-arrive { from { opacity:0; transform:translateX(100%) scale(.82); } to { opacity:1; transform:none; } }
  @keyframes mobile-side-breathe { 0%,100% { box-shadow:-10px 14px 38px rgba(0,0,0,.32),0 0 13px color-mix(in srgb,var(--accent),transparent 84%); } 50% { box-shadow:-12px 16px 42px rgba(0,0,0,.36),0 0 25px color-mix(in srgb,var(--accent),transparent 66%); } }
  @keyframes mobile-side-chevron { 0%,100% { opacity:.68; transform:translateX(1px) rotate(var(--mobile-chevron-rotation)); } 50% { opacity:1; transform:translateX(-3px) rotate(var(--mobile-chevron-rotation)); } }
  .location-launcher { position:fixed; left:50%; bottom:max(24px,env(safe-area-inset-bottom)); z-index:99992; display:grid; width:min(440px,calc(100vw - 28px)); padding:21px; transform:translateX(-50%); border:1px solid color-mix(in srgb,var(--accent),transparent 46%); border-radius:22px; background:linear-gradient(145deg,rgba(7,17,31,.97),rgba(8,24,39,.95)); color:#fff; box-shadow:0 24px 80px rgba(0,0,0,.38),0 0 24px color-mix(in srgb,var(--accent),transparent 82%); text-align:center; animation:location-card-arrive .5s cubic-bezier(.22,1,.36,1) both; }
  .location-launcher>small { color:var(--accent); font-size:9px; font-weight:900; letter-spacing:.14em; text-transform:uppercase; }
  .location-launcher>strong { margin-top:7px; font-family:var(--heading-font); font-size:22px; letter-spacing:-.025em; }
  .location-launcher>p { margin:8px 0 0; color:#b4c3d1; font-size:12px; line-height:1.45; }
  .location-launcher>div { display:grid; grid-template-columns:1fr 1fr; gap:8px; margin-top:15px; }
  .location-launcher .primary,.location-launcher .secondary { display:grid; place-items:center; min-height:44px; padding:10px 12px; border-radius:999px; cursor:pointer; }
  @keyframes location-card-arrive { from { opacity:0; transform:translate(-50%,14px) scale(.97); } to { opacity:1; transform:translateX(-50%); } }
  @keyframes launcher-unfold { from { opacity:0; transform:translateX(18px) scale(.9); transform-origin:right center; } to { opacity:1; transform:none; } }
  .initial-pip { position:fixed; right:max(18px,env(safe-area-inset-right)); bottom:max(18px,env(safe-area-inset-bottom)); z-index:99991; width:min(232px,calc(100vw - 36px)); overflow:hidden; border:1px solid color-mix(in srgb,var(--accent),transparent 42%); border-radius:18px; background:#07111f; color:#fff; box-shadow:0 20px 64px rgba(0,0,0,.4),0 0 24px color-mix(in srgb,var(--accent),transparent 78%); animation:pip-arrive .58s cubic-bezier(.22,1,.36,1) both; transition:transform .35s cubic-bezier(.22,1,.36,1),box-shadow .3s ease; }
  .initial-pip__glow { position:absolute; z-index:7; inset:0; display:block; width:100%; height:100%; overflow:visible; pointer-events:none; }
  .initial-pip__streak-runner { position:absolute; left:0; top:0; width:0; height:0; offset-path:path("M162 1.5H20A18.5 18.5 0 0 0 1.5 20V88A18.5 18.5 0 0 0 20 106.5H162"); offset-distance:0%; offset-rotate:auto; will-change:offset-distance,opacity; animation:pip-streak-follow-path 7.2s cubic-bezier(.65,0,.35,1) infinite alternate; }
  .initial-pip__streak { position:absolute; left:-30px; top:-1.5px; width:60px; height:3px; border-radius:50%; clip-path:polygon(0 50%,18% 40%,36% 16%,50% 0,64% 16%,82% 40%,100% 50%,82% 60%,64% 84%,50% 100%,36% 84%,18% 60%); background:linear-gradient(90deg,transparent 0%,color-mix(in srgb,var(--accent),transparent 94%) 14%,color-mix(in srgb,var(--accent),transparent 56%) 34%,#dffcff 46%,#fff 50%,#dffcff 54%,color-mix(in srgb,var(--accent),transparent 56%) 66%,color-mix(in srgb,var(--accent),transparent 94%) 86%,transparent 100%); filter:drop-shadow(0 0 2px color-mix(in srgb,var(--accent),transparent 34%)); will-change:transform; animation:pip-streak-kinetic 7.2s cubic-bezier(.4,0,.2,1) infinite alternate; }
  .initial-pip__streak::before { content:""; position:absolute; inset:-3px -5px; border-radius:50%; background:linear-gradient(90deg,transparent,color-mix(in srgb,var(--accent),transparent 82%) 24%,color-mix(in srgb,var(--accent),transparent 18%) 50%,color-mix(in srgb,var(--accent),transparent 82%) 76%,transparent); filter:blur(3px); opacity:.78; }
  .initial-pip.widget-bottom-left .initial-pip__glow,.initial-pip.widget-top-left .initial-pip__glow { transform:scaleX(-1); }
  .initial-pip.widget-bottom-left,.initial-pip.widget-top-left { right:auto; left:max(18px,env(safe-area-inset-left)); }
  .initial-pip.widget-top-right,.initial-pip.widget-top-left { top:max(18px,env(safe-area-inset-top)); bottom:auto; }
  .initial-pip:hover,.initial-pip:focus-within { transform:translateY(-3px) scale(1.025); box-shadow:0 25px 72px rgba(0,0,0,.46),0 0 30px color-mix(in srgb,var(--accent),transparent 66%); }
  .initial-pip__main { display:block; width:100%; padding:0; border:0; background:transparent; color:inherit; text-align:left; cursor:pointer; }
  .initial-pip__media { position:relative; display:block; width:100%; aspect-ratio:16/9; overflow:hidden; background:linear-gradient(145deg,var(--surface),color-mix(in srgb,var(--surface),var(--accent) 16%)); }
  .initial-pip__media video,.initial-pip__media img { display:block; width:100%; height:100%; object-fit:cover; }
  .initial-pip__media>.launcher-orb { position:absolute; left:50%; top:50%; transform:translate(-50%,-50%); }
  .initial-pip__media>i { position:absolute; left:50%; top:50%; width:38px; height:38px; transform:translate(-50%,-50%); border:1px solid rgba(255,255,255,.48); border-radius:50%; background:rgba(2,8,15,.46); backdrop-filter:blur(10px); box-shadow:0 8px 24px rgba(0,0,0,.24); }
  .initial-pip__media>i::after { content:""; position:absolute; left:52%; top:50%; transform:translate(-45%,-50%); border-top:6px solid transparent; border-bottom:6px solid transparent; border-left:9px solid #fff; }
  .initial-pip__copy { display:block; padding:9px 32px 10px 12px; background:linear-gradient(145deg,rgba(7,17,31,.98),rgba(10,28,45,.96)); }
  .initial-pip__copy small,.initial-pip__copy strong { display:block; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .initial-pip__copy small { color:var(--accent); font-size:7px; font-weight:900; letter-spacing:.12em; text-transform:uppercase; }.initial-pip__copy strong { margin-top:3px; font-size:11px; }
  .initial-pip__open-cue { display:none; }
  .initial-pip__collapse { position:absolute; right:5px; bottom:6px; z-index:3; display:grid; place-items:center; width:25px; height:31px; padding:0; border:0; border-radius:999px; background:rgba(255,255,255,.04); color:#a5b7c7; font-size:20px; cursor:pointer; }
  .initial-pip__collapse:hover,.initial-pip__collapse:focus-visible { color:#fff; background:rgba(255,255,255,.11); outline:none; }
  .initial-pip--glass { background:rgba(8,21,34,.72); backdrop-filter:blur(20px); }.initial-pip--glass .initial-pip__copy { background:rgba(8,21,34,.68); }
  .initial-pip--minimal { width:min(188px,calc(100vw - 36px)); border-color:rgba(255,255,255,.16); border-radius:14px; box-shadow:0 16px 42px rgba(0,0,0,.35); }.initial-pip--minimal .initial-pip__copy { display:none; }.initial-pip--minimal .initial-pip__collapse { bottom:auto; top:5px; background:rgba(2,8,15,.58); }
  .initial-pip--edge-preview { right:0; bottom:max(54px,env(safe-area-inset-bottom)); display:grid; grid-template-columns:minmax(0,122px) 40px; width:min(162px,calc(100vw - 18px)); min-height:108px; border-color:color-mix(in srgb,var(--accent),transparent 82%); border-right:0; border-radius:19px 0 0 19px; background:linear-gradient(145deg,rgba(5,14,25,.98),rgba(8,25,40,.97)); box-shadow:-13px 16px 48px rgba(0,0,0,.38),0 0 25px color-mix(in srgb,var(--accent),transparent 69%); }
  .initial-pip--edge-preview.widget-bottom-left,.initial-pip--edge-preview.widget-top-left { right:auto; left:0; grid-template-columns:40px minmax(0,122px); border-right:1px solid color-mix(in srgb,var(--accent),transparent 82%); border-left:0; border-radius:0 19px 19px 0; }
  .initial-pip--edge-preview .initial-pip__main { display:block; min-width:0; min-height:108px; }
  .initial-pip--edge-preview .initial-pip__media { width:auto; min-width:0; aspect-ratio:auto; margin:3px 0 3px 3px; border-radius:15px; box-shadow:inset 0 0 0 1px rgba(255,255,255,.08); }
  .initial-pip--edge-preview.widget-bottom-left .initial-pip__main,.initial-pip--edge-preview.widget-top-left .initial-pip__main { order:2; }
  .initial-pip--edge-preview.widget-bottom-left .initial-pip__media,.initial-pip--edge-preview.widget-top-left .initial-pip__media { margin:3px 3px 3px 0; }
  .initial-pip--edge-preview .initial-pip__media>i { width:32px; height:32px; opacity:.76; transition:opacity .2s ease,transform .25s cubic-bezier(.22,1,.36,1); }
  .initial-pip--edge-preview .initial-pip__main:hover .initial-pip__media>i,.initial-pip--edge-preview .initial-pip__main:focus-visible .initial-pip__media>i { opacity:1; transform:translate(-50%,-50%) scale(1.08); }
  .initial-pip--edge-preview .initial-pip__copy,.initial-pip--edge-preview .initial-pip__open-cue { display:none; }
  .initial-pip--edge-preview .initial-pip__collapse { position:static; z-index:5; display:grid; width:auto; height:auto; min-height:100%; padding:0; border-radius:0; background:linear-gradient(90deg,rgba(255,255,255,.015),rgba(255,255,255,.045)); color:var(--accent); font-size:30px; text-shadow:0 0 13px color-mix(in srgb,var(--accent),transparent 28%); animation:pip-cue-breathe 2.7s ease-in-out infinite; }
  .initial-pip--edge-preview .initial-pip__collapse:hover,.initial-pip--edge-preview .initial-pip__collapse:focus-visible { background:rgba(255,255,255,.09); color:#eaffff; }
  .initial-pip--edge-preview.widget-bottom-left .initial-pip__collapse,.initial-pip--edge-preview.widget-top-left .initial-pip__collapse { order:1; transform:rotate(180deg); }
  @keyframes pip-arrive { from { opacity:0; transform:translateX(28px) scale(.88); } to { opacity:1; transform:none; } }
  @keyframes pip-streak-follow-path { 0% { offset-distance:0%; opacity:.28; } 12% { opacity:.72; } 50% { opacity:1; } 88% { opacity:.72; } 100% { offset-distance:100%; opacity:.28; } }
  @keyframes pip-streak-kinetic { 0%,100% { transform:scaleX(.56); } 50% { transform:scaleX(1.18); } }
  @keyframes pip-cue-breathe { 0%,100% { opacity:.58; translate:1px 0; } 50% { opacity:1; translate:-3px 0; } }
  .initial-pip--from-tab { transform-origin:right bottom; animation:pip-from-side .72s cubic-bezier(.16,1,.3,1) both; }
  .initial-pip--from-tab.widget-bottom-left,.initial-pip--from-tab.widget-top-left { transform-origin:left bottom; }
  @keyframes pip-from-side { 0% { opacity:.15; clip-path:inset(18% 0 18% 84% round 18px); transform:translateX(22px) scale(.58); } 52% { opacity:1; clip-path:inset(0 0 0 0 round 18px); } 100% { opacity:1; clip-path:inset(0 0 0 0 round 18px); transform:none; } }
  .project-companion { position:fixed; right:max(18px,env(safe-area-inset-right)); bottom:max(18px,env(safe-area-inset-bottom)); z-index:99991; display:grid; grid-template-columns:126px minmax(0,1fr) 28px; align-items:stretch; width:min(440px,calc(100vw - 36px)); min-height:132px; overflow:hidden; border:1px solid color-mix(in srgb,var(--accent),transparent 30%); border-radius:20px; background:linear-gradient(135deg,rgba(7,17,31,.98),rgba(10,28,45,.96)); color:#fff; box-shadow:0 22px 70px rgba(0,0,0,.42),0 0 calc(var(--glow-blur,18px) * .9) color-mix(in srgb,var(--accent),transparent 78%); animation:companion-arrive .55s cubic-bezier(.22,1,.36,1) both; }
  .project-companion::after { content:""; position:absolute; inset:0; pointer-events:none; background-image:linear-gradient(color-mix(in srgb,var(--accent),transparent 91%) 1px,transparent 1px),linear-gradient(90deg,color-mix(in srgb,var(--accent),transparent 91%) 1px,transparent 1px); background-size:22px 22px; mask-image:linear-gradient(90deg,transparent 20%,#000); opacity:.48; }
  .project-companion--glass { background:linear-gradient(135deg,rgba(13,31,48,.76),rgba(6,19,32,.7)); backdrop-filter:blur(22px); }
  .project-companion--minimal { grid-template-columns:126px 28px; width:155px; min-height:108px; border-color:rgba(255,255,255,.18); border-radius:15px; box-shadow:0 16px 45px rgba(0,0,0,.36); }
  .project-companion--minimal .companion-copy { display:none; }
  .companion-media { position:relative; z-index:1; display:block; width:126px; height:100%; min-height:132px; overflow:hidden; border:0; border-right:1px solid color-mix(in srgb,var(--accent),transparent 68%); background:linear-gradient(145deg,var(--surface),color-mix(in srgb,var(--surface),var(--accent) 14%)); }
  .companion-media img,.companion-media video { display:block; width:100%; height:100%; object-fit:cover; }
  .companion-media::after { content:""; position:absolute; inset:0; background:linear-gradient(90deg,transparent 45%,rgba(7,17,31,.34)); pointer-events:none; }
  .companion-sound,.companion-replay { position:absolute; z-index:3; display:grid; place-items:center; width:28px; height:28px; padding:0; border:1px solid rgba(255,255,255,.28); border-radius:50%; background:rgba(2,8,15,.68); backdrop-filter:blur(12px); color:#fff; font-size:13px; cursor:pointer; box-shadow:0 5px 18px rgba(0,0,0,.3); }
  .companion-sound { left:8px; bottom:8px; }
  .companion-sound .sound-icon { width:16px; height:16px; }
  .companion-replay { right:8px; bottom:8px; opacity:0; visibility:hidden; transform:scale(.82); transition:.2s ease; }
  .project-companion.is-ended .companion-replay { opacity:1; visibility:visible; transform:scale(1); }
  .companion-sound:hover,.companion-sound:focus-visible,.companion-replay:hover,.companion-replay:focus-visible { border-color:var(--accent); outline:none; }
  .companion-status { position:absolute; z-index:3; left:8px; right:8px; top:8px; padding:5px 7px; border-radius:999px; background:rgba(2,8,15,.64); color:#fff; font-size:8px; font-weight:800; line-height:1.1; text-align:center; opacity:0; transform:translateY(-4px); transition:.2s ease; pointer-events:none; }
  .project-companion.needs-sound .companion-status { opacity:1; transform:none; }
  .companion-copy { position:relative; z-index:1; display:grid; align-content:center; gap:4px; min-width:0; padding:13px 10px 12px 14px; border:0; background:transparent; color:inherit; text-align:left; cursor:pointer; }
  .companion-copy small { color:var(--accent); font-size:8px; font-weight:900; letter-spacing:.13em; text-transform:uppercase; }
  .companion-copy strong { overflow:hidden; font-size:13px; line-height:1.25; text-overflow:ellipsis; white-space:nowrap; }
  .companion-copy span { color:#a9bac9; font-size:10px; line-height:1.3; }
  .companion-copy b { width:max-content; margin-top:4px; color:#f7fbff; font-size:10px; text-decoration:underline; text-decoration-color:color-mix(in srgb,var(--accent),transparent 38%); text-underline-offset:3px; }
  .companion-dismiss { position:relative; z-index:2; align-self:start; width:28px; height:34px; border:0; background:transparent; color:#91a5b7; font-size:18px; cursor:pointer; }
  .companion-dismiss:hover,.companion-dismiss:focus-visible { color:#fff; outline:none; }
  @keyframes companion-arrive { from { opacity:0; transform:translateY(18px) scale(.96); } to { opacity:1; transform:none; } }
  .overlay { position:fixed; inset:0; z-index:99999; display:grid; place-items:center; padding:max(18px,env(safe-area-inset-top)) max(18px,env(safe-area-inset-right)) max(18px,env(safe-area-inset-bottom)) max(18px,env(safe-area-inset-left)); background:rgba(1,5,11,.76); backdrop-filter:blur(15px); }
  .overlay.is-opening { animation:overlay-arrive .32s ease-out both; }
  .overlay.is-opening .stage { animation:stage-open .68s cubic-bezier(.16,1,.3,1) both; }
  @keyframes overlay-arrive { from { opacity:0; backdrop-filter:blur(0); } to { opacity:1; backdrop-filter:blur(15px); } }
  @keyframes stage-open { from { opacity:.3; transform:translateY(18px) scale(.88); } to { opacity:1; transform:none; } }
  .overlay .stage { width:min(1120px,100%); max-height:calc(100dvh - 36px); }
  .overlay.portrait .stage { width:min(470px,100%); }
  .overlay.mode-widget { inset:auto; display:block; width:min(560px,calc(100vw - 36px)); padding:0; background:transparent; backdrop-filter:none; filter:drop-shadow(0 24px 52px rgba(0,0,0,.38)); }
  .overlay.mode-widget.widget-size-compact:not(.portrait) { width:min(430px,calc(100vw - 36px)); }
  .overlay.mode-widget.widget-size-large:not(.portrait) { width:min(720px,calc(100vw - 36px)); }
  .overlay.mode-widget.portrait { width:min(420px,calc((76dvh - 24px) * .5625),calc(100vw - 36px)); }
  .overlay.mode-widget.portrait.widget-size-compact { width:min(330px,calc((60dvh - 20px) * .5625),calc(100vw - 36px)); }
  .overlay.mode-widget.portrait.widget-size-large { width:min(500px,calc((91dvh - 20px) * .5625),calc(100vw - 36px)); }
  .overlay.mode-widget.widget-bottom-right { right:max(18px,env(safe-area-inset-right)); bottom:max(18px,env(safe-area-inset-bottom)); }
  .overlay.mode-widget.widget-bottom-left { left:max(18px,env(safe-area-inset-left)); bottom:max(18px,env(safe-area-inset-bottom)); }
  .overlay.mode-widget.widget-top-right { top:max(18px,env(safe-area-inset-top)); right:max(18px,env(safe-area-inset-right)); }
  .overlay.mode-widget.widget-top-left { top:max(18px,env(safe-area-inset-top)); left:max(18px,env(safe-area-inset-left)); }
  .overlay.mode-widget .stage,.overlay.mode-widget.portrait .stage { width:100%; min-height:0; max-height:calc(100dvh - 36px); border-radius:var(--radius); }
  .mode-widget .topbar { cursor:default; }
  .overlay.mode-widget.is-dragging { user-select:none; }
  .mode-widget .widget-drag-handle { position:absolute; top:6px; left:50%; display:grid; place-items:center; width:48px; height:12px; margin:0; padding:0; transform:translateX(-50%); border:0; border-radius:999px; background:rgba(3,10,18,.52); backdrop-filter:blur(12px); color:transparent; font-size:0; cursor:grab; pointer-events:auto; touch-action:none; }
  .mode-widget .widget-drag-handle::before { content:""; width:24px; height:3px; border-radius:99px; background:rgba(255,255,255,.62); box-shadow:0 0 8px color-mix(in srgb,var(--accent),transparent 66%); }
  .mode-widget .widget-drag-handle:active { cursor:grabbing; }
  .mode-widget .widget-size-button { display:grid; place-items:center; width:32px; height:32px; min-height:32px; padding:0; border:1px solid rgba(255,255,255,.18); border-radius:50%; background:rgba(2,8,15,.48); backdrop-filter:blur(14px); color:#fff; font-size:18px; cursor:pointer; }
  .mode-widget .widget-size-button:hover,.mode-widget .widget-size-button:focus-visible { border-color:var(--accent); outline:none; }
  .mode-widget.widget-size-standard .icon-btn,.mode-widget.widget-size-standard .media-toggle { width:var(--control-widget-size,46px); height:var(--control-widget-size,46px); min-height:var(--control-widget-size,46px); }
  .mode-widget.widget-size-standard .sound-toggle { width:var(--control-widget-sound-size,41px); height:var(--control-widget-sound-size,41px); min-height:var(--control-widget-sound-size,41px); }
  .mode-widget.widget-size-standard .widget-size-button { width:30px; height:30px; min-height:30px; font-size:17px; }
  .mode-widget h2 { font-size:clamp(var(--heading-min,21px),7cqw,var(--heading-max,31px)); overflow-wrap:anywhere; }
  .mode-widget .choice b { overflow-wrap:anywhere; }
  .mode-widget.widget-size-compact .topbar { gap:8px; padding:15px 13px; }
  .mode-widget.widget-size-compact .topbar>div { gap:6px; }
  .mode-widget.widget-size-compact .icon-btn,.mode-widget.widget-size-compact .media-toggle { width:var(--control-compact-size,39px); height:var(--control-compact-size,39px); min-height:var(--control-compact-size,39px); }
  .mode-widget.widget-size-compact .sound-toggle { width:var(--control-compact-sound-size,35px); height:var(--control-compact-sound-size,35px); min-height:var(--control-compact-sound-size,35px); }
  .mode-widget.widget-size-compact .icon-btn,.mode-widget.widget-size-compact .sound-toggle { font-size:18px; }
  .mode-widget.widget-size-compact .media-toggle { font-size:0; }
  .mode-widget.widget-size-compact .widget-size-button { width:27px; height:27px; min-height:27px; font-size:15px; }
  .mode-widget.widget-size-compact .content { padding:36px 12px 15px; }
  .mode-widget.widget-size-compact h2 { font-size:clamp(var(--heading-16-min,19px),8cqw,var(--heading-16-max,25px)); }
  .mode-widget.widget-size-compact .body { margin-top:7px; font-size:var(--body-16-min,12px); }
  .mode-widget.widget-size-compact .choices,.mode-widget.widget-size-compact .choice-swipe-shell { gap:7px; margin-top:12px; }
  .mode-widget.widget-size-compact .choice { min-height:var(--choice-compact-min-height,48px); padding:var(--choice-compact-pad-y,8px) var(--choice-compact-pad-x,7px); border-radius:11px; }
  .mode-widget.widget-size-compact .choice b { font-size:var(--button-16-min,12px); }
  .mode-widget.widget-size-compact .interaction-preview { top:43px; left:12px; right:12px; font-size:13px; }
  .mode-widget.widget-size-compact .choices--swipe .choice { flex-basis:calc(100% - 56px); }
  .mode-widget.widget-size-compact .choice-swipe-controls { min-height:26px; }
  .mode-widget.widget-size-compact .choice-swipe-arrow { width:27px; height:27px; min-height:27px; }
  .mode-widget.widget-size-compact .location-prelude { padding:12px; }
  .mode-widget.widget-size-compact .location-prelude__card,.mode-widget.widget-size-compact .start-gate__card { padding:16px; border-radius:16px; }
  .mode-widget.widget-size-compact .location-prelude h2,.mode-widget.widget-size-compact .start-gate__greeting { font-size:21px; }
  .mode-widget.widget-size-compact .location-prelude p { font-size:12px; }
  .loading,.error { display:grid; place-items:center; min-height:340px; padding:32px; border-radius:var(--radius); background:var(--bg); color:var(--text); text-align:center; }
  .spinner { width:34px; height:34px; border:3px solid rgba(255,255,255,.18); border-top-color:var(--accent); border-radius:50%; animation:spin .8s linear infinite; }
  .sr-only { position:absolute!important; width:1px!important; height:1px!important; overflow:hidden!important; clip:rect(1px,1px,1px,1px)!important; white-space:nowrap!important; }
  @keyframes spin { to { transform:rotate(360deg); } }
  @keyframes wait-pulse { 0% { box-shadow:0 0 0 0 color-mix(in srgb,var(--accent),transparent 35%); } 70%,100% { box-shadow:0 0 0 8px transparent; } }
  @media (hover:hover) and (pointer:fine) {
    .stage.layout-bottom:not(.is-interaction-revealed) .interaction-teaser:hover,.stage.layout-bottom:not(.is-interaction-revealed) .interaction-teaser:focus-visible { height:96px; background:linear-gradient(180deg,color-mix(in srgb,var(--surface),transparent 28%),color-mix(in srgb,var(--bg),transparent 4%)); outline:none; }
    .layout-bottom .interaction-teaser:hover i,.layout-bottom .interaction-teaser:focus-visible i { transform:translateY(-13px); }
    .layout-bottom .interaction-teaser:hover .interaction-preview,.layout-bottom .interaction-teaser:focus-visible .interaction-preview { opacity:1; transform:translateY(0); }
  }
  @media (max-width:620px) {
    .stage { min-height:min(88dvh,820px); aspect-ratio:auto; border-radius:18px; }
    .content,.layout-side .content { top:auto; left:0; right:0; bottom:0; width:100%; max-height:54dvh; padding:44px 14px max(20px,env(safe-area-inset-bottom)); border:1px solid rgba(255,255,255,.12); border-bottom:0; border-radius:22px 22px 0 0; transform:translateY(calc(100% + 8px)); }
    .stage.has-rich-content .content { max-height:min(82dvh,calc(100% - 12px)); }
    .stage.has-inquiry-form .content { max-height:calc(100% - 8px); padding-bottom:max(28px,env(safe-area-inset-bottom)); }
    .inquiry-grid { grid-template-columns:1fr; }
    .context-summary { align-items:flex-start; }
    .context-summary button { padding-top:2px; }
    .inquiry-field--wide { grid-column:auto; }
    .content::before,.layout-side .content::before { display:none; }
    .layout-side .interaction-teaser { left:0; right:0; bottom:0; width:100%; height:34px; padding:0; transform:none; border-radius:13px 13px 0 0; writing-mode:horizontal-tb; }
    .layout-side .interaction-teaser i { position:relative; width:24px; height:18px; }
    .layout-side.is-interaction-revealed .interaction-teaser { transform:translateY(24px); }
    .content:has(.choices) .body { display:none; }
    h2 { font-size:clamp(var(--heading-min,23px),7vw,var(--heading-max,34px)); }
    .choices { grid-template-columns:repeat(12,minmax(0,1fr)); gap:9px; margin-top:16px; }
    .choice-swipe-shell { margin-inline:-14px; width:calc(100% + 28px); }
    .choice-swipe-shell::before,.choice-swipe-shell::after { width:28px; bottom:34px; }
    .choices--swipe { display:flex; gap:9px; width:100%; padding:2px 28px 9px; scroll-padding-inline:28px; }
    .choices--swipe .choice { flex-basis:min(82vw,310px); min-height:62px; }
    .choice { grid-column:span var(--choice-mobile-span,6); min-height:var(--choice-mobile-min-height,62px); padding:var(--choice-mobile-pad-y,10px) var(--choice-mobile-pad-x,8px); }
    .choice b { font-size:clamp(var(--button-16-min,12px),3.4vw,var(--button-size,15px)); }
    .choice small { display:none; }
    .choice:nth-child(5):last-child { grid-column:1/-1; min-height:56px; }
    .recommendations { grid-template-columns:1fr; }
    .portfolio-peek-slot { gap:7px; }
    .portfolio-peek { grid-template-columns:48px minmax(0,1fr); min-height:58px; gap:7px; padding:5px; }
    .portfolio-peek__media { width:48px; height:46px; }
    .portfolio-peek__arrow,.portfolio-peek__copy em { display:none; }
    .portfolio-peek__copy strong { white-space:normal; display:-webkit-box; -webkit-box-orient:vertical; -webkit-line-clamp:2; }
    .overlay { padding:0; }
    .overlay .stage,.overlay.portrait .stage { width:100%; min-height:100dvh; max-height:none; border-radius:0; }
    .overlay.mode-widget:not(.portrait) { width:min(430px,calc(100vw - 20px)); }
    .overlay.mode-widget:not(.portrait).widget-size-compact { width:min(360px,calc(88vw - 20px)); }
    .overlay.mode-widget:not(.portrait).widget-size-large { width:calc(100vw - 20px); }
    .overlay.mode-widget.portrait { width:min(calc(100vw - 20px),calc(75dvh * .5625)); }
    .overlay.mode-widget.portrait.widget-size-compact { width:min(calc(88vw - 20px),calc(58dvh * .5625)); }
    .overlay.mode-widget.portrait.widget-size-large { width:min(calc(100vw - 20px),calc(90dvh * .5625)); }
    .overlay.mode-widget .stage,.overlay.mode-widget.portrait .stage { width:100%; min-height:0; max-height:calc(100dvh - 20px); aspect-ratio:var(--ratio,16/9); border-radius:18px; }
    .overlay.mode-widget.widget-bottom-right { right:max(10px,env(safe-area-inset-right)); bottom:max(10px,env(safe-area-inset-bottom)); }
    .overlay.mode-widget.widget-bottom-left { left:max(10px,env(safe-area-inset-left)); bottom:max(10px,env(safe-area-inset-bottom)); }
    .overlay.mode-widget.widget-top-right { top:max(10px,env(safe-area-inset-top)); right:max(10px,env(safe-area-inset-right)); }
    .overlay.mode-widget.widget-top-left { top:max(10px,env(safe-area-inset-top)); left:max(10px,env(safe-area-inset-left)); }
    .launcher { bottom:max(12px,env(safe-area-inset-bottom)); right:max(12px,env(safe-area-inset-right)); }
    .launcher-shell { right:max(12px,env(safe-area-inset-right)); bottom:max(12px,env(safe-area-inset-bottom)); }
    .launcher-shell.widget-bottom-left,.launcher-shell.widget-top-left { right:auto; left:max(12px,env(safe-area-inset-left)); }
    .launcher-shell.widget-top-right,.launcher-shell.widget-top-left { top:max(12px,env(safe-area-inset-top)); bottom:auto; }
    .location-launcher { bottom:max(14px,env(safe-area-inset-bottom)); padding:18px; border-radius:20px; }
    .location-launcher>div { grid-template-columns:1fr; }
    .initial-pip { right:max(12px,env(safe-area-inset-right)); bottom:max(12px,env(safe-area-inset-bottom)); width:min(210px,calc(100vw - 24px)); border-radius:16px; }
    .initial-pip--edge-preview { right:0; bottom:max(54px,env(safe-area-inset-bottom)); grid-template-columns:minmax(0,112px) 36px; width:min(148px,calc(100vw - 12px)); min-height:100px; border-radius:18px 0 0 18px; }
    .initial-pip--edge-preview .initial-pip__streak-runner { offset-path:path("M148 1.5H19A17.5 17.5 0 0 0 1.5 19V81A17.5 17.5 0 0 0 19 98.5H148"); }
    .initial-pip--edge-preview .initial-pip__main { min-height:100px; }
    .initial-pip--edge-preview .initial-pip__media { margin:3px 0 3px 3px; border-radius:14px; }
    .initial-pip--edge-preview.widget-bottom-left,.initial-pip--edge-preview.widget-top-left { left:0; grid-template-columns:36px minmax(0,112px); border-radius:0 18px 18px 0; }
    .initial-pip--edge-preview.widget-bottom-left .initial-pip__media,.initial-pip--edge-preview.widget-top-left .initial-pip__media { margin:3px 3px 3px 0; }
    .initial-pip.widget-bottom-left,.initial-pip.widget-top-left { right:auto; left:max(12px,env(safe-area-inset-left)); }
    .initial-pip.widget-top-right,.initial-pip.widget-top-left { top:max(12px,env(safe-area-inset-top)); bottom:auto; }
    .launcher.widget-bottom-left,.launcher.widget-top-left { right:auto; left:max(12px,env(safe-area-inset-left)); }
    .launcher.widget-top-right,.launcher.widget-top-left { top:max(12px,env(safe-area-inset-top)); bottom:auto; }
    .project-companion { right:max(12px,env(safe-area-inset-right)); bottom:max(12px,env(safe-area-inset-bottom)); grid-template-columns:96px minmax(0,1fr) 28px; width:min(380px,calc(100vw - 24px)); min-height:112px; border-radius:18px; }
    .companion-media { width:96px; min-height:112px; }
    .companion-copy { padding:11px 7px 10px 11px; }
    .companion-copy span { display:-webkit-box; overflow:hidden; -webkit-box-orient:vertical; -webkit-line-clamp:2; }
    .media-toggle,.icon-btn { width:var(--control-mobile-size,44px); height:var(--control-mobile-size,44px); min-height:var(--control-mobile-size,44px); }
    .sound-toggle { width:var(--control-mobile-sound-size,39px); height:var(--control-mobile-sound-size,39px); min-height:var(--control-mobile-sound-size,39px); }
    .topbar { gap:9px; padding:14px 14px; }
  }
  @container (max-width:620px) {
    .content,.layout-side .content { top:auto; left:0; right:0; bottom:0; width:100%; max-height:64%; padding:44px 14px max(20px,env(safe-area-inset-bottom)); border-right:1px solid rgba(255,255,255,.12); border-bottom:0; border-radius:22px 22px 0 0; transform:translateY(calc(100% + 8px)); }
    .stage.has-rich-content .content { max-height:86%; }
    .stage.has-inquiry-form .content { max-height:calc(100% - 8px); }
    .layout-side .interaction-teaser { left:0; right:0; bottom:0; width:100%; height:34px; padding:0; transform:none; border-radius:13px 13px 0 0; writing-mode:horizontal-tb; }
    .layout-side .interaction-teaser i { position:relative; width:24px; height:18px; }
    .layout-side.is-interaction-revealed .interaction-teaser { transform:translateY(24px); }
    h2 { font-size:clamp(23px,7cqw,34px); }
    .choices { grid-template-columns:repeat(12,minmax(0,1fr)); gap:9px; }
    .choice-swipe-shell { margin-inline:-14px; width:calc(100% + 28px); }
    .choice-swipe-shell::before,.choice-swipe-shell::after { width:28px; bottom:34px; }
    .choices--swipe { display:flex; gap:9px; width:100%; padding:2px 28px 9px; scroll-padding-inline:28px; }
    .choices--swipe .choice { flex-basis:min(78cqw,310px); min-height:64px; }
    .choice { grid-column:span var(--choice-mobile-span,6); min-height:64px; padding:10px 8px; }
    .choice small { display:none; }
    .recommendations { grid-template-columns:1fr; }
    .portfolio-peek-slot { grid-template-columns:1fr; }
    .portfolio-peek:only-child { grid-column:auto; }
    .portfolio-peek { grid-template-columns:56px minmax(0,1fr) 22px; min-height:58px; gap:8px; padding:5px 7px 5px 5px; }
    .portfolio-peek__media { width:56px; height:48px; }
    .portfolio-peek__copy strong { white-space:normal; display:-webkit-box; -webkit-box-orient:vertical; -webkit-line-clamp:2; }
    .quick-links { align-items:flex-start; flex-direction:column; overflow:visible; }
    .quick-link { width:100%; justify-content:space-between; white-space:normal; }
  }
  @media (max-width:620px) { .timed-page { grid-template-columns:42px minmax(0,1fr) auto; gap:8px; }.timed-page--no-media { grid-template-columns:minmax(0,1fr) auto; }.timed-page.is-visible { max-height:96px; padding:9px 10px; }.timed-page__media { width:42px; height:42px; border-radius:9px; }.timed-page__copy strong { font-size:12px; }.timed-page__copy span { font-size:9px; -webkit-line-clamp:1; } }
  @media (max-width:620px) {
    .stage.mobile-responsive { min-height:0; aspect-ratio:var(--mobile-ratio,9/16); }
    .stage.mobile-fixed { min-height:0; aspect-ratio:var(--ratio,16/9); }
    .stage.mobile-responsive .media { object-fit:cover; object-position:var(--media-focus-x,50%) center; }
    .stage.mobile-responsive .content { max-height:54%; padding:44px 14px max(20px,env(safe-area-inset-bottom)); }
    .stage.mobile-responsive.has-rich-content .content { max-height:82%; }
    .stage.mobile-responsive h2 { font-size:clamp(var(--heading-min,23px),7vw,var(--heading-max,34px)); }
    .stage.mobile-responsive .body { margin-top:7px; font-size:var(--body-min,13px); line-height:1.45; }
    .stage.mobile-responsive .choices { gap:9px; margin-top:14px; }
    .stage.mobile-responsive .choice { min-height:var(--choice-mobile-min-height,62px); padding:var(--choice-mobile-pad-y,10px) var(--choice-mobile-pad-x,8px); }
    .stage.mobile-responsive .choice b { font-size:clamp(var(--button-16-min,12px),3.4vw,var(--button-size,15px)); }
    .stage.mobile-responsive .wait-hint { left:10px; bottom:calc(var(--interaction-panel-height,0px) + 6px); }
    .overlay.mode-widget.mobile-responsive { width:min(calc(100vw - 20px),var(--mobile-widget-standard,42.1875dvh)); }
    .overlay.mode-widget.mobile-responsive.widget-size-compact { width:min(calc(88vw - 20px),var(--mobile-widget-compact,32.625dvh)); }
    .overlay.mode-widget.mobile-responsive.widget-size-large { width:min(calc(100vw - 20px),var(--mobile-widget-large,50.625dvh)); }
  }
  /* Widget typography follows the rendered player, not the browser viewport.
     These final rules intentionally win over the generic mobile/container layout. */
  .overlay.mode-widget {
    --widget-heading-min:var(--widget-heading-standard-min,17px);
    --widget-heading-max:var(--widget-heading-standard-max,31px);
    --widget-body-min:var(--widget-body-standard-min,10px);
    --widget-body-max:var(--widget-body-standard-max,15px);
    --widget-button-min:var(--widget-button-standard-min,11px);
    --widget-button-max:var(--widget-button-standard-max,15px);
    --widget-choice-height:var(--widget-choice-standard,46px);
  }
  .overlay.mode-widget.widget-size-compact {
    --widget-heading-min:var(--widget-heading-compact-min,14px);
    --widget-heading-max:var(--widget-heading-compact-max,26px);
    --widget-body-min:var(--widget-body-compact-min,9px);
    --widget-body-max:var(--widget-body-compact-max,13px);
    --widget-button-min:var(--widget-button-compact-min,10px);
    --widget-button-max:var(--widget-button-compact-max,14px);
    --widget-choice-height:var(--widget-choice-compact,39px);
  }
  .overlay.mode-widget.widget-size-large {
    --widget-heading-min:var(--widget-heading-large-min,20px);
    --widget-heading-max:var(--heading-max,38px);
    --widget-body-min:var(--widget-body-large-min,12px);
    --widget-body-max:var(--body-max,16px);
    --widget-button-min:var(--button-16-min,12px);
    --widget-button-max:var(--widget-button-large-max,16px);
    --widget-choice-height:var(--choice-min-height,56px);
  }
  .overlay.mode-widget .stage h2 { font-size:clamp(var(--widget-heading-min),6cqw,var(--widget-heading-max)); line-height:1.04; }
  .overlay.mode-widget .stage .body { font-size:clamp(var(--widget-body-min),3.2cqw,var(--widget-body-max)); }
  .overlay.mode-widget .stage .eyebrow { margin-bottom:clamp(6px,2.5cqw,12px); font-size:clamp(8px,2.35cqw,11px); }
  .overlay.mode-widget .stage .choice { min-height:var(--widget-choice-height); padding:clamp(6px,2.2cqw,var(--choice-pad-y,12px)) clamp(8px,3cqw,var(--choice-pad-x,16px)); }
  .overlay.mode-widget .stage .choice b { font-size:clamp(var(--widget-button-min),3.4cqw,var(--widget-button-max)); }
  .overlay.mode-widget .stage .content { padding:44px clamp(10px,4cqw,20px) clamp(10px,4cqw,21px); }
  .overlay.mode-widget .stage .topbar { gap:clamp(5px,2cqw,9px); padding:clamp(10px,3.5cqw,18px); }
  .overlay.mode-widget .stage .topbar>div { gap:clamp(4px,1.7cqw,8px); }
  .overlay.mode-widget .stage .icon-btn,.overlay.mode-widget .stage .media-toggle { width:clamp(29px,9cqw,var(--control-widget-size,46px)); height:clamp(29px,9cqw,var(--control-widget-size,46px)); min-height:clamp(29px,9cqw,var(--control-widget-size,46px)); }
  .overlay.mode-widget .stage .sound-toggle { width:clamp(27px,8.2cqw,var(--control-widget-sound-size,41px)); height:clamp(27px,8.2cqw,var(--control-widget-sound-size,41px)); min-height:clamp(27px,8.2cqw,var(--control-widget-sound-size,41px)); }
  .overlay.mode-widget.widget-size-compact .stage .icon-btn,.overlay.mode-widget.widget-size-compact .stage .media-toggle { width:clamp(27px,8.5cqw,var(--control-compact-size,39px)); height:clamp(27px,8.5cqw,var(--control-compact-size,39px)); min-height:clamp(27px,8.5cqw,var(--control-compact-size,39px)); }
  .overlay.mode-widget.widget-size-compact .stage .sound-toggle { width:clamp(25px,7.8cqw,var(--control-compact-sound-size,35px)); height:clamp(25px,7.8cqw,var(--control-compact-sound-size,35px)); min-height:clamp(25px,7.8cqw,var(--control-compact-sound-size,35px)); }
  .overlay.mode-widget .stage.aspect-16-9 .content { max-height:min(64%,300px); padding:40px clamp(9px,3cqw,18px) clamp(7px,2cqw,12px); scroll-padding:40px 0 8px; }
  .overlay.mode-widget .stage.aspect-16-9 .content:has(.choices) .body { display:none; }
  .overlay.mode-widget .stage.aspect-16-9 .choices,.overlay.mode-widget .stage.aspect-16-9 .choice-swipe-shell { gap:clamp(4px,1.4cqw,7px); margin-top:clamp(4px,1.7cqw,8px); }
  .overlay.mode-widget .stage.aspect-16-9 .choice { min-height:clamp(28px,8cqw,var(--widget-choice-height)); padding:clamp(3px,1.3cqw,6px) clamp(6px,2.3cqw,11px); }
  .overlay.mode-widget.widget-size-compact .stage.aspect-16-9 .content { max-height:68%; }
  .overlay.mode-widget .stage.aspect-16-9 .interaction-preview { top:clamp(30px,8cqw,48px); font-size:clamp(11px,3.5cqw,17px); }
  .overlay.mode-widget .stage.aspect-1-1 .content { max-height:64%; }
  .overlay.mode-widget .stage.aspect-4-5 .content,.overlay.mode-widget .stage.aspect-9-16 .content { max-height:58%; }
  .overlay.mode-widget .stage.has-rich-content .content { max-height:86%; }
  .overlay.mode-widget .stage.has-inquiry-form .content { max-height:calc(100% - 8px); }
  @media (prefers-reduced-motion:reduce) { *,*::before,*::after { scroll-behavior:auto!important; animation-duration:.01ms!important; transition-duration:.01ms!important; } }
  @supports not (color:color-mix(in srgb,white,black)) {
    .stage,.content,.edge-preview,.companion,.choice-select { border-color:var(--accent); }
    .content,.companion,.choice-select,.wait-hint { background:var(--surface); color:var(--text); }
    .choice { background:var(--surface); color:var(--text); }
    .choice:hover,.choice:focus { background:var(--bg); border-color:var(--accent); }
    .choice-swipe-arrow { background:var(--surface); border-color:var(--accent); }
    .media-placeholder { background:linear-gradient(120deg,#152a43,#050a12); }
    .stage { box-shadow:0 12px 32px rgba(0,0,0,.3); }
  }
  @supports not (aspect-ratio:1/1) {
    .stage::before { content:""; display:block; padding-top:var(--legacy-ratio,56.25%); }
    :host([data-scroll-floating]) .stage::before { padding-top:177.7778%; }
    @media(max-width:620px) { .stage.mobile-responsive::before { padding-top:var(--legacy-mobile-ratio,177.7778%); } }
  }
`;

// Embedded CSS is not processed by Vite. Adapt units before inserting it.
function compatibleUnits(css: string): string {
  if (!CSS.supports('height', '100dvh')) css = css.replace(/dvh\b/g, 'vh');
  if (!CSS.supports('width', '1cqw')) css = css.replace(/([\d.]+)cqw\b/g, 'calc($1 * var(--legacy-cqw,3px))');
  return css;
}

function uid(prefix = 'id'): string {
  return browserSessionId(prefix);
}

function escapeHtml(value: unknown): string {
  return String(value ?? '').replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#039;', '"': '&quot;' })[char]!);
}

/** Ohne Autoplay zeigt Safari erst mit Zeitmarke ein Standbild statt einer schwarzen Fläche. */
function stillFrameUrl(url: string): string {
  return url && !url.includes('#') ? `${url}#t=0.1` : url;
}

function safeUrl(value: unknown): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  if (raw.startsWith('#') || raw.startsWith('/')) return raw;
  try {
    const url = new URL(raw, window.location.href);
    return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
  } catch { return ''; }
}

function interpolate(value: string | undefined, profile: NeedProfile): string {
  return String(value ?? '')
    .split('{{city}}').join(profile.city || '')
    .split('{{region}}').join(profile.regionCode || '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function deviceClass(): string {
  if (window.matchMedia('(max-width: 600px)').matches) return 'mobile';
  if (window.matchMedia('(max-width: 1024px)').matches) return 'tablet';
  return 'desktop';
}

function formatTime(seconds: number): string {
  const safe = Math.max(0, Math.ceil(seconds));
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, '0')}`;
}

const trackedPageContexts = new Set<string>();

function normalizedPath(pathname: string): string {
  const clean = `/${pathname}`.replace(/\/{2,}/g, '/');
  return clean === '/' ? clean : clean.replace(/\/$/, '');
}

function matchesCurrentUrl(rawUrl?: string): boolean {
  if (!rawUrl || rawUrl.startsWith('#')) return false;
  try {
    const candidate = new URL(rawUrl, window.location.origin);
    if (candidate.origin !== window.location.origin) return false;
    if (normalizedPath(candidate.pathname) !== normalizedPath(window.location.pathname)) return false;
    const current = new URL(window.location.href);
    return Array.from(candidate.searchParams.entries()).every(([key, value]) => current.searchParams.get(key) === value);
  } catch {
    return false;
  }
}

function applySitePersonalization(profile: NeedProfile): string[] {
  const activeKeys = new Set([
    ...profile.goals,
    ...profile.needs,
    ...profile.audiences,
    ...profile.industries,
    ...profile.formats,
    ...profile.styles,
    ...profile.platforms,
  ].filter((item) => item.score > 0).map((item) => item.key));
  const activated: string[] = [];
  document.querySelectorAll<HTMLElement>('[data-lgs-personalized]').forEach((container) => {
    const variants = Array.from(container.querySelectorAll<HTMLElement>('[data-lgs-for]'));
    const matching = variants.filter((variant) => (variant.dataset.lgsFor ?? '').split(',').map((key) => key.trim()).some((key) => activeKeys.has(key)));
    variants.forEach((variant) => { variant.hidden = !matching.includes(variant); });
    container.querySelectorAll<HTMLElement>('[data-lgs-fallback]').forEach((fallback) => { fallback.hidden = matching.length > 0; });
    container.hidden = variants.length > 0 && matching.length === 0 && !container.querySelector('[data-lgs-fallback]');
    matching.forEach((variant) => activated.push(variant.dataset.lgsFor ?? ''));
  });
  document.documentElement.dataset.lgsIntent = Array.from(activeKeys).slice(0, 8).join(' ');
  return activated;
}

function normalizedMatchValue(value: string): string {
  return value.toLocaleLowerCase('de').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
}

function profileSearchText(profile: NeedProfile, dimensions: Array<keyof Pick<NeedProfile, 'goals' | 'needs' | 'audiences' | 'industries' | 'formats' | 'styles' | 'platforms'>>): string {
  return normalizedMatchValue(dimensions.flatMap((dimension) => profile[dimension].slice(0, 4).flatMap((tag) => [tag.key, ...tag.labels])).join(' '));
}

function framefieldGoal(profile: NeedProfile): string {
  const text = profileSearchText(profile, ['goals', 'needs']);
  if (/mitarbeiter|recruit|bewerb|fachkraft|arbeitgeber/.test(text)) return 'mitarbeitergewinnung';
  if (/kunde|anfrag|lead|vertrieb|auftrag|conversion/.test(text)) return 'auftragsgewinnung';
  if (/erklar|versteh|angebot/.test(text)) return 'erklaren-verstehen';
  if (/vertrau|testimonial/.test(text)) return 'vertrauen-aufbauen';
  if (/event|veranstaltung|messe/.test(text)) return 'eventkommunikation';
  if (/image|marke|sichtbar|reichweite|bekannt/.test(text)) return 'imagesteigerung';
  return '';
}

function framefieldFilmType(profile: NeedProfile): string {
  const text = profileSearchText(profile, ['formats', 'needs', 'goals', 'platforms']);
  if (/recruit|mitarbeiter|arbeitgeber/.test(text)) return 'recruitingfilm';
  if (/erklar|versteh/.test(text)) return 'erklarfilm';
  if (/reel|social|content/.test(text)) return 'reels';
  if (/image|marke/.test(text)) return 'imagefilm';
  return '';
}

class LGSVideoFlowElement extends HTMLElement {
  private root: ShadowRoot;
  private graph: FlowGraph | null = null;
  private session: PlayerSession | null = null;
  private isOpen = false;
  private launcherCollapsed = false;
  private launcherExpanded = false;
  private openingPipExpanded = false;
  /** Vorschau-Medien, die im Browser nicht laden (404, Abbruch), gelten als nicht vorhanden. */
  private launcherVideoFailed = false;
  private launcherPosterFailed = false;
  private reducedMotionQuery: MediaQueryList | null = null;
  private companionCollapsed = false;
  private projectCompanion: { projectId: string; title: string; client: string; poster: string; preview: string } | null = null;
  private pageCompanion: PageCompanionState | null = null;
  private loading = false;
  private errorMessage = '';
  private previewConfig: FlowGraph | null = null;
  private restoreFocus: HTMLElement | null = null;
  private eventQueue: Record<string, unknown>[] = [];
  private eventTimer: number | null = null;
  private impressionKeys = new Set<string>();
  private videoProgressKeys = new Set<string>();
  private mediaNodeId = '';
  private interactionRevealed = false;
  private interactionDismissed = false;
  private interactionAutoTriggered = false;
  private locationPromptDismissed = false;
  private playerStarted = false;
  // Chapter control is opt-in per explicit open/play, never restored from storage.
  private chapterControlStarted = false;
  private pendingChapterId: string | null = null;
  private mediaLoading: ReturnType<typeof bindMediaLoading> | null = null;
  private peerStates = new Map<HTMLElement, { inert: boolean; ariaHidden: string | null }>();
  private previousOverflow = '';
  private pageEnteredAt = 0;
  private ownsPageTracking = false;
  private framefieldApplySignature = '';
  private framefieldRelaxedSignature = '';
  /** Direct node filter rules (0.15.49). Inactive without a rule: no bridge calls at all. */
  private portfolioFilterKey = '';
  private portfolioFilter = new PortfolioFilterController({
    galleries: () => this.portfolioGalleries(),
    bridge: () => (window as Window & { FramefieldLGSBridge?: PortfolioFilterBridge }).FramefieldLGSBridge,
    request: (detail) => document.dispatchEvent(new CustomEvent('lgs-video:portfolio-filters-requested', { detail })),
    emit: (name, detail) => { this.emit(name, detail); if (name === 'portfolio-filter-rule-applied') this.track(name, { value: String(detail.gridId ?? '') }); },
  }, () => this.graph?.slug ?? '');
  /** 0.15.51: Referenzfinder-Antworten als exakte Galerie-Facetten mit stufenweiser Lockerung. */
  private advisorFilterKey = '';
  private advisorFilter = new PortfolioFilterController({
    galleries: () => this.portfolioGalleries(),
    bridge: () => (window as Window & { FramefieldLGSBridge?: PortfolioFilterBridge }).FramefieldLGSBridge,
    request: (detail) => document.dispatchEvent(new CustomEvent('lgs-video:portfolio-filters-requested', { detail })),
    emit: (name, detail) => {
      this.emit(name, detail);
      if (name === 'portfolio-filters-relaxed') { this.track(name, { value: `advisor-step-${String(detail.step ?? '')}` }); this.syncPortfolioPeek(); }
    },
  }, () => this.graph?.slug ?? '');
  private animatePlayerOpen = false;
  private choiceSwipeHintPlayed = false;
  private widgetSize: 'compact' | 'standard' | 'large' = 'standard';
  private liteObserver: MutationObserver | null = null;
  private widgetOffset: { left: number; top: number } | null = null;
  private widgetDrag: { pointerId: number; startX: number; startY: number; originLeft: number; originTop: number; handle: HTMLElement } | null = null;
  private lastMobileViewport = false;
  private interactionPanelObserver: ResizeObserver | null = null;
  private portfolioDialogObserver: MutationObserver | null = null;
  private portfolioDialogOpenTimer: number | null = null;
  private portfolioHome: Comment | null = null;
  private relocatingForPortfolio = false;
  private scrollDockFrame = 0;
  private scrollDockSeen = false;
  private scrollDockDismissed = false;
  private scrollDockOriginalHeight = '';

  private scheduleScrollDock = (): void => {
    if (!this.scrollDockFrame) this.scrollDockFrame = requestAnimationFrame(() => {
      this.scrollDockFrame = 0;
      this.syncScrollDock();
    });
  };

  private syncScrollDock(): void {
    const enabled = this.mode === 'inline' && this.graph?.settings.scrollDock === true && !this.previewConfig;
    this.toggleAttribute('data-scroll-dock', enabled);
    const bounds = this.getBoundingClientRect();
    const wasFloating = this.hasAttribute('data-scroll-floating');
    const state = resolveScrollDock({ enabled, top: bounds.top, bottom: bounds.bottom, viewportHeight: window.innerHeight, seen: this.scrollDockSeen, dismissed: this.scrollDockDismissed, floating: wasFloating });
    this.scrollDockSeen = state.seen;
    this.scrollDockDismissed = state.dismissed;
    const floating = state.floating;
    if (floating && !wasFloating) {
      this.scrollDockOriginalHeight = this.style.minHeight;
      this.style.minHeight = `${bounds.height}px`;
    }
    if (!floating && wasFloating) this.style.minHeight = this.scrollDockOriginalHeight;
    this.toggleAttribute('data-scroll-floating', floating);
    const stage = this.root.querySelector<HTMLElement>('.stage');
    if (stage) {
      const aspect = floating ? '9:16' : this.effectiveAspect();
      for (const format of ['16:9', '9:16', '1:1', '4:5']) stage.classList.toggle(`aspect-${format.replace(':', '-')}`, format === aspect);
    }
    const controls = this.root.querySelector<HTMLElement>('.scroll-dock-controls');
    if (controls) controls.hidden = !floating;
  }

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    if (this.relocatingForPortfolio) return;
    this.lastMobileViewport = this.isMobileViewport();
    this.addEventListener('click', this.handleClick);
    this.root.addEventListener('change', this.handleChange);
    this.root.addEventListener('submit', this.handleSubmit);
    this.root.addEventListener('pointerdown', this.handleWidgetPointerDown as EventListener);
    document.addEventListener('ffp:matching-applied', this.handleFramefield as EventListener);
    document.addEventListener('lgs-video:portfolio-updated', this.handlePortfolioState as EventListener);
    document.addEventListener('ffp:shortlist-change', this.handleFramefieldShortlist as EventListener);
    document.addEventListener('ffp:integration-ready', this.handleFramefieldReady as EventListener);
    document.addEventListener('ffp:lgs-profile-applied', this.handleFramefieldProfileApplied);
    ['click', 'change', 'input'].forEach((type) => document.addEventListener(type, this.handleManualPortfolioFilter, true));
    document.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('pagehide', this.handlePageHide);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('lgs-video:exclusive-play', this.handleExclusivePlay as EventListener);
    window.addEventListener('resize', this.handleViewportResize);
    window.addEventListener('scroll', this.scheduleScrollDock, { passive: true });
    window.addEventListener('resize', this.scheduleScrollDock);
    if (typeof window.matchMedia === 'function') {
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotionQuery.addEventListener?.('change', this.handleReducedMotionChange);
    }
    if (this.isLiquidGlass) this.syncLiteMode();
    if (this.isLiquidGlass && typeof MutationObserver !== 'undefined') {
      this.liteObserver = new MutationObserver(() => this.syncLiteMode());
      this.liteObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'data-frontface-lite'] });
    }
    this.load();
  }

  disconnectedCallback(): void {
    if (this.relocatingForPortfolio) return;
    this.chapterControlStarted = false;
    this.pendingChapterId = null;
    this.removeEventListener('click', this.handleClick);
    this.root.removeEventListener('change', this.handleChange);
    this.root.removeEventListener('submit', this.handleSubmit);
    this.root.removeEventListener('pointerdown', this.handleWidgetPointerDown as EventListener);
    this.stopWidgetDrag(false);
    document.removeEventListener('ffp:matching-applied', this.handleFramefield as EventListener);
    document.removeEventListener('lgs-video:portfolio-updated', this.handlePortfolioState as EventListener);
    document.removeEventListener('ffp:shortlist-change', this.handleFramefieldShortlist as EventListener);
    document.removeEventListener('ffp:integration-ready', this.handleFramefieldReady as EventListener);
    document.removeEventListener('ffp:lgs-profile-applied', this.handleFramefieldProfileApplied);
    ['click', 'change', 'input'].forEach((type) => document.removeEventListener(type, this.handleManualPortfolioFilter, true));
    document.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('pagehide', this.handlePageHide);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('lgs-video:exclusive-play', this.handleExclusivePlay as EventListener);
    window.removeEventListener('resize', this.handleViewportResize);
    window.removeEventListener('scroll', this.scheduleScrollDock);
    window.removeEventListener('resize', this.scheduleScrollDock);
    this.liteObserver?.disconnect();
    this.reducedMotionQuery?.removeEventListener?.('change', this.handleReducedMotionChange);
    this.liteObserver = null;
    cancelAnimationFrame(this.scrollDockFrame);
    this.scrollDockFrame = 0;
    this.interactionPanelObserver?.disconnect();
    this.interactionPanelObserver = null;
    this.stopPortfolioDialogWatch();
    if (this.eventTimer) window.clearTimeout(this.eventTimer);
    this.mediaLoading?.dispose();
    this.finishPageTracking();
    this.flushEvents();
    this.releaseMedia();
    this.restorePeers();
  }

  set config(value: FlowGraph | null) {
    const safeValue = value ? repairFlowGraphStart(value).graph : null;
    this.previewConfig = safeValue;
    this.graph = safeValue;
    if (safeValue) {
      this.errorMessage = '';
      this.loading = false;
      this.resetSession(false);
    }
    this.render();
  }

  /** Updates an already mounted admin preview without losing its current node or answers. */
  updatePreviewConfig(value: FlowGraph | null): void {
    const safeValue = value ? repairFlowGraphStart(value).graph : null;
    if (!safeValue || !this.graph || safeValue.slug !== this.graph.slug || !this.session) {
      this.config = safeValue;
      return;
    }
    this.previewConfig = safeValue;
    this.graph = safeValue;
    this.render();
  }

  /** Repaints presentation-only attribute changes without restarting the preview journey. */
  refresh(): void { this.render(); }

  get flowSlug(): string { return this.getAttribute('flow') || this.graph?.slug || ''; }
  get mode(): string { return this.getAttribute('mode') || 'inline'; }
  /** Website-wide visual layer chosen in WordPress (FrontFace › Darstellung). Unknown values mean Standard. */
  get playerDesign(): PlayerDesign { return resolvePlayerDesign(this.getAttribute('player-design')); }
  private get isLiquidGlass(): boolean { return this.playerDesign === 'liquid-glass'; }
  set mode(value: string) { this.setAttribute('mode', value || 'inline'); }
  get restUrl(): string { return (this.getAttribute('rest-url') || '/wp-json/lgs-interactive-video/v1/').replace(/\/?$/, '/'); }

  private persistOpenState(open: boolean): void {
    if (this.mode === 'inline' || !this.flowSlug) return;
    try {
      const key = `lgsiv-open:${this.flowSlug}`;
      if (open) sessionStorage.setItem(key, '1');
      else sessionStorage.removeItem(key);
    } catch { /* optional UI continuity */ }
  }

  private shouldRestoreOpenState(): boolean {
    if (this.mode === 'inline' || !this.flowSlug) return false;
    try { return sessionStorage.getItem(`lgsiv-open:${this.flowSlug}`) === '1'; }
    catch { return false; }
  }

  async load(): Promise<void> {
    if (this.previewConfig) return;
    const slug = this.flowSlug;
    if (!slug) { this.errorMessage = 'Es wurde keine Video Journey ausgewählt.'; this.render(); return; }
    this.loading = true; this.render();
    try {
      const response = await fetch(`${this.restUrl}public/flows/${encodeURIComponent(slug)}`, {
        credentials: 'same-origin',
        cache: 'no-cache',
      });
      if (!response.ok) throw new Error(response.status === 404 ? 'Diese Video Journey ist nicht veröffentlicht.' : 'Die Video Journey konnte nicht geladen werden.');
      this.graph = repairFlowGraphStart(await response.json() as FlowGraph).graph;
      this.restoreSession();
      this.restorePageCompanion();
      this.isOpen = this.pageCompanion ? false : this.shouldRestoreOpenState();
      this.captureCurrentPageContext();
      this.beginPageTracking();
      if (this.session) {
        applySitePersonalization(this.session.profile);
        this.scheduleFramefieldPersonalization();
        this.syncFramefieldBridge();
      }
      this.errorMessage = '';
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Unbekannter Ladefehler.';
    } finally {
      this.loading = false;
      this.render();
      if (this.isOpen && !['inline', 'widget'].includes(this.mode)) {
        this.suspendPeers();
        this.previousOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
      }
    }
  }

  open(): void {
    this.chapterControlStarted = true;
    this.pendingChapterId = null;
    const shouldAnimate = !this.isOpen;
    this.launcherCollapsed = false;
    this.launcherExpanded = true;
    this.openingPipExpanded = true;
    this.companionCollapsed = false;
    this.restoreFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (!['inline', 'widget'].includes(this.mode)) this.suspendPeers();
    this.isOpen = true;
    this.animatePlayerOpen = shouldAnimate;
    if (this.projectCompanion || this.pageCompanion) this.pausePortfolioMedia();
    this.persistOpenState(true);
    this.render();
    this.animatePlayerOpen = false;
    if (!['inline', 'widget'].includes(this.mode)) {
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
    }
    queueMicrotask(() => this.root.querySelector<HTMLElement>('.stage button,.stage a')?.focus({ preventScroll: true }));
    this.track('opened');
  }

  close(): void {
    this.chapterControlStarted = false;
    this.pendingChapterId = null;
    this.isOpen = false;
    // Liquid Glass: the next opening starts as the regular widget, not in the cinema view.
    if (this.isLiquidGlass && this.widgetSize === 'large') this.widgetSize = 'standard';
    this.collapseToSideTab();
    this.persistOpenState(false);
    this.releaseMedia();
    this.render();
    document.documentElement.style.overflow = this.previousOverflow;
    this.restorePeers();
    this.restoreFocus?.focus({ preventScroll: true });
    this.track('closed');
  }

  private collapseToSideTab(): void {
    this.launcherCollapsed = true;
    this.launcherExpanded = false;
    this.openingPipExpanded = false;
    this.companionCollapsed = false;
  }

  private stopPortfolioDialogWatch(): void {
    this.portfolioDialogObserver?.disconnect();
    this.portfolioDialogObserver = null;
    if (this.portfolioDialogOpenTimer !== null) window.clearTimeout(this.portfolioDialogOpenTimer);
    this.portfolioDialogOpenTimer = null;
    this.restorePortfolioHome();
  }

  // Native modal dialogs live above every document z-index and make outside
  // controls inert. Keep the same player instance inside that dialog (no reload).
  private enterPortfolioDialog(dialog: HTMLElement): void {
    if (this.parentElement === dialog) return;
    if (!this.portfolioHome) {
      this.portfolioHome = document.createComment('lgs-video-portfolio-home');
      this.before(this.portfolioHome);
    }
    this.relocatingForPortfolio = true;
    try {
      if (typeof dialog.moveBefore === 'function') dialog.moveBefore(this, null);
      else dialog.append(this);
      this.setAttribute('data-portfolio-foreground', '');
    } finally { this.relocatingForPortfolio = false; }
  }

  private restorePortfolioHome(): void {
    const home = this.portfolioHome;
    if (!home) return;
    this.relocatingForPortfolio = true;
    try {
      if (home.parentElement && typeof home.parentElement.moveBefore === 'function') {
        home.parentElement.moveBefore(this, home);
        home.remove();
      } else home.replaceWith(this);
      this.removeAttribute('data-portfolio-foreground');
      this.portfolioHome = null;
    } finally { this.relocatingForPortfolio = false; }
  }

  private dismissProjectCompanionWhenDialogCloses(projectId: string, card: HTMLElement): void {
    this.stopPortfolioDialogWatch();
    const gallery = card.closest<HTMLElement>('[data-ffp-gallery]');
    const dialog = gallery?.querySelector<HTMLElement>('.ffp-dialog');
    if (!dialog) {
      this.projectCompanion = null;
      this.collapseToSideTab();
      this.render();
      return;
    }

    let dialogWasOpen = !dialog.hidden;
    const sync = (): void => {
      if (!dialog.hidden) {
        dialogWasOpen = true;
        this.enterPortfolioDialog(dialog);
        return;
      }
      if (!dialogWasOpen || this.projectCompanion?.projectId !== projectId) return;
      this.stopPortfolioDialogWatch();
      this.projectCompanion = null;
      this.collapseToSideTab();
      this.render();
      this.track('project-companion-dismiss', { value: projectId, reason: 'project-dialog-closed' });
    };

    this.portfolioDialogObserver = new MutationObserver(sync);
    this.portfolioDialogObserver.observe(dialog, { attributes: true, attributeFilter: ['hidden', 'open'] });
    this.enterPortfolioDialog(dialog);
    this.portfolioDialogOpenTimer = window.setTimeout(() => {
      this.portfolioDialogOpenTimer = null;
      if (!dialog.hidden) {
        dialogWasOpen = true;
        this.enterPortfolioDialog(dialog);
        return;
      }
      if (this.projectCompanion?.projectId !== projectId) return;
      this.stopPortfolioDialogWatch();
      this.projectCompanion = null;
      this.collapseToSideTab();
      this.render();
    }, 1600);
  }

  private showProjectCompanion(projectId: string, title: string, client: string, poster: string, preview: string, card: HTMLElement): void {
    if (!this.graph || this.mode === 'inline' || this.graph.settings.projectCompanion === false) {
      this.releaseMedia();
      return;
    }
    this.isOpen = false;
    this.persistOpenState(false);
    this.releaseMedia();
    document.documentElement.style.overflow = this.previousOverflow;
    this.restorePeers();
    this.projectCompanion = { projectId, title, client, poster, preview };
    this.companionCollapsed = false;
    this.dismissProjectCompanionWhenDialogCloses(projectId, card);
    this.render();
    this.track('project-companion-shown', { value: projectId });
  }

  restart(): void {
    document.querySelectorAll<HTMLInputElement>('input[name="lgs_video_need_profile"]').forEach((field) => {
      field.value = '';
      field.removeAttribute('value');
    });
    this.resetSession(true);
    this.render();
  }

  /** Request an existing chapter without opening or starting the journey.
   * after-current queues only the newest request until the current clip ends.
   * immediate is reserved for an explicit chapter-button click and clears a queue.
   */
  requestChapter(nodeId: string, options: { mode?: 'after-current' | 'immediate' } = {}): boolean {
    if (!options || typeof options !== 'object' || Array.isArray(options)) return false;
    const mode = options.mode ?? 'after-current';
    if (!['after-current', 'immediate'].includes(mode) || typeof nodeId !== 'string' || !nodeId) return false;
    if (!this.chapterControlStarted || !this.graph || !this.session || this.previewConfig) return false;
    if (this.mode !== 'inline' && !this.isOpen) return false;
    if (!this.graph.nodes.some((node) => node.id === nodeId)) return false;
    if (mode === 'immediate') {
      this.pendingChapterId = null;
      return this.enterRequestedChapter(nodeId);
    }
    const video = this.root.querySelector<HTMLVideoElement>('video.media');
    // Scroll must not restart a paused clip or leave an unbounded stale request.
    if (!video || (video.paused && !video.ended)) { this.pendingChapterId = null; return false; }
    if (nodeId === this.session.currentNodeId) { this.pendingChapterId = null; return true; }
    if (video.ended) { this.pendingChapterId = null; return this.enterRequestedChapter(nodeId); }
    this.pendingChapterId = nodeId;
    return true;
  }

  private enterRequestedChapter(nodeId: string): boolean {
    if (!this.chapterControlStarted || !this.graph || !this.session || !this.graph.nodes.some((node) => node.id === nodeId)) return false;
    if (nodeId === this.session.currentNodeId) return true;
    const nextVisits = (this.session.visits[nodeId] ?? 0) + 1;
    if (nextVisits > this.graph.settings.maxNodeVisits) return false;
    const previous = this.currentNode();
    // A chapter jump records navigation, never a fabricated answer or mutation.
    if (previous) this.session.history.push({ nodeId: previous.id, edgeId: '', evidenceIds: [], occurredAt: new Date().toISOString() });
    this.session.currentNodeId = nodeId;
    this.session.visits[nodeId] = nextVisits;
    this.saveSession();
    this.publishLeadContext(false);
    this.releaseMedia();
    this.render();
    this.track('chapter-enter', { nodeId });
    this.track('node-enter', { nodeId });
    return true;
  }

  private finishRequestedChapter(currentNodeId: string): boolean {
    if (this.session?.currentNodeId !== currentNodeId) return false;
    const target = this.pendingChapterId;
    this.pendingChapterId = null;
    if (!target || !this.chapterControlStarted) return false;
    if (this.mode !== 'inline' && !this.isOpen) return false;
    return this.enterRequestedChapter(target);
  }

  getNeedProfile(): NeedProfile | null { return this.session?.profile ?? null; }
  getPortfolioMatches(profile: NeedProfile): Recommendation[] | null {
    if (!this.graph) return null;
    return rankPortfolioWithFallback(profile, this.graph.catalog ?? [], 6).items;
  }

  private resetSession(track = true): void {
    this.chapterControlStarted = false;
    this.pendingChapterId = null;
    if (!this.graph) return;
    this.releaseMedia();
    this.mediaNodeId = '';
    this.interactionRevealed = false;
    this.interactionDismissed = false;
    this.interactionAutoTriggered = false;
    this.locationPromptDismissed = this.wasLocationPromptHandled();
    this.playerStarted = false;
    this.choiceSwipeHintPlayed = false;
    const id = uid('session');
    const evidence: NeedEvidence[] = [];
    const city = this.getAttribute('city') || undefined;
    const regionCode = this.getAttribute('region') || undefined;
    this.session = {
      id,
      currentNodeId: this.graph.startNodeId,
      history: [],
      profile: calculateNeedProfile(id, evidence, { city, regionCode }),
      visits: { [this.graph.startNodeId]: 1 },
      startedAt: new Date().toISOString(),
    };
    this.saveSession();
    if (track) this.track('restarted');
  }

  private restoreSession(): void {
    if (!this.graph) return;
    this.locationPromptDismissed = this.wasLocationPromptHandled();
    const city = this.getAttribute('city') || undefined;
    const regionCode = this.getAttribute('region') || undefined;
    if (this.graph.settings.resume === 'session') {
      try {
        const stored = sessionStorage.getItem(`lgsiv:${this.graph.slug}:${this.graph.releaseVersion ?? 0}`);
        if (stored) {
          const parsed = JSON.parse(stored) as PlayerSession;
          if (this.graph.nodes.some((node) => node.id === parsed.currentNodeId)) {
            this.session = parsed;
            if (city || regionCode) this.session.profile = calculateNeedProfile(parsed.id, parsed.profile.evidence, { city: city || parsed.profile.city, regionCode: regionCode || parsed.profile.regionCode });
            return;
          }
        }
      } catch { /* session storage is optional */ }
    }
    this.resetSession(false);
  }

  private saveSession(): void {
    if (!this.graph || !this.session || this.graph.settings.resume !== 'session') return;
    try { sessionStorage.setItem(`lgsiv:${this.graph.slug}:${this.graph.releaseVersion ?? 0}`, JSON.stringify(this.session)); } catch { /* optional */ }
  }

  private locationPromptKey(): string {
    return `lgsiv-location-prompt:${this.flowSlug}`;
  }

  private wasLocationPromptHandled(): boolean {
    if (!this.flowSlug) return false;
    try { return sessionStorage.getItem(this.locationPromptKey()) === '1'; } catch { return this.locationPromptDismissed; }
  }

  private markLocationPromptHandled(): void {
    this.locationPromptDismissed = true;
    if (!this.flowSlug) return;
    try { sessionStorage.setItem(this.locationPromptKey(), '1'); } catch { /* session storage is optional */ }
  }

  private pageCompanionKey(): string { return `lgsiv-page-companion:${this.flowSlug}`; }

  private normalizedPath(value: string): string {
    try {
      const path = new URL(value, window.location.href).pathname.replace(/\/+$/, '');
      return path || '/';
    } catch { return '/'; }
  }

  private clearPageCompanion(): void {
    if (!this.flowSlug) return;
    try { sessionStorage.removeItem(this.pageCompanionKey()); } catch { /* optional continuity */ }
  }

  private restorePageCompanion(): void {
    this.pageCompanion = null;
    if (!this.flowSlug) return;
    try {
      const stored = sessionStorage.getItem(this.pageCompanionKey());
      if (!stored) return;
      const parsed = JSON.parse(stored) as PageCompanionState;
      const fresh = Number.isFinite(parsed.createdAt) && Date.now() - parsed.createdAt < 4 * 60 * 60 * 1000;
      if (fresh && parsed.targetPath === this.normalizedPath(window.location.href) && this.graph?.nodes.some((node) => node.id === parsed.nodeId)) this.pageCompanion = parsed;
      else if (!fresh) this.clearPageCompanion();
    } catch { this.clearPageCompanion(); }
  }

  private preparePageNavigation(pageUrl?: string): void {
    if (pageUrl && this.graph && this.session) {
      const overlay = this.currentNode()?.content.pageOverlay;
      try {
        const target = new URL(pageUrl, window.location.href);
        if (overlay && target.origin === window.location.origin) {
          const state: PageCompanionState = {
            nodeId: this.session.currentNodeId,
            targetPath: this.normalizedPath(target.href),
            title: overlay.title?.trim() || 'Passender Website-Bereich',
            eyebrow: overlay.companionEyebrow?.trim() || 'Auf dieser Seite',
            message: playerCopy(this.graph, 'pageCompanion', overlay.companionMessage?.trim() || overlay.description?.trim()),
            video: safeUrl(overlay.companionVideoUrl),
            poster: safeUrl(overlay.companionPosterUrl || overlay.image),
            highlightMinimize: overlay.highlightMinimize !== false,
            createdAt: Date.now(),
          };
          sessionStorage.setItem(this.pageCompanionKey(), JSON.stringify(state));
        }
      } catch { /* external links still open normally */ }
    }
    this.persistOpenState(false);
    this.saveSession();
    this.root.querySelector<HTMLVideoElement>('video.media')?.pause();
    this.root.querySelector<HTMLAudioElement>('audio[data-end-audio]')?.pause();
  }

  private currentNode(): FlowNode | null {
    if (!this.graph || !this.session) return null;
    return this.graph.nodes.find((node) => node.id === this.session?.currentNodeId) ?? null;
  }

  private choiceSwipeMetrics(): { scroller: HTMLElement; cards: HTMLElement[]; step: number; maxScroll: number; pages: number; page: number } | null {
    const scroller = this.root.querySelector<HTMLElement>('[data-choice-swipe]');
    if (!scroller) return null;
    const cards = Array.from(scroller.querySelectorAll<HTMLElement>('.choice'));
    if (!cards.length) return null;
    const gap = cards.length > 1 ? Math.max(0, cards[1].offsetLeft - cards[0].offsetLeft - cards[0].offsetWidth) : 0;
    const step = Math.max(1, cards[0].offsetWidth + gap);
    const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const pages = Math.max(1, Math.ceil(maxScroll / step) + 1);
    const page = Math.min(pages, Math.max(1, Math.round(scroller.scrollLeft / step) + 1));
    return { scroller, cards, step, maxScroll, pages, page };
  }

  private syncChoiceSwipe(): void {
    const metrics = this.choiceSwipeMetrics();
    if (!metrics) return;
    const shell = metrics.scroller.closest<HTMLElement>('[data-choice-swipe-shell]');
    const previous = shell?.querySelector<HTMLButtonElement>('[data-action="swipe-prev"]');
    const next = shell?.querySelector<HTMLButtonElement>('[data-action="swipe-next"]');
    const index = shell?.querySelector<HTMLElement>('[data-choice-swipe-index]');
    const total = shell?.querySelector<HTMLElement>('[data-choice-swipe-total]');
    const atStart = metrics.scroller.scrollLeft <= 3;
    const atEnd = metrics.scroller.scrollLeft >= metrics.maxScroll - 3;
    shell?.classList.toggle('is-start', atStart);
    shell?.classList.toggle('is-end', atEnd);
    if (previous) previous.disabled = atStart;
    if (next) next.disabled = atEnd;
    if (index) index.textContent = String(metrics.page);
    if (total) total.textContent = String(metrics.pages);
  }

  private moveChoiceSwipe(direction: -1 | 1): void {
    const metrics = this.choiceSwipeMetrics();
    if (!metrics) return;
    const targetPage = Math.min(metrics.pages, Math.max(1, metrics.page + direction));
    metrics.scroller.scrollTo({
      left: Math.min(metrics.maxScroll, (targetPage - 1) * metrics.step),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
    window.setTimeout(() => this.syncChoiceSwipe(), 320);
  }

  private playChoiceSwipeHint(): void {
    const metrics = this.choiceSwipeMetrics();
    const node = this.currentNode();
    const isFirstQuestion = Boolean(this.graph && this.session && node?.id === this.graph.startNodeId && this.session.history.length === 0);
    if (!metrics || !node || !isFirstQuestion || this.choiceSwipeHintPlayed || metrics.maxScroll < 20 || metrics.scroller.scrollLeft > 3 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.choiceSwipeHintPlayed = true;
    const distance = Math.min(54, Math.max(34, metrics.step * 0.18), metrics.maxScroll);
    metrics.scroller.classList.add('is-peeking');
    metrics.scroller.scrollTo({ left: distance, behavior: 'smooth' });
    window.setTimeout(() => metrics.scroller.scrollTo({ left: 0, behavior: 'smooth' }), 520);
    window.setTimeout(() => { metrics.scroller.classList.remove('is-peeking'); this.syncChoiceSwipe(); }, 980);
  }

  private bindChoiceSwipe(): void {
    const scroller = this.root.querySelector<HTMLElement>('[data-choice-swipe]');
    if (!scroller) return;
    let frame = 0;
    scroller.addEventListener('scroll', () => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => { frame = 0; this.syncChoiceSwipe(); });
    }, { passive: true });
    scroller.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.moveChoiceSwipe(event.key === 'ArrowRight' ? 1 : -1);
    });
    this.syncChoiceSwipe();
    if (this.interactionRevealed) window.setTimeout(() => this.playChoiceSwipeHint(), 1550);
  }

  private setWidgetSize(direction: -1 | 1): void {
    const sizes: Array<'compact' | 'standard' | 'large'> = ['compact', 'standard', 'large'];
    const index = Math.max(0, Math.min(sizes.length - 1, sizes.indexOf(this.widgetSize) + direction));
    this.applyWidgetSize(sizes[index]);
  }

  private applyWidgetSize(size: 'compact' | 'standard' | 'large'): void {
    const sizes: Array<'compact' | 'standard' | 'large'> = ['compact', 'standard', 'large'];
    const index = sizes.indexOf(size);
    this.widgetSize = size;
    const overlay = this.root.querySelector<HTMLElement>('.overlay.mode-widget');
    if (!overlay) return;
    const before = this.isLiquidGlass ? overlay.getBoundingClientRect() : null;
    overlay.classList.remove('widget-size-compact', 'widget-size-standard', 'widget-size-large');
    overlay.classList.add(`widget-size-${this.widgetSize}`);
    overlay.querySelector<HTMLButtonElement>('[data-action="widget-smaller"]')?.toggleAttribute('disabled', index === 0);
    overlay.querySelector<HTMLButtonElement>('[data-action="widget-larger"]')?.toggleAttribute('disabled', index === sizes.length - 1);
    const cinema = overlay.querySelector<HTMLButtonElement>('[data-action="widget-cinema"]');
    if (cinema) {
      const label = this.cinemaLabel(size === 'large');
      cinema.setAttribute('aria-pressed', String(size === 'large'));
      cinema.setAttribute('aria-label', label);
      cinema.title = label;
    }
    // The cinema/fullscreen view is centered by the design; a dragged position stays
    // stored for the regular widget and is restored when leaving it.
    if (!(this.isLiquidGlass && size === 'large')) window.requestAnimationFrame(() => this.clampWidgetOffset(overlay));
    if (before) this.animateWidgetResize(overlay, before);
    this.track('widget-size', { value: this.widgetSize });
  }

  private cinemaLabel(active: boolean): string {
    if (this.isMobileViewport()) return active ? 'Vollbild verlassen' : 'Vollbild';
    return active ? 'Kinoansicht verlassen' : 'Kinoansicht – ganzes 16:9-Video';
  }

  /** FLIP with a critically damped spring: transform only, interruptible, skipped for reduced motion and lite mode. */
  private animateWidgetResize(overlay: HTMLElement, before: DOMRect): void {
    if (this.hasAttribute('data-lite') || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const stage = overlay.querySelector<HTMLElement>('.stage');
    if (!stage || typeof stage.animate !== 'function') return;
    const after = overlay.getBoundingClientRect();
    if (!after.width || !after.height) return;
    const dx = before.left - after.left + (before.width - after.width) / 2;
    const dy = before.top - after.top + (before.height - after.height) / 2;
    const sx = before.width / after.width;
    const sy = before.height / after.height;
    if (Math.abs(dx) < 1 && Math.abs(dy) < 1 && Math.abs(sx - 1) < .01) return;
    stage.getAnimations().forEach((animation) => animation.cancel());
    try {
      stage.animate([
        { transform: `translate(${dx}px,${dy}px) scale(${sx},${sy})`, opacity: .85 },
        { transform: 'none', opacity: 1 },
      ], { duration: 520, easing: CSS.supports('transition-timing-function', 'linear(0,1)') ? SPRING_SETTLE : 'cubic-bezier(.16,1,.3,1)' });
    } catch { /* Visual polish only. */ }
  }

  private syncLiteMode(): void {
    const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
    const lite = isLiteEnvironment({
      rootClassList: document.documentElement.classList,
      rootHasAttribute: (name) => document.documentElement.hasAttribute(name),
      saveData: Boolean(connection?.saveData),
    });
    if (lite !== this.hasAttribute('data-lite')) this.toggleAttribute('data-lite', lite);
  }

  private clampWidgetOffset(overlay: HTMLElement): void {
    if (!this.widgetOffset) return;
    const rect = overlay.getBoundingClientRect();
    const margin = 8;
    const maxLeft = Math.max(margin, window.innerWidth - rect.width - margin);
    const maxTop = Math.max(margin, window.innerHeight - rect.height - margin);
    this.widgetOffset = {
      left: Math.max(margin, Math.min(maxLeft, this.widgetOffset.left)),
      top: Math.max(margin, Math.min(maxTop, this.widgetOffset.top)),
    };
    overlay.style.left = `${this.widgetOffset.left}px`;
    overlay.style.top = `${this.widgetOffset.top}px`;
    overlay.style.right = 'auto';
    overlay.style.bottom = 'auto';
  }

  private handleWidgetPointerDown = (event: PointerEvent): void => {
    if (this.mode !== 'widget' || event.button !== 0) return;
    const handle = event.composedPath().find((item) => item instanceof HTMLElement && item.matches('[data-widget-drag]')) as HTMLElement | undefined;
    const overlay = this.root.querySelector<HTMLElement>('.overlay.mode-widget');
    if (!handle || !overlay) return;
    this.stopWidgetDrag(false);
    const rect = overlay.getBoundingClientRect();
    this.widgetOffset = { left: rect.left, top: rect.top };
    this.widgetDrag = { pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, originLeft: rect.left, originTop: rect.top, handle };
    window.addEventListener('pointermove', this.handleWidgetPointerMove, { passive: false });
    window.addEventListener('pointerup', this.handleWidgetPointerUp);
    window.addEventListener('pointercancel', this.handleWidgetPointerUp);
    try { handle.setPointerCapture(event.pointerId); } catch { /* Pointer capture is optional. */ }
    overlay.classList.add('is-dragging');
    event.preventDefault();
  };

  private handleWidgetPointerMove = (event: PointerEvent): void => {
    if (!this.widgetDrag || event.pointerId !== this.widgetDrag.pointerId) return;
    const overlay = this.root.querySelector<HTMLElement>('.overlay.mode-widget');
    if (!overlay) return;
    this.widgetOffset = {
      left: this.widgetDrag.originLeft + event.clientX - this.widgetDrag.startX,
      top: this.widgetDrag.originTop + event.clientY - this.widgetDrag.startY,
    };
    this.clampWidgetOffset(overlay);
    event.preventDefault();
  };

  private handleWidgetPointerUp = (event: PointerEvent): void => {
    if (!this.widgetDrag || event.pointerId !== this.widgetDrag.pointerId) return;
    this.stopWidgetDrag(true);
  };

  private stopWidgetDrag(trackMove: boolean): void {
    if (!this.widgetDrag) return;
    const { handle, pointerId } = this.widgetDrag;
    try {
      if (handle.hasPointerCapture(pointerId)) handle.releasePointerCapture(pointerId);
    } catch { /* The handle may already have left the document. */ }
    window.removeEventListener('pointermove', this.handleWidgetPointerMove);
    window.removeEventListener('pointerup', this.handleWidgetPointerUp);
    window.removeEventListener('pointercancel', this.handleWidgetPointerUp);
    this.root.querySelector<HTMLElement>('.overlay.mode-widget')?.classList.remove('is-dragging');
    this.widgetDrag = null;
    if (trackMove) this.track('widget-moved');
  }

  private handleClick = (event: Event): void => {
    const target = event.composedPath().find((item) => item instanceof HTMLElement) as HTMLElement | undefined;
    if (!target) return;
    if (this.hasAttribute('design-mode')) {
      const designTarget = target.closest('h2,.body,.eyebrow')
        ? 'typography'
        : target.closest('.topbar,.icon-btn,.media-toggle,.sound-toggle')
          ? 'controls'
        : target.closest('.choice,.primary,.secondary,.inquiry-submit,.quick-link,.recommendation,.portfolio-peek')
        ? 'buttons'
        : target.closest('.content,.interaction-teaser,.wait-hint')
          ? 'panel'
          : target.closest('.journey-timeline')
            ? 'motion'
            : 'frame';
      event.preventDefault();
      event.stopPropagation();
      this.dispatchEvent(new CustomEvent('lgs-video:design-select', { bubbles: true, composed: true, detail: { target: designTarget } }));
      return;
    }
    const action = target.closest<HTMLElement>('[data-action]')?.dataset.action;
    if (action === 'widget-smaller') this.setWidgetSize(-1);
    if (action === 'widget-larger') this.setWidgetSize(1);
    if (action === 'widget-cinema') this.applyWidgetSize(this.widgetSize === 'large' ? 'standard' : 'large');
    // Liquid Glass cinema view: a click on the dimmed page returns to the regular widget.
    if (!action && this.isLiquidGlass && this.widgetSize === 'large' && target.matches('.overlay.mode-widget')) this.applyWidgetSize('standard');
    if (action === 'open') this.open();
    if (action === 'close') this.close();
    if (action === 'collapse-launcher') { this.launcherCollapsed = true; this.launcherExpanded = false; this.openingPipExpanded = false; this.render(); }
    if (action === 'expand-launcher') { this.launcherCollapsed = false; this.launcherExpanded = true; this.companionCollapsed = false; this.render(); }
    if (action === 'expand-opening-pip') { this.launcherCollapsed = false; this.launcherExpanded = false; this.openingPipExpanded = true; this.companionCollapsed = false; this.render(); }
    if (action === 'collapse-project-companion') {
      this.companionCollapsed = true;
      this.track(this.pageCompanion ? 'page-companion-collapse' : 'project-companion-collapse', { value: this.pageCompanion?.nodeId ?? this.projectCompanion?.projectId ?? '' });
      this.render();
    }
    if (action === 'expand-project-companion') { this.companionCollapsed = false; this.render(); }
    if (action === 'resume-project-companion') {
      this.track(this.pageCompanion ? 'page-companion-resume' : 'project-companion-resume', { value: this.pageCompanion?.nodeId ?? this.projectCompanion?.projectId ?? '' });
      this.pausePortfolioMedia();
      this.open();
    }
    if (action === 'dismiss-project-companion') {
      this.track(this.pageCompanion ? 'page-companion-dismiss' : 'project-companion-dismiss', { value: this.pageCompanion?.nodeId ?? this.projectCompanion?.projectId ?? '' });
      this.projectCompanion = null;
      this.pageCompanion = null;
      this.stopPortfolioDialogWatch();
      this.clearPageCompanion();
      this.collapseToSideTab();
      this.render();
    }
    if (action === 'toggle-companion-sound') {
      const video = this.root.querySelector<HTMLVideoElement>('[data-companion-video]');
      const shell = this.root.querySelector<HTMLElement>('.project-companion');
      if (video) {
        video.muted = !video.muted;
        if (!video.muted) this.holdPortfolioMediaForCompanion();
        shell?.classList.remove('needs-sound');
        if (video.paused) void video.play().catch(() => undefined);
        this.syncCompanionSoundButton(video);
        this.track('project-companion-sound', { value: video.muted ? 'muted' : 'audible' });
      }
    }
    if (action === 'replay-companion') {
      const video = this.root.querySelector<HTMLVideoElement>('[data-companion-video]');
      if (video) {
        video.currentTime = 0;
        this.holdPortfolioMediaForCompanion();
        this.root.querySelector<HTMLElement>('.project-companion')?.classList.remove('is-ended');
        this.signalExclusivePlayback();
        void video.play().catch(() => undefined);
        this.track(this.pageCompanion ? 'page-companion-replay' : 'project-companion-replay', { value: this.pageCompanion?.nodeId ?? this.projectCompanion?.projectId ?? '' });
      }
    }
    if (action === 'back') this.goBack();
    if (action === 'restart') this.restart();
    if (action === 'location') this.requestLocation();
    if (action === 'skip-location') { this.markLocationPromptHandled(); this.render(); }
    if (action === 'toggle-video' || action === 'start-video') {
      const video = this.root.querySelector<HTMLVideoElement>('video.media');
      this.pendingChapterId = null;
      if (action === 'start-video' || video?.paused) this.chapterControlStarted = true;
      if (action === 'start-video') this.playerStarted = true;
      if (video?.ended) {
        this.stopEndAudio();
        this.root.querySelector<HTMLElement>('.stage')?.classList.remove('is-video-ended');
        video.currentTime = 0;
      }
      if (video?.paused) void video.play().catch(() => this.announce('Das Video konnte nicht gestartet werden.'));
      else video?.pause();
    }
    if (action === 'toggle-sound') {
      const video = this.root.querySelector<HTMLVideoElement>('video.media');
      const audio = this.root.querySelector<HTMLAudioElement>('audio[data-end-audio]');
      if (video) {
        const activeMedia: HTMLMediaElement = video.ended && audio ? audio : video;
        activeMedia.muted = !activeMedia.muted;
        video.muted = activeMedia.muted;
        if (audio) audio.muted = activeMedia.muted;
        this.syncSoundButton(video, audio);
      }
    }
    if (action === 'reveal-interaction') this.revealInteraction('manual');
    if (action === 'collapse-interaction') this.collapseInteraction();
    if (action === 'dismiss-page-overlay') {
      const overlay = target.closest<HTMLElement>('[data-timed-page]');
      if (overlay) { overlay.dataset.dismissed = 'true'; overlay.classList.remove('is-visible'); }
      this.track('page-overlay-dismiss', { nodeId: this.currentNode()?.id ?? '' });
    }
    if (action === 'swipe-prev') this.moveChoiceSwipe(-1);
    if (action === 'swipe-next') this.moveChoiceSwipe(1);
    const choice = target.closest<HTMLElement>('[data-choice]')?.dataset.choice;
    if (choice) this.choose(choice);
    const framefieldProject = target.closest<HTMLElement>('[data-framefield-project]')?.dataset.framefieldProject;
    if (framefieldProject) {
      const card = document.querySelector<HTMLElement>(`[data-ffp-card][data-project-id="${CSS.escape(framefieldProject)}"]`);
      const trigger = card?.querySelector<HTMLElement>('[data-ffp-open]');
      if (card && trigger) {
        this.track('portfolio-project-open', { value: framefieldProject });
        const posterNode = card.querySelector<HTMLImageElement>('.ffp-poster');
        const poster = safeUrl(posterNode?.currentSrc || posterNode?.src);
        const preview = safeUrl(trigger.dataset.previewVideo);
        const client = trigger.dataset.client?.trim() || trigger.dataset.type?.trim() || 'Filmreferenz';
        this.showProjectCompanion(framefieldProject, trigger.dataset.title?.trim() || 'Passendes Projekt', client, poster, preview, card);
        this.holdPortfolioMediaForCompanion();
        window.setTimeout(() => {
          card.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
          trigger.click();
          this.holdPortfolioMediaForCompanion();
          window.setTimeout(() => this.holdPortfolioMediaForCompanion(), 350);
        }, 180);
      }
      return;
    }
    const recommendation = target.closest<HTMLElement>('[data-recommendation]')?.dataset.recommendation;
    if (recommendation) {
      this.track('recommendation-click', { value: recommendation });
      this.preparePageNavigation();
    }
    const cta = target.closest<HTMLElement>('[data-cta]');
    if (cta) {
      this.publishLeadContext();
      this.track('cta', { value: cta.dataset.cta || '' });
      this.preparePageNavigation();
    }
    const pageLink = target.closest<HTMLAnchorElement>('.actions a.secondary');
    if (pageLink) this.preparePageNavigation();
    const timedPageLink = target.closest<HTMLAnchorElement>('[data-timed-page-link]');
    if (timedPageLink) {
      this.track('page-overlay-click', { nodeId: this.currentNode()?.id ?? '', value: timedPageLink.href });
      this.preparePageNavigation(timedPageLink.href);
    }
  };

  private isMobileViewport(): boolean {
    if (this.hasAttribute('preview-desktop')) return false;
    return this.hasAttribute('preview-mobile') || window.matchMedia('(max-width: 620px)').matches;
  }

  private effectiveAspect(): '16:9' | '9:16' | '4:5' | '1:1' {
    const configured = (this.getAttribute('aspect') || this.graph?.settings.autoAspect || '16:9') as '16:9' | '9:16' | '4:5' | '1:1';
    const responsive = (this.graph?.settings.aspectMode ?? (this.graph?.settings.mobileAutoReframe === false ? 'fixed' : 'responsive')) === 'responsive';
    return responsive && this.isMobileViewport() ? (this.graph?.settings.mobileAspect || '9:16') : configured;
  }

  private handleViewportResize = (): void => {
    const mobile = this.isMobileViewport();
    if (mobile === this.lastMobileViewport) return;
    this.lastMobileViewport = mobile;
    if (mobile && !this.isOpen && !this.projectCompanion && !this.pageCompanion) {
      this.launcherCollapsed = false;
      this.launcherExpanded = false;
      this.openingPipExpanded = false;
    }
    this.render();
  };

  private handleChange = (event: Event): void => {
    const select = event.target instanceof HTMLSelectElement ? event.target : null;
    if (!select?.matches('[data-choice-select]') || !select.value) return;
    const choice = select.value;
    select.value = '';
    this.choose(choice);
  };

  private handleSubmit = async (event: Event): Promise<void> => {
    const form = event.composedPath().find((item) => item instanceof HTMLFormElement && item.matches('[data-inquiry-form]')) as HTMLFormElement | undefined;
    if (!form || !this.graph || !this.session) return;
    event.preventDefault();
    const submit = form.querySelector<HTMLButtonElement>('[type="submit"]');
    const status = form.querySelector<HTMLElement>('[data-inquiry-status]');
    if (submit?.disabled) return;
    if (this.previewConfig) {
      if (status) status.textContent = 'In der Vorschau wird keine echte Anfrage gesendet.';
      return;
    }
    const data = new FormData(form);
    // Optionales Teilen: ohne Häkchen gehen weder Antworten noch Profil oder Referenzen mit.
    const shareContext = this.currentNode()?.content.formShareAnswers !== 'optional' || data.get('shareAnswers') === 'yes';
    if (submit) { submit.disabled = true; submit.textContent = 'Anfrage wird gesendet …'; }
    if (status) status.textContent = '';
    try {
      const response = await fetch(`${this.restUrl}inquiries`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          flowId: Number(this.graph.id) || 0,
          flowSlug: this.graph.slug,
          releaseVersion: this.graph.releaseVersion ?? 0,
          nodeId: this.session.currentNodeId,
          sessionId: this.session.id,
          pagePath: window.location.pathname,
          path: [...this.session.history.map((entry) => entry.nodeId), this.session.currentNodeId],
          needProfile: shareContext ? this.session.profile : {},
          journeyAnswers: shareContext ? this.journeyAnswers() : [],
          portfolioReferences: shareContext ? this.session.integrations?.framefield ?? null : null,
          name: data.get('name'),
          email: data.get('email'),
          company: data.get('company'),
          phone: data.get('phone'),
          message: data.get('message'),
          consent: data.get('consent') === 'yes',
          contextConsent: data.get('consent') === 'yes' && shareContext,
          website: data.get('website'),
          subject: data.get('subject'),
        }),
      });
      const result = await response.json().catch(() => ({})) as { message?: string };
      if (!response.ok) throw new Error(result.message || 'Die Anfrage konnte gerade nicht gesendet werden.');
      this.publishLeadContext();
      this.track('inquiry-submit');
      form.reset();
      const wrap = form.closest<HTMLElement>('[data-inquiry-wrap]');
      wrap?.classList.add('is-complete');
      wrap?.querySelector<HTMLElement>('.inquiry-success')?.setAttribute('aria-hidden', 'false');
      form.setAttribute('aria-hidden', 'true');
      wrap?.querySelector<HTMLElement>('.inquiry-success h3')?.focus({ preventScroll: true });
      this.announce('Ihre Anfrage wurde erfolgreich übermittelt.');
    } catch (error) {
      if (status) status.textContent = error instanceof Error ? error.message : 'Die Anfrage konnte gerade nicht gesendet werden.';
      if (submit) { submit.disabled = false; submit.textContent = submit.dataset.label || 'Unverbindlich anfragen'; }
    }
  };

  private handleKeydown = (event: KeyboardEvent): void => {
    if (!this.isOpen || this.mode === 'inline') return;
    if (event.key === 'Escape' && this.mode === 'widget' && this.isLiquidGlass && this.widgetSize === 'large') { this.applyWidgetSize('standard'); return; }
    if (event.key === 'Escape') { this.close(); return; }
    if (this.mode === 'widget') return;
    if (event.key !== 'Tab') return;
    const focusable = Array.from(this.root.querySelectorAll<HTMLElement>('button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'))
      .filter((element) => !element.closest('[inert]'));
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = this.root.activeElement;
    if (event.shiftKey && (active === first || !active)) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && active === last) { event.preventDefault(); first.focus(); }
  };

  private handlePageHide = (): void => {
    this.finishPageTracking();
    this.flushEvents();
  };

  private handleVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') this.flushEvents();
  };

  private handleExclusivePlay = (event: CustomEvent<{ source?: LGSVideoFlowElement }>): void => {
    if (event.detail?.source === this) return;
    this.root.querySelector<HTMLVideoElement>('video.media')?.pause();
    this.root.querySelector<HTMLVideoElement>('[data-companion-video]')?.pause();
    this.root.querySelector<HTMLAudioElement>('audio[data-end-audio]')?.pause();
  };

  private signalExclusivePlayback = (): void => {
    window.dispatchEvent(new CustomEvent('lgs-video:exclusive-play', { detail: { source: this } }));
  };

  private suspendPeers(): void {
    this.restorePeers();
    document.querySelectorAll<HTMLElement>('lgs-video-flow').forEach((peer) => {
      if (peer === this) return;
      this.peerStates.set(peer, { inert: peer.inert, ariaHidden: peer.getAttribute('aria-hidden') });
      peer.inert = true;
      peer.setAttribute('aria-hidden', 'true');
    });
  }

  private restorePeers(): void {
    this.peerStates.forEach((state, peer) => {
      peer.inert = state.inert;
      if (state.ariaHidden === null) peer.removeAttribute('aria-hidden');
      else peer.setAttribute('aria-hidden', state.ariaHidden);
    });
    this.peerStates.clear();
  }

  private normalizeFramefieldReferences(value: unknown): FramefieldReference[] {
    if (!Array.isArray(value)) return [];
    return value.slice(0, 12).flatMap((item) => {
      if (!item || typeof item !== 'object') return [];
      const raw = item as Record<string, unknown>;
      const id = String(raw.id ?? '').replace(/[^0-9A-Za-z_-]/g, '').slice(0, 80);
      if (!id) return [];
      const reasons = Array.isArray(raw.reasons) ? raw.reasons.slice(0, 6).map((reason) => String(reason).slice(0, 180)) : undefined;
      const poster = safeUrl(raw.poster ? String(raw.poster) : '');
      const preview = safeUrl(raw.preview ? String(raw.preview) : '');
      return [{
        id,
        title: raw.title ? String(raw.title).slice(0, 180) : undefined,
        client: raw.client ? String(raw.client).slice(0, 180) : undefined,
        poster: poster || undefined,
        preview: preview || undefined,
        match: Number.isFinite(Number(raw.match)) ? Math.max(0, Math.min(100, Number(raw.match))) : undefined,
        reasons,
      }];
    });
  }

  private rememberFramefield(references: unknown, shortlist: unknown, replaceShortlist = false): void {
    if (!this.session) return;
    const previous = this.session.integrations?.framefield;
    const hasReferences = Array.isArray(references);
    const hasShortlist = Array.isArray(shortlist);
    const normalizedReferences = this.normalizeFramefieldReferences(references);
    const normalizedShortlist = this.normalizeFramefieldReferences(shortlist);
    this.session.integrations = {
      ...this.session.integrations,
      framefield: {
        ...previous,
        references: hasReferences ? normalizedReferences : previous?.references ?? [],
        shortlist: replaceShortlist || hasShortlist ? normalizedShortlist : previous?.shortlist ?? [],
        updatedAt: new Date().toISOString(),
      },
    };
    this.saveSession();
  }

  private handleFramefieldShortlist = (event: CustomEvent): void => {
    if (!this.graph || !this.session || !acceptsPortfolioMessage(event.detail, this.graph.slug) || !Array.isArray(event.detail.references)) return;
    const payload = event.detail as { references?: unknown[] };
    this.rememberFramefield(undefined, payload.references ?? [], true);
    this.publishLeadContext(false);
  };

  private handlePortfolioState = (event: CustomEvent): void => {
    if (!this.graph || !this.session || !event.detail || !acceptsPortfolioMessage(event.detail, this.graph.slug)) return;
    const state = readPortfolioGallery(event.detail);
    this.portfolioFilter.handleUpdate(event.detail);
    this.advisorFilter.handleUpdate(event.detail);
    const previous = this.session.integrations?.framefield;
    this.session.integrations = { ...this.session.integrations, framefield: {
      references: previous?.references ?? [], shortlist: previous?.shortlist ?? [],
      ...previous, galleries: { ...previous?.galleries, [state.gridId || 'default']: state },
      updatedAt: new Date().toISOString(),
    } };
    this.saveSession();
  };

  /** Framefield gallery handles for direct filter rules; only canonical cards count. */
  private portfolioGalleries(): PortfolioGalleryHandle[] {
    const bridge = (window as Window & { FramefieldLGSBridge?: { getPortfolioState?: (root?: Element) => unknown } }).FramefieldLGSBridge;
    return Array.from(document.querySelectorAll<HTMLElement>('[data-ffp-gallery]')).map((root) => ({
      gridId: root.dataset.gridId ?? '',
      state: () => {
        const raw = typeof bridge?.getPortfolioState === 'function' ? bridge.getPortfolioState(root) : null;
        return raw && typeof raw === 'object' ? readPortfolioGallery(raw as Record<string, unknown>).filters : null;
      },
      total: () => root.querySelectorAll('[data-ffp-card]:not([data-ffp-row-clone])').length,
    }));
  }

  /** Enter/leave node rules. `node-reveal` rules wait for the interaction. */
  private syncNodePortfolioFilter(reveal = false): void {
    const node = this.currentNode();
    if (!node || !this.session || this.previewConfig) return;
    const rule = normalizePortfolioFilterRule(node.content.portfolioFilter);
    if (!rule || node.content.hiddenModules?.includes('portfolio-filter')) {
      if (this.portfolioFilter.activeNodeId) this.portfolioFilter.clear();
      this.portfolioFilterKey = node.id;
      return;
    }
    // Nur nach einer bewussten Nutzung: geöffneter Player oder bereits beantwortete Schritte.
    const eligible = this.isOpen || this.session.history.length > 0;
    const ready = eligible && (rule.trigger !== 'node-reveal' || reveal || this.interactionRevealed);
    const key = `${node.id}:${ready ? 'ready' : 'waiting'}`;
    if (key === this.portfolioFilterKey) return;
    this.portfolioFilterKey = key;
    if (!ready) { if (this.portfolioFilter.activeNodeId !== node.id) this.portfolioFilter.clear(); return; }
    this.portfolioFilter.enter(node.id, rule);
    this.syncPortfolioPeek();
  }

  private handleFramefieldProfileApplied = (): void => {
    if (this.advisorFilter.activeNodeId) window.setTimeout(() => this.advisorFilter.assert(), 0);
    if (!this.portfolioFilter.activeNodeId) return;
    window.setTimeout(() => this.portfolioFilter.assert(), 0);
  };

  /**
   * Referenzfinder (0.15.51): Ziel, Branche und Machart gehen exakt an die Galerie. Die erste Stufe
   * mit genug Treffern im Katalog startet; bestätigt Framefield zu wenige Karten, folgt die nächste
   * Stufe (Machart, Branche, zuletzt Ziel weglassen). Eine leere Galerie bleibt so nie stehen.
   */
  private syncAdvisorPortfolioFilter(): void {
    if (!this.session || !this.graph || this.previewConfig || this.portfolioFilter.activeNodeId || !this.session.history.length) {
      if (this.advisorFilter.activeNodeId) this.advisorFilter.clear();
      this.advisorFilterKey = '';
      return;
    }
    const selection = advisorSelection(this.session.profile);
    const steps = advisorFilterSteps(selection);
    if (steps.length < 2 || !document.querySelector('[data-ffp-gallery]')) {
      if (this.advisorFilter.activeNodeId) this.advisorFilter.clear();
      this.advisorFilterKey = '';
      return;
    }
    const key = JSON.stringify(selection);
    if (key === this.advisorFilterKey && this.advisorFilter.activeNodeId) { this.advisorFilter.assert(); return; }
    this.advisorFilterKey = key;
    // Eine einzige exakt passende Referenz ist besser als viele allgemeine; leer bleibt die Galerie nie.
    const minimum = 1;
    const projects = (this.graph.catalog ?? []).filter((item) => item.kind === 'portfolio-project' && item.published);
    const required = Math.min(minimum, projects.length);
    const start = projects.length ? Math.max(0, steps.findIndex((step) => projects.filter((item) => catalogMatchesFacets(item, step.facets)).length >= required)) : 0;
    this.advisorFilter.enterSteps(`advisor-${this.advisorFilterCounter += 1}`, steps.slice(start).map((step) => ({ categories: [], facets: step.facets, minimumVisible: minimum })));
  }
  private advisorFilterCounter = 0;

  /** Only real visitor input counts as a manual filter choice; programmatic clicks are not trusted. */
  private handleManualPortfolioFilter = (event: Event): void => {
    if (!event.isTrusted || !(event.target instanceof Element)) return;
    const control = event.target.closest(PORTFOLIO_MANUAL_CONTROLS);
    const root = control?.closest<HTMLElement>('[data-ffp-gallery]');
    if (root) { this.portfolioFilter.markManual(root.dataset.gridId ?? ''); this.advisorFilter.markManual(root.dataset.gridId ?? ''); }
  };

  private handleFramefieldReady = (): void => {
    if (!this.session) return;
    this.syncFramefieldBridge();
    if (this.portfolioFilter.activeNodeId) this.portfolioFilter.assert();
    this.emit('intent-updated', {
      bridgeVersion: '1.0',
      flow: this.graph?.slug ?? '',
      profile: this.session.profile,
      primaryNeed: this.session.profile.needs[0] ?? null,
    });
  };

  private handleFramefield = (event: CustomEvent): void => {
    if (!this.graph || !this.session || !event.detail) return;
    this.dataset.portfolioResponseFlow = String(event.detail.flow ?? '');
    if (!acceptsPortfolioMessage(event.detail, this.graph.slug)) return;
    const payload = event.detail as { answers?: { question?: string; answer?: string }[]; references?: { id?: string; match?: number; reasons?: string[] }[]; shortlist?: unknown[] };
    this.rememberFramefield(payload.references ?? [], payload.shortlist ?? []);
    this.dataset.portfolioMatchCount = String(this.session.integrations?.framefield?.references.length ?? 0);
    this.syncPortfolioPeek();
    // Matching is an output of our answers, never new evidence to score again.
    if (event.detail.source === 'lgs-interactive-video') {
      this.publishLeadContext(false);
      return;
    }
    const evidence: NeedEvidence[] = [];
    readPortfolioAnswers(event.detail).forEach((answer, answerIndex) => {
      const haystack = `${answer.question ?? ''} ${answer.answer ?? ''}`.toLocaleLowerCase('de');
      this.graph!.taxonomy.forEach((term, termIndex) => {
        const words = [term.label, term.key, ...(term.aliases ?? [])].map((word) => word.toLocaleLowerCase('de'));
        if (!words.some((word) => haystack.includes(word))) return;
        evidence.push(...evidenceFromMutations('portfolio', `ffp-answer-${answerIndex}-${termIndex}`, [{ dimension: term.dimension, key: term.key, weight: 25, label: `${term.label} in Portfolioberatung gewählt` }]));
      });
    });
    (payload.references ?? []).forEach((reference, index) => {
      (reference.reasons ?? []).forEach((reason, reasonIndex) => {
        const term = this.graph!.taxonomy.find((candidate) => reason.toLocaleLowerCase('de').includes(candidate.label.toLocaleLowerCase('de')));
        if (term) evidence.push(...evidenceFromMutations('portfolio', `ffp-ref-${reference.id ?? index}-${reasonIndex}`, [{ dimension: term.dimension, key: term.key, weight: Math.min(25, Math.max(8, (reference.match ?? 40) / 4)), label: `Passende Referenz geöffnet: ${term.label}` }]));
      });
    });
    if (!evidence.length) {
      this.publishLeadContext(false);
      return;
    }
    const deduped = new Map(this.session.profile.evidence.map((item) => [item.id, item]));
    evidence.forEach((item) => deduped.set(item.id, item));
    this.updateProfile(Array.from(deduped.values()));
    this.publishLeadContext(false);
    this.render();
  };

  private choose(optionId: string): void {
    this.pendingChapterId = null;
    if (!this.graph || !this.session) return;
    const node = this.currentNode();
    if (!node) return;
    const option = (node.content.options ?? []).find((item) => item.id === optionId);
    const candidates = this.graph.edges.filter((edge) => edge.sourceNodeId === node.id && edge.sourceHandle === optionId);
    const choiceKey = `${node.id}:${optionId}:${this.session.history.length}:${Date.now()}`;
    const currentEvidence = this.session.profile.evidence;
    const { city, regionCode } = this.session.profile;
    const resolution = resolveChoiceTransition(this.session.id, currentEvidence, candidates, option?.mutations ?? [], choiceKey, { city, regionCode });
    const edge = resolution.edge;
    if (!edge) { this.announce('Für diese Auswahl fehlt noch eine Verbindung.'); return; }
    const nextVisits = (this.session.visits[edge.targetNodeId] ?? 0) + 1;
    if (nextVisits > this.graph.settings.maxNodeVisits) {
      this.announce('Dieser Weg wurde zum Schutz vor einer Schleife beendet.');
      return;
    }
    const mutationEvidence = resolution.evidence;
    this.updateProfile(resolution.profile.evidence);
    const entry: HistoryEntry = { nodeId: node.id, edgeId: edge.id, evidenceIds: mutationEvidence.map((item) => item.id), occurredAt: new Date().toISOString() };
    this.session.history.push(entry);
    this.session.currentNodeId = edge.targetNodeId;
    this.session.visits[edge.targetNodeId] = nextVisits;
    this.track('choice', { nodeId: node.id, edgeId: edge.id, value: option?.label ?? optionId });
    this.track('node-enter', { nodeId: edge.targetNodeId });
    this.saveSession();
    this.publishLeadContext(false);
    this.releaseMedia();
    this.render();
    queueMicrotask(() => this.root.querySelector<HTMLElement>('h2')?.focus({ preventScroll: true }));
  }

  private goBack(): void {
    this.pendingChapterId = null;
    if (!this.session || !this.graph || !this.graph.settings.allowBack) return;
    const previousNode = this.currentNode();
    const entry = this.session.history.pop();
    if (!entry) return;
    if (previousNode) {
      const remainingVisits = Math.max(0, (this.session.visits[previousNode.id] ?? 1) - 1);
      if (remainingVisits) this.session.visits[previousNode.id] = remainingVisits;
      else delete this.session.visits[previousNode.id];
    }
    const remove = new Set(entry.evidenceIds);
    const evidence = this.session.profile.evidence.filter((item) => !remove.has(item.id));
    this.session.currentNodeId = entry.nodeId;
    this.updateProfile(evidence);
    this.track('back', { nodeId: entry.nodeId });
    if (previousNode?.type === 'recommendation') this.emit('recommendations-updated', { recommendations: [] });
    this.saveSession();
    this.publishLeadContext(false);
    this.releaseMedia();
    this.render();
  }

  private updateProfile(evidence: NeedEvidence[]): void {
    if (!this.session) return;
    const { city, regionCode } = this.session.profile;
    this.session.profile = calculateNeedProfile(this.session.id, evidence, { city, regionCode });
    this.saveSession();
    const adapted = applySitePersonalization(this.session.profile);
    this.scheduleFramefieldPersonalization();
    this.syncFramefieldBridge();
    this.emit('intent-updated', { bridgeVersion: '1.0', flow: this.graph?.slug ?? '', profile: this.session.profile, primaryNeed: this.session.profile.needs[0] ?? null });
    if (adapted.length) this.emit('website-adapted', { profile: this.session.profile, targets: adapted });
    this.track('intent-updated', { value: this.session.profile.needs[0]?.key ?? this.session.profile.goals[0]?.key ?? '' });
  }

  private syncFramefieldBridge(): void {
    if (!this.session) return;
    const detail = {
      bridgeVersion: '1.0',
      flow: this.graph?.slug ?? '',
      release: this.graph?.releaseVersion ?? 0,
      profile: this.session.profile,
      primaryNeed: this.session.profile.needs[0] ?? null,
    };
    document.dispatchEvent(new CustomEvent('lgs-video:intent-updated', { detail }));
    const bridge = (window as Window & {
      FramefieldLGSBridge?: {
        version?: string;
        applyNeedProfile?: (profile: NeedProfile, meta?: Record<string, unknown>) => void;
      };
    }).FramefieldLGSBridge;
    this.dataset.portfolioBridge = typeof bridge?.applyNeedProfile === 'function' ? 'connected' : 'waiting';
    if (typeof bridge?.applyNeedProfile === 'function') bridge.applyNeedProfile(this.session.profile, detail);
  }

  private scheduleFramefieldPersonalization(): void {
    if (!this.session) return;
    const run = (): void => {
      this.syncFramefieldBridge();
      this.syncAdvisorPortfolioFilter();
      this.applyFramefieldPersonalization();
      // Direct node rules win over profile filters: re-assert after Framefield applied the profile.
      if (this.portfolioFilter.activeNodeId) this.portfolioFilter.assert();
    };
    queueMicrotask(run);
    window.setTimeout(run, 300);
    window.setTimeout(run, 1200);
  }

  private applyFramefieldPersonalization(): void {
    if (!this.session) return;
    const roots = Array.from(document.querySelectorAll<HTMLElement>('[data-ffp-gallery]'));
    if (!roots.length) return;
    const profile = this.session.profile;
    const goal = framefieldGoal(profile);
    const filmType = framefieldFilmType(profile);
    const industryText = profileSearchText(profile, ['industries']);
    const styleText = profileSearchText(profile, ['styles']);
    const shortlist = this.session.integrations?.framefield?.shortlist ?? [];
    const ruleNodeId = this.portfolioFilter.activeNodeId;
    const signature = JSON.stringify({ goal, filmType, industryText, styleText, shortlist: shortlist.map((item) => item.id), ...(ruleNodeId ? { ruleNodeId } : {}) });
    if (signature === this.framefieldApplySignature || signature === this.framefieldRelaxedSignature) return;
    let changed = false;
    const applied: Record<string, string> = {};

    roots.forEach((root) => {
      if (this.portfolioFilter.controls(root.dataset.gridId ?? '') || this.advisorFilter.controls(root.dataset.gridId ?? '')) return;
      const applyFacet = (key: string, preferred: string): void => {
        if (!preferred) return;
        const select = root.querySelector<HTMLSelectElement>(`[data-ffp-facet="${key}"]`);
        if (!select) return;
        const preferredText = normalizedMatchValue(preferred);
        const option = Array.from(select.options).find((item) => item.value && (
          normalizedMatchValue(item.value) === preferredText
          || normalizedMatchValue(item.textContent ?? '') === preferredText
          || preferredText.includes(normalizedMatchValue(item.value))
          || normalizedMatchValue(item.textContent ?? '').split(' ').some((term) => term.length > 4 && preferredText.includes(term))
        ));
        if (!option || select.value === option.value) return;
        if (select.value && !select.dataset.lgsivApplied) return;
        const previous = select.dataset.lgsivApplied;
        if (previous && previous !== option.value) {
          root.querySelector<HTMLElement>(`[data-ffp-remove-facet="${key}"][data-ffp-remove-value="${CSS.escape(previous)}"]`)?.click();
        }
        select.value = option.value;
        select.dataset.lgsivApplied = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        applied[key] = option.value;
        changed = true;
      };

      applyFacet('ffp_goal', goal);
      applyFacet('ffp_industry', industryText);
      applyFacet('ffp_style', styleText);

      if (filmType) {
        const buttons = Array.from(root.querySelectorAll<HTMLElement>('[data-ffp-filter]'));
        const target = buttons.find((button) => normalizedMatchValue(button.dataset.ffpFilter ?? '') === normalizedMatchValue(filmType));
        const manuallyActive = buttons.some((button) => button.dataset.ffpFilter !== '*' && button.getAttribute('aria-pressed') === 'true' && button.dataset.lgsivApplied !== 'true');
        if (target && target.getAttribute('aria-pressed') !== 'true' && !manuallyActive) {
          buttons.filter((button) => button !== target && button.dataset.lgsivApplied === 'true' && button.getAttribute('aria-pressed') === 'true').forEach((button) => button.click());
          target.dataset.lgsivApplied = 'true';
          target.click();
          applied.ffp_film_type = target.dataset.ffpFilter ?? filmType;
          changed = true;
        }
      }

      shortlist.forEach((reference) => {
        const card = root.querySelector<HTMLElement>(`[data-ffp-card][data-project-id="${CSS.escape(reference.id)}"]`);
        const toggle = card?.querySelector<HTMLElement>('[data-ffp-shortlist-toggle]');
        if (toggle && toggle.getAttribute('aria-pressed') !== 'true') {
          toggle.click();
          changed = true;
        }
      });
    });

    this.framefieldApplySignature = signature;
    this.syncPortfolioPeek();
    window.setTimeout(() => this.relaxEmptyFramefieldFilters(roots, signature), 120);
    window.setTimeout(() => this.relaxEmptyFramefieldFilters(roots, signature), 520);
    if (!changed) return;
    this.emit('portfolio-filters-applied', {
      filters: applied,
      references: shortlist.map((item) => item.id),
      profile,
    });
    this.track('portfolio-filters-applied', { value: Object.values(applied).join(',') });
  }

  private relaxEmptyFramefieldFilters(roots: HTMLElement[], signature: string): void {
    if (signature !== this.framefieldApplySignature || signature === this.framefieldRelaxedSignature) return;
    let relaxed = false;
    const minimumVisibleReferences = 4;
    roots.forEach((root) => {
      if (this.portfolioFilter.controls(root.dataset.gridId ?? '') || this.advisorFilter.controls(root.dataset.gridId ?? '')) return;
      const cards = Array.from(root.querySelectorAll<HTMLElement>('[data-ffp-card]:not([data-ffp-row-clone])'));
      if (!cards.length) return;
      const visibleCards = cards.filter((card) => {
        const style = window.getComputedStyle(card);
        return !card.hidden && card.getAttribute('aria-hidden') !== 'true' && style.display !== 'none' && style.visibility !== 'hidden';
      });
      const requiredVisibleCards = Math.min(minimumVisibleReferences, cards.length);
      if (visibleCards.length >= requiredVisibleCards) return;

      const appliedSelects = Array.from(root.querySelectorAll<HTMLSelectElement>('[data-ffp-facet][data-lgsiv-applied]'));
      const appliedButtons = Array.from(root.querySelectorAll<HTMLElement>('[data-ffp-filter][data-lgsiv-applied="true"]'));
      if (!appliedSelects.length && !appliedButtons.length) return;

      appliedSelects.forEach((select) => {
        const appliedValue = select.dataset.lgsivApplied;
        delete select.dataset.lgsivApplied;
        if (!appliedValue || select.value !== appliedValue) return;
        const facetKey = select.dataset.ffpFacet ?? '';
        const appliedChip = facetKey
          ? root.querySelector<HTMLElement>(`[data-ffp-remove-facet="${CSS.escape(facetKey)}"][data-ffp-remove-value="${CSS.escape(appliedValue)}"]`)
          : null;
        if (appliedChip) appliedChip.click();
        else {
          select.value = '';
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
      const allButton = root.querySelector<HTMLElement>('[data-ffp-filter="*"]');
      appliedButtons.forEach((button) => delete button.dataset.lgsivApplied);
      if (allButton && allButton.getAttribute('aria-pressed') !== 'true') allButton.click();
      else if (!allButton) appliedButtons.filter((button) => button.getAttribute('aria-pressed') === 'true').forEach((button) => button.click());
      relaxed = true;
    });
    if (!relaxed) return;
    this.framefieldRelaxedSignature = signature;
    this.syncPortfolioPeek();
    this.emit('portfolio-filters-relaxed', {
      reason: 'minimum-results',
      minimumVisibleReferences,
      profile: this.session?.profile ?? null,
    });
    this.track('portfolio-filters-relaxed', { value: 'minimum-results' });
  }

  private captureCurrentPageContext(): void {
    if (!this.graph || !this.session || this.graph.settings.capturePageContext === false) return;
    const mutations: NeedMutation[] = [];
    const matchedItems = (this.graph.catalog ?? []).filter((item) => item.published && matchesCurrentUrl(item.url));
    matchedItems.forEach((item) => {
      (Object.entries(item.tags) as Array<[NeedMutation['dimension'], Record<string, number> | undefined]>).forEach(([dimension, tags]) => {
        Object.entries(tags ?? {}).forEach(([key, weight]) => mutations.push({
          dimension,
          key,
          weight: Math.max(4, Math.min(14, Number(weight) * 0.4)),
          label: `Besuchte Seite: ${item.title}`,
        }));
      });
    });
    if (!mutations.length) return;
    const sourceId = `page:${normalizedPath(window.location.pathname)}${window.location.search}`;
    const pageEvidence = evidenceFromMutations('page', sourceId, mutations);
    const merged = new Map(this.session.profile.evidence.map((item) => [item.id, item]));
    pageEvidence.forEach((item) => merged.set(item.id, item));
    const { city, regionCode } = this.session.profile;
    this.session.profile = calculateNeedProfile(this.session.id, Array.from(merged.values()), { city, regionCode });
    this.saveSession();
    this.emit('page-context', { profile: this.session.profile, pages: matchedItems.map((item) => item.id) });
  }

  private beginPageTracking(): void {
    if (!this.graph || !this.session) return;
    const page = `${normalizedPath(window.location.pathname)}${window.location.search}`;
    const key = `${this.graph.slug}:${page}`;
    if (trackedPageContexts.has(key)) return;
    trackedPageContexts.add(key);
    this.ownsPageTracking = true;
    this.pageEnteredAt = Date.now();
    this.track('page-view', { value: page });
  }

  private finishPageTracking(): void {
    if (!this.ownsPageTracking || !this.pageEnteredAt) return;
    this.ownsPageTracking = false;
    const seconds = Math.max(1, Math.round((Date.now() - this.pageEnteredAt) / 1000));
    this.track('page-dwell', { value: String(seconds) });
  }

  private syncSoundButton(video: HTMLVideoElement, audio = this.root.querySelector<HTMLAudioElement>('audio[data-end-audio]')): void {
    const button = this.root.querySelector<HTMLButtonElement>('[data-action="toggle-sound"]');
    if (!button) return;
    const muted = video.ended && audio ? audio.muted : video.muted;
    button.dataset.muted = String(muted);
    button.setAttribute('aria-pressed', String(!muted));
    button.setAttribute('aria-label', muted ? 'Ton einschalten' : 'Ton ausschalten');
  }

  private revealInteraction(source: 'time' | 'manual' | 'ended'): void {
    if (this.interactionRevealed) return;
    this.interactionRevealed = true;
    this.interactionDismissed = false;
    const stage = this.root.querySelector<HTMLElement>('.stage');
    const panel = this.root.querySelector<HTMLElement>('[data-interaction-panel]');
    stage?.classList.add('is-interaction-revealed');
    panel?.removeAttribute('inert');
    panel?.setAttribute('aria-hidden', 'false');
    if (panel) panel.scrollTop = 0;
    this.root.querySelector<HTMLElement>('[data-action="reveal-interaction"]')?.setAttribute('aria-label', 'Interaktion jetzt öffnen');
    const video = this.root.querySelector<HTMLVideoElement>('video.media');
    const node = this.currentNode();
    if (node?.content.pauseAtReveal && video && !video.paused) video.pause();
    this.track('interaction-revealed', { nodeId: node?.id ?? '', value: source });
    this.announce('Die Auswahlmöglichkeiten sind jetzt sichtbar.');
    window.setTimeout(() => this.playChoiceSwipeHint(), 1550);
    this.syncNodePortfolioFilter(true);
    if (source === 'manual') window.setTimeout(() => panel?.querySelector<HTMLElement>('button,a')?.focus({ preventScroll: true }), 420);
  }

  private collapseInteraction(): void {
    if (!this.interactionRevealed) return;
    this.interactionRevealed = false;
    this.interactionDismissed = true;
    const stage = this.root.querySelector<HTMLElement>('.stage');
    const panel = this.root.querySelector<HTMLElement>('[data-interaction-panel]');
    const teaser = this.root.querySelector<HTMLElement>('[data-action="reveal-interaction"]');
    const teaserLabel = this.root.querySelector<HTMLElement>('[data-reveal-countdown]');
    stage?.classList.remove('is-interaction-revealed');
    panel?.setAttribute('inert', '');
    panel?.setAttribute('aria-hidden', 'true');
    teaser?.setAttribute('aria-label', 'Antworten wieder öffnen');
    if (teaserLabel) teaserLabel.textContent = 'Antworten wieder öffnen';
    this.track('interaction-collapsed', { nodeId: this.currentNode()?.id ?? '' });
    this.announce(playerCopy(this.graph, 'collapsed'));
    window.setTimeout(() => this.root.querySelector<HTMLElement>('[data-action="reveal-interaction"]')?.focus({ preventScroll: true }), 420);
  }

  private releaseMedia(): void {
    const video = this.root.querySelector<HTMLVideoElement>('video.media');
    this.mediaLoading?.dispose();
    this.mediaLoading = null;
    this.stopEndAudio(true);
    if (!video) return;
    video.dataset.releasing = 'true';
    video.pause();
    video.removeAttribute('src');
    video.querySelectorAll('source').forEach((source) => source.remove());
    video.load();
  }

  private portfolioVideos(): HTMLVideoElement[] {
    const selector = [
      '[data-ffp-player] video',
      '[data-ffp-dialog] video',
      '[data-ffp-modal] video',
      '.ffp-player video',
      '.ffp-dialog video',
      '.ffp-modal video',
      '.ffp-lightbox video',
    ].join(',');
    return Array.from(document.querySelectorAll<HTMLVideoElement>(selector))
      .filter((video) => !video.hasAttribute('data-preview-src'));
  }

  private portfolioFrames(): HTMLIFrameElement[] {
    const selector = [
      '[data-ffp-player] iframe',
      '[data-ffp-dialog] iframe',
      '[data-ffp-modal] iframe',
      '.ffp-player iframe',
      '.ffp-dialog iframe',
      '.ffp-modal iframe',
      '.ffp-lightbox iframe',
    ].join(',');
    return Array.from(document.querySelectorAll<HTMLIFrameElement>(selector));
  }

  private sendPortfolioFrameCommand(frame: HTMLIFrameElement, command: 'hold' | 'release' | 'pause'): void {
    const target = frame.contentWindow;
    if (!target) return;
    const pause = command === 'pause';
    const hold = command === 'hold';
    target.postMessage(JSON.stringify({ event: 'command', func: pause ? 'pauseVideo' : hold ? 'mute' : 'unMute', args: [] }), '*');
    target.postMessage({ method: pause ? 'pause' : hold ? 'setVolume' : 'setVolume', value: hold ? 0 : 1 }, '*');
    if (!pause && !hold) {
      target.postMessage(JSON.stringify({ event: 'command', func: 'playVideo', args: [] }), '*');
      target.postMessage({ method: 'play' }, '*');
    }
  }

  private holdPortfolioMediaForCompanion(): void {
    if (this.isOpen || !this.root.querySelector('[data-companion-video]')) return;
    this.portfolioVideos().forEach((video) => {
      video.dataset.lgsivAudioHeld = 'true';
      video.defaultMuted = true;
      video.muted = true;
    });
    this.portfolioFrames().forEach((frame) => {
      frame.dataset.lgsivAudioHeld = 'true';
      this.sendPortfolioFrameCommand(frame, 'hold');
    });
    document.dispatchEvent(new CustomEvent('lgs-video:portfolio-media-command', { detail: { command: 'hold' } }));
  }

  private releasePortfolioMediaAfterCompanion(): void {
    if (this.isOpen) return;
    this.portfolioVideos().forEach((video) => {
      if (video.dataset.lgsivAudioHeld !== 'true') return;
      delete video.dataset.lgsivAudioHeld;
      video.defaultMuted = false;
      video.muted = false;
      void video.play().catch(() => undefined);
    });
    this.portfolioFrames().forEach((frame) => {
      if (frame.dataset.lgsivAudioHeld !== 'true') return;
      delete frame.dataset.lgsivAudioHeld;
      this.sendPortfolioFrameCommand(frame, 'release');
    });
    document.dispatchEvent(new CustomEvent('lgs-video:portfolio-media-command', { detail: { command: 'release' } }));
  }

  private pausePortfolioMedia(): void {
    this.portfolioVideos().forEach((video) => {
      delete video.dataset.lgsivAudioHeld;
      video.pause();
    });
    this.portfolioFrames().forEach((frame) => {
      delete frame.dataset.lgsivAudioHeld;
      this.sendPortfolioFrameCommand(frame, 'pause');
    });
    document.dispatchEvent(new CustomEvent('lgs-video:portfolio-media-command', { detail: { command: 'pause' } }));
  }

  private stopEndAudio(release = false): void {
    const audio = this.root.querySelector<HTMLAudioElement>('audio[data-end-audio]');
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    if (!release) return;
    audio.removeAttribute('src');
    audio.querySelectorAll('source').forEach((source) => source.remove());
    audio.load();
  }

  private journeyTimelineState(node: FlowNode): { segments: number; activeIndex: number } {
    if (!this.graph || !this.session) return { segments: 1, activeIndex: 0 };
    const activeIndex = this.session.history.length;
    if (node.type === 'result' || node.type === 'form') return { segments: activeIndex + 1, activeIndex };
    const queue: Array<{ id: string; distance: number }> = [{ id: node.id, distance: 0 }];
    const visited = new Set<string>();
    let remaining: number | null = null;
    while (queue.length) {
      const current = queue.shift()!;
      if (visited.has(current.id)) continue;
      visited.add(current.id);
      const candidate = this.graph.nodes.find((item) => item.id === current.id);
      if (candidate?.type === 'result' || candidate?.type === 'form') { remaining = current.distance; break; }
      this.graph.edges
        .filter((edge) => edge.sourceNodeId === current.id)
        .forEach((edge) => queue.push({ id: edge.targetNodeId, distance: current.distance + 1 }));
    }
    const fallbackRemaining = Math.max(0, this.graph.nodes.length - activeIndex - 1);
    return { segments: Math.max(1, activeIndex + 1 + (remaining ?? fallbackRemaining)), activeIndex };
  }

  private async requestLocation(): Promise<void> {
    if (!navigator.geolocation || !this.session) { this.announce('Ihr Browser unterstützt keine Standortfreigabe.'); return; }
    const consentRequest = new CustomEvent('lgs-video:location-consent-requested', {
      bubbles: true,
      composed: true,
      cancelable: true,
      detail: { flow: this.graph?.slug ?? '', purpose: 'personal-greeting' },
    });
    if (!this.dispatchEvent(consentRequest)) {
      this.announce(playerCopy(this.graph, 'locationBlocked'));
      return;
    }
    this.announce('Der Browser fragt jetzt nach Ihrer Standortfreigabe.');
    navigator.geolocation.getCurrentPosition(async (position) => {
      try {
        const response = await fetch(`${this.restUrl}location`, {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ latitude: position.coords.latitude, longitude: position.coords.longitude }),
        });
        if (!response.ok) throw new Error('Ort konnte nicht aufgelöst werden.');
        const location = await response.json() as { city: string; regionCode?: string };
        this.session!.profile.city = location.city;
        this.session!.profile.regionCode = location.regionCode;
        this.markLocationPromptHandled();
        this.saveSession();
        this.publishLeadContext(false);
        this.track('location-resolved', { value: location.regionCode ?? '' });
        this.render();
        this.emit('location-consent', { status: 'granted' });
        this.announce(`Persönliche Begrüßung für ${location.city} ist bereit.`);
      } catch { this.markLocationPromptHandled(); this.render(); this.announce('Die Beratung funktioniert auch ohne Standortangabe.'); }
    }, () => { this.markLocationPromptHandled(); this.render(); this.emit('location-consent', { status: 'denied' }); this.announce('Kein Problem – die Beratung läuft ohne Standort weiter.'); }, { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 });
  }

  private shouldPromptLocation(node: FlowNode): boolean {
    return Boolean(this.graph?.settings.locationMode === 'optional-browser' && node.content.requestLocation && this.isBeforeFirstPlayback(node) && !this.session?.profile.city && !this.locationPromptDismissed && !this.wasLocationPromptHandled());
  }

  private isBeforeFirstPlayback(node: FlowNode): boolean {
    return Boolean(!this.playerStarted && this.session?.history.length === 0 && node.id === this.graph?.startNodeId);
  }

  private journeyAnswers(): Array<{ nodeId: string; question: string; answer: string }> {
    if (!this.graph || !this.session) return [];
    return this.session.history.slice(0, 24).flatMap((entry) => {
      const node = this.graph!.nodes.find((item) => item.id === entry.nodeId);
      const edge = this.graph!.edges.find((item) => item.id === entry.edgeId);
      const option = node?.content.options?.find((item) => item.id === edge?.sourceHandle);
      if (!node || !option) return [];
      return [{
        nodeId: node.id,
        question: node.content.heading?.trim() || node.titleInternal,
        answer: option.label,
      }];
    });
  }

  private personalizedContentUrl(rawUrl?: string): string {
    const safe = safeUrl(rawUrl);
    if (!safe) return '';
    try {
      const url = new URL(safe, window.location.href);
      if (url.origin !== window.location.origin || !normalizedPath(url.pathname).includes('/portfolio')) return url.href;
      const shortlist = this.session?.integrations?.framefield?.shortlist ?? [];
      if (shortlist.length) url.hash = new URLSearchParams({ 'ffp-ref': shortlist.map((item) => item.id).join(',') }).toString();
      return url.href;
    } catch { return safe; }
  }

  private recommendations(node: FlowNode): Recommendation[] {
    if (!this.graph || !this.session) return [];
    const limit = node.content.recommendationLimit ?? 3;
    const ranked = rankRecommendations(this.session.profile, this.graph.catalog ?? [], {
      kinds: node.content.recommendationKinds,
      limit: Math.max(limit, 24),
      // Portfolio projects often carry many descriptive taxonomy terms. A single
      // strong matching signal must still be visible instead of being diluted by
      // unrelated metadata in a richly tagged project.
      minConfidence: 0.1,
    });
    const firstReference = node.content.recommendationKinds?.includes('portfolio-project')
      ? ranked.find((item) => item.content.kind === 'portfolio-project')
      : undefined;
    return firstReference
      ? [firstReference, ...ranked.filter((item) => item !== firstReference)].slice(0, limit)
      : ranked.slice(0, limit);
  }

  private renderRecommendations(node: FlowNode): string {
    if (node.content.recommendationKinds?.includes('portfolio-project') || document.querySelector('[data-ffp-gallery]')) {
      return `<div class="portfolio-peek-slot portfolio-peek-slot--recommendation" data-portfolio-peek><div class="body">${escapeHtml(interpolate(node.content.fallbackBody, this.session!.profile))}</div></div>`;
    }
    const items = this.recommendations(node);
    if (!items.length) return `<div class="body">${escapeHtml(interpolate(node.content.fallbackBody, this.session!.profile))}</div>`;
    items.forEach((item) => {
      const key = `${this.session!.id}:${node.id}:${item.content.id}`;
      if (!this.impressionKeys.has(key)) {
        this.impressionKeys.add(key);
        this.track('recommendation-impression', { value: item.content.id });
      }
    });
    this.emit('recommendations-updated', { recommendations: items });
    return `<div class="recommendations">${items.map((item) => {
      const reason = item.reasons.slice(0, 2).join(' · ');
      return `<a class="recommendation" data-recommendation="${escapeHtml(item.content.id)}" href="${escapeHtml(this.personalizedContentUrl(item.content.url))}">
        ${item.content.image ? `<span class="recommendation__media"><img src="${escapeHtml(item.content.image)}" alt="" loading="lazy"></span>` : ''}
        <small>${item.content.kind === 'portfolio-project' ? 'Referenz' : item.content.kind === 'website-page' ? 'Leistung' : 'Empfehlung'} · ${Math.round(item.confidence * 100)} %</small>
        <strong>${escapeHtml(item.content.title)}</strong>
        ${item.content.description ? `<span>${escapeHtml(item.content.description)}</span>` : ''}
        ${reason ? `<em>Passt wegen: ${escapeHtml(reason)}</em>` : ''}
      </a>`;
    }).join('')}</div>`;
  }

  private renderQuickLinks(node: FlowNode): string {
    if (!this.graph || !this.session || this.session.history.length === 0 || ['recommendation', 'result', 'form'].includes(node.type)) return '';
    const enabled = node.content.showPortfolioSuggestions ?? this.graph.settings.showRecommendationsDuringFlow !== false;
    if (!enabled) return '';
    const limit = Math.max(1, Math.min(2, Number(node.content.portfolioSuggestionLimit ?? 2)));
    const label = node.content.portfolioSuggestionLabel?.trim() || 'Schon passend';
    if (this.graph.catalog?.some(item => item.kind === 'portfolio-project') || document.querySelector('[data-ffp-gallery]')) return `<div class="portfolio-peek-slot" data-portfolio-peek data-label="${escapeHtml(label)}"></div>`;
    const seenTitles = new Set<string>();
    const items = rankRecommendations(this.session.profile, this.graph.catalog ?? [], { kinds: ['website-page'], limit: 8 })
      .filter((item) => !matchesCurrentUrl(item.content.url))
      .filter((item) => {
        const titleKey = normalizedMatchValue(item.content.title);
        if (!titleKey || seenTitles.has(titleKey)) return false;
        seenTitles.add(titleKey);
        return true;
      })
      .slice(0, limit);
    if (!items.length) return '';
    return `<nav class="quick-links" aria-label="${escapeHtml(label)}"><small>${escapeHtml(label)}</small>${items.map((item) => `<a class="quick-link" data-recommendation="${escapeHtml(item.content.id)}" href="${escapeHtml(this.personalizedContentUrl(item.content.url))}">${escapeHtml(item.content.title)}</a>`).join('')}</nav>`;
  }

  private syncPortfolioPeek(): void {
    const slot = this.root.querySelector<HTMLElement>('[data-portfolio-peek]');
    if (!slot) return;
    const cards = Array.from(document.querySelectorAll<HTMLElement>('[data-ffp-card]:not([data-ffp-row-clone])'));
    const cardById = new Map(cards.map((item) => [String(item.dataset.projectId ?? ''), item]));
    const isVisible = (item: HTMLElement | undefined): item is HTMLElement => Boolean(item && !item.hidden && item.getAttribute('aria-hidden') !== 'true');
    const references = [...(this.session?.integrations?.framefield?.references ?? [])]
      .sort((a, b) => Number(b.match ?? 0) - Number(a.match ?? 0))
    const referenceById = new Map(references.map((item) => [item.id, item]));
    const rankedReferenceIds = references.map((item) => item.id);
    const rankedCatalogIds = this.graph && this.session
      ? rankRecommendations(this.session.profile, this.graph.catalog ?? [], { kinds: ['portfolio-project'], limit: 12 })
        .map((item) => item.content.targetId ?? '')
        .filter(Boolean)
      : [];
    const rankedIds = [...new Set([...rankedReferenceIds, ...rankedCatalogIds])];
    // Exact references are recommendations, not active filter results. Keep them
    // available even when a user-selected portfolio facet currently hides the
    // underlying card. Catalog fallbacks still respect the visible filter set.
    const referenceCards = rankedReferenceIds.map((id) => cardById.get(id)).filter((item): item is HTMLElement => Boolean(item));
    const catalogCards = rankedCatalogIds.map((id) => cardById.get(id)).filter(isVisible);
    const suggestionLimit = Math.max(1, Math.min(2, Number(this.currentNode()?.content.portfolioSuggestionLimit ?? 2)));
    // Older bridge versions cannot resolve renamed taxonomy terms. Use only
    // explicit goal-equivalent projects as a fallback, without inventing scores.
    const goal = this.session ? framefieldGoal(this.session.profile) : '';
    const compatibleCards = referenceCards.length ? [] : cards.filter(card =>
      portfolioGoalMatches(card.getAttribute('data-facet-ffp-goal') ?? '', goal));
    const rankedCards = [...new Map([...referenceCards, ...compatibleCards, ...catalogCards].map((card) => [String(card.dataset.projectId ?? ''), card])).values()].slice(0, suggestionLimit);
    const cardSuggestions = rankedCards.flatMap((card) => {
      const trigger = card.querySelector<HTMLElement>('[data-ffp-open]');
      const projectId = String(card.dataset.projectId ?? '').replace(/[^0-9A-Za-z_-]/g, '').slice(0, 80);
      const title = trigger?.dataset.title?.trim() ?? '';
      if (!trigger || !projectId || !title) return [];
      const client = trigger.dataset.client?.trim() || trigger.dataset.type?.trim() || 'Filmreferenz';
      const reference = referenceById.get(projectId);
      const posterNode = card.querySelector<HTMLImageElement>('.ffp-poster');
      return [{
        projectId,
        title,
        client: reference?.client || client,
        poster: reference?.poster || safeUrl(posterNode?.currentSrc || posterNode?.src),
        preview: reference?.preview || safeUrl(trigger.dataset.previewVideo),
        match: reference?.match,
      }];
    });
    const fallback = this.graph && this.session ? rankPortfolioWithFallback(this.session.profile, this.graph.catalog ?? [], suggestionLimit) : null;
    const catalogMatches = fallback?.items ?? [];
    const note = fallback && this.graph?.catalog?.some(item => item.kind === 'portfolio-project') ? portfolioFallbackNote(fallback) : '';
    const suggestions = this.graph?.catalog?.some(item => item.kind === 'portfolio-project') ? catalogMatches.map(item => ({
      projectId: item.content.targetId || '', title: item.content.title,
      client: item.reasons.slice(0, 2).join(' · '), poster: safeUrl(item.content.image),
      preview: '', match: undefined, url: safeUrl(item.content.url),
    })) : cardSuggestions.map(item => ({ ...item, url: '' }));
    if (!suggestions.length) {
      delete slot.dataset.signature;
      slot.replaceChildren();
      return;
    }
    const signature = JSON.stringify({ suggestions, note });
    if (slot.dataset.signature === signature) return;
    slot.dataset.signature = signature;
    slot.innerHTML = (note ? `<p class="portfolio-peek-note">${escapeHtml(note)}</p>` : '') + suggestions.map((item) => {
      const native = cardById.has(item.projectId);
      const start = native ? `<button type="button" data-framefield-project="${escapeHtml(item.projectId)}"` : `<a href="${escapeHtml(item.url)}" data-recommendation="${escapeHtml(item.projectId)}"`;
      return `${start} class="portfolio-peek"><span class="portfolio-peek__media">${item.preview ? `<video muted loop playsinline preload="none" ${item.poster ? `poster="${escapeHtml(item.poster)}"` : ''} data-preview-src="${escapeHtml(item.preview)}"></video>` : item.poster ? `<img src="${escapeHtml(item.poster)}" alt="" loading="lazy">` : ''}${!item.poster && !item.preview ? '<i aria-hidden="true">▶</i>' : ''}</span><span class="portfolio-peek__copy"><small>${item.match ? `${Math.round(item.match)} % passend` : 'Passendes Projekt'}</small><strong>${escapeHtml(item.title)}</strong><em>${escapeHtml(item.client)} · Direkt ansehen</em></span><span class="portfolio-peek__arrow" aria-hidden="true">→</span></${native ? 'button' : 'a'}>`;
    }).join('');
    slot.querySelectorAll<HTMLElement>('.portfolio-peek').forEach((button) => {
      const video = button.querySelector<HTMLVideoElement>('video[data-preview-src]');
      if (!video) return;
      const play = (): void => {
        if (!video.src) video.src = video.dataset.previewSrc ?? '';
        void video.play().catch(() => undefined);
      };
      const stop = (): void => { video.pause(); video.currentTime = 0; };
      button.addEventListener('mouseenter', play);
      button.addEventListener('mouseleave', stop);
      button.addEventListener('focusin', play);
      button.addEventListener('focusout', stop);
    });
  }

  private renderInquiryForm(node: FlowNode): string {
    const privacyUrl = safeUrl(node.content.formPrivacyUrl);
    const buttonLabel = node.content.formButtonLabel?.trim() || 'Unverbindlich anfragen';
    const consentLabel = node.content.formConsentLabel?.trim() || 'Ich stimme zu, dass meine Kontaktdaten sowie meine Antworten und ausgewählten Referenzen zur persönlichen Bearbeitung dieser Anfrage übermittelt werden.';
    const successHeading = node.content.formSuccessHeading?.trim() || 'Vielen Dank für Ihre Anfrage.';
    const successBody = playerCopy(this.graph, 'success', node.content.formSuccessBody);
    const subject = node.content.formSubject?.trim() || 'Neue Anfrage aus der Videoberatung';
    return `<div class="inquiry-wrap" data-inquiry-wrap>
      <form class="inquiry-form" data-inquiry-form>
        <input type="hidden" name="subject" value="${escapeHtml(subject)}">
        <label class="inquiry-trap" aria-hidden="true">Website<input name="website" type="text" tabindex="-1" autocomplete="off"></label>
        <div class="inquiry-grid">
          <label class="inquiry-field"><span>Name *</span><input name="name" type="text" autocomplete="name" maxlength="120" placeholder="Ihr Name" required></label>
          <label class="inquiry-field"><span>E-Mail *</span><input name="email" type="email" autocomplete="email" maxlength="190" placeholder="name@unternehmen.de" required></label>
          ${node.content.formShowCompany !== false ? '<label class="inquiry-field"><span>Unternehmen</span><input name="company" type="text" autocomplete="organization" maxlength="160" placeholder="Unternehmen"></label>' : ''}
          ${node.content.formShowPhone !== false ? '<label class="inquiry-field"><span>Telefon</span><input name="phone" type="tel" autocomplete="tel" maxlength="80" placeholder="Telefonnummer"></label>' : ''}
          ${node.content.formShowMessage !== false ? '<label class="inquiry-field inquiry-field--wide"><span>Worum geht es?</span><textarea name="message" maxlength="4000" placeholder="Ein paar Stichpunkte genügen …"></textarea></label>' : ''}
        </div>
        ${node.content.formShareAnswers === 'optional' && this.journeyAnswers().length ? `<label class="inquiry-consent inquiry-consent--share"><input name="shareAnswers" type="checkbox" value="yes"><span>Meine Antworten aus der Beratung und die passenden Referenzen mitsenden (${this.journeyAnswers().length} ${this.journeyAnswers().length === 1 ? 'Antwort' : 'Antworten'}). So kann direkt daran angeknüpft werden.</span></label>` : ''}
        <label class="inquiry-consent"><input name="consent" type="checkbox" value="yes" required><span>${escapeHtml(consentLabel)}${privacyUrl ? ` <a href="${escapeHtml(privacyUrl)}" target="_blank" rel="noopener noreferrer">Datenschutz</a>` : ''}</span></label>
        <button class="inquiry-submit" type="submit" data-label="${escapeHtml(buttonLabel)}">${escapeHtml(buttonLabel)}</button>
        <p class="inquiry-status" data-inquiry-status role="alert" aria-live="polite"></p>
      </form>
      <div class="inquiry-success" aria-hidden="true"><i aria-hidden="true">✓</i><h3 tabindex="-1">${escapeHtml(successHeading)}</h3><p>${escapeHtml(successBody)}</p><div class="actions"><button class="secondary" type="button" data-action="restart">Beratung neu starten</button></div></div>
    </div>`;
  }

  private renderContextSummary(node: FlowNode): string {
    if (!this.graph || !this.session || this.graph.settings.showContextSummary === false || !['recommendation', 'result', 'form'].includes(node.type)) return '';
    const candidates = [this.session.profile.goals[0], this.session.profile.needs[0], this.session.profile.audiences[0]].filter(Boolean);
    const seen = new Set<string>();
    const labels = candidates.flatMap((item) => {
      if (!item || seen.has(item.key)) return [];
      seen.add(item.key);
      return [this.graph!.taxonomy.find((term) => term.key === item.key)?.label || item.labels[0] || item.key];
    }).slice(0, 3);
    if (!labels.length) return '';
    return `<div class="context-summary"><div><small>${escapeHtml(playerCopy(this.graph, 'summary'))}</small><p>${labels.map((label) => `<span>${escapeHtml(label)}</span>`).join('')}</p></div><button type="button" data-action="restart">Antworten ändern</button></div>`;
  }

  private renderNode(node: FlowNode): string {
    if (!this.session || !this.graph) return '';
    const profile = this.session.profile;
    const heading = interpolate(node.content.heading || node.content.fallbackHeading || node.titleInternal, profile);
    const body = interpolate(node.content.body, profile);
    const video = safeUrl(node.content.video?.url);
    const poster = safeUrl(node.content.poster);
    const endAudio = safeUrl(node.content.endBackgroundAudio);
    const revealAt = Math.max(0, Number(node.content.revealAtSeconds ?? 0));
    const interactionTiming = node.content.interactionTiming === 'before-end' ? 'before-end' : 'from-start';
    if (this.mediaNodeId !== node.id) {
      this.mediaNodeId = node.id;
      this.interactionRevealed = !video || (interactionTiming === 'from-start' && revealAt <= 0);
      this.interactionDismissed = false;
      this.interactionAutoTriggered = this.interactionRevealed;
    }
    const layout = resolveInteractionLayout(this.graph) === 'side-panel' ? 'layout-side' : 'layout-bottom';
    const revealed = this.interactionRevealed;
    const recommendations = node.type === 'recommendation' ? this.renderRecommendations(node) : '';
    const quickLinks = this.renderQuickLinks(node);
    const contextSummary = this.renderContextSummary(node);
    const showInquiryForm = node.type === 'form' || node.content.showInquiryForm === true;
    const inquiryForm = showInquiryForm ? this.renderInquiryForm(node) : '';
    const options = node.content.options ?? [];
    const configuredChoicePresentation = node.content.choicePresentation ?? 'auto';
    const choicePresentation = configuredChoicePresentation === 'auto'
      ? options.length <= 4 ? 'grid' : options.length <= 8 ? 'swipe' : 'select'
      : configuredChoicePresentation;
    const isFollowUpSwipe = this.session.history.length > 0 || node.id !== this.graph.startNodeId;
    const choiceButtons = options.map((option: ChoiceOption, index) => {
      const length = option.label.trim().length;
      const span = options.length === 1 || length > 32 || (options.length === 5 && index === 4)
        ? 12
        : options.length === 2 || options.length === 4 || options.length === 5 || length > 20
          ? 6
          : 4;
      const mobileSpan = span === 12 ? 12 : 6;
      return `<button class="choice" style="--choice-span:${span};--choice-mobile-span:${mobileSpan}" type="button" data-choice="${escapeHtml(option.id)}"><span><b>${escapeHtml(option.label)}</b>${option.helper ? `<small>${escapeHtml(option.helper)}</small>` : ''}</span><span class="arrow" aria-hidden="true">→</span></button>`;
    }).join('');
    const choices = !options.length ? '' : choicePresentation === 'select'
      ? `<div class="choices choices--select"><label class="choice-select__label" for="lgsiv-choice-${escapeHtml(node.id)}">Antwort auswählen</label><select class="choice-select" id="lgsiv-choice-${escapeHtml(node.id)}" data-choice-select><option value="">Bitte auswählen …</option>${options.map((option) => `<option value="${escapeHtml(option.id)}">${escapeHtml(option.label)}</option>`).join('')}</select></div>`
      : choicePresentation === 'swipe'
        ? `<div class="choice-swipe-shell is-start${isFollowUpSwipe ? ' is-follow-up' : ''}" data-choice-swipe-shell><div class="choices choices--swipe" data-choice-swipe role="group" aria-label="Antwortmöglichkeiten – horizontal wischbar" tabindex="0">${choiceButtons}</div><div class="choice-swipe-controls" aria-label="Auswahlseiten"><button class="choice-swipe-arrow choice-swipe-arrow--prev" type="button" data-action="swipe-prev" aria-label="Vorherige Auswahl" disabled>←</button><span class="choice-swipe-page" aria-live="polite"><b data-choice-swipe-index>1</b> / <span data-choice-swipe-total>${options.length}</span></span><button class="choice-swipe-arrow choice-swipe-arrow--next" type="button" data-action="swipe-next" aria-label="Nächste Auswahl">→</button></div></div>`
        : `<div class="choices choices--grid">${choiceButtons}</div>`;
    const timelineState = this.journeyTimelineState(node);
    const initialOverallProgress = ((timelineState.activeIndex + (video ? 0 : 1)) / timelineState.segments) * 100;
    const endMode = ['hold-frame', 'poster', 'soft-wait'].includes(node.content.endScreenMode ?? '') ? node.content.endScreenMode! : 'soft-wait';
    const endText = playerCopy(this.graph, 'selection', node.content.endScreenText);
    const pageOverlay = node.content.pageOverlay;
    const pageOverlayUrl = safeUrl(pageOverlay?.url);
    const pageOverlayImage = safeUrl(pageOverlay?.image);
    const timedPage = pageOverlay && pageOverlayUrl && pageOverlay.title?.trim() ? `<aside class="timed-page${pageOverlayImage ? '' : ' timed-page--no-media'}${video ? '' : ' is-visible'}" data-timed-page data-show-at="${Math.max(0, Number(pageOverlay.showAtSeconds ?? 0))}" data-hide-after="${Math.max(0, Number(pageOverlay.hideAfterSeconds ?? 0))}" data-pause-video="${pageOverlay.pauseVideo ? 'true' : 'false'}" aria-live="polite">
      ${pageOverlayImage ? `<a class="timed-page__media" data-timed-page-link href="${escapeHtml(pageOverlayUrl)}" tabindex="-1"><img src="${escapeHtml(pageOverlayImage)}" alt="" loading="lazy"></a>` : ''}
      <a class="timed-page__copy" data-timed-page-link href="${escapeHtml(pageOverlayUrl)}"><small>${escapeHtml(pageOverlay.label?.trim() || 'Passend zum Gespräch')}</small><strong>${escapeHtml(pageOverlay.title.trim())}</strong>${pageOverlay.description?.trim() ? `<span>${escapeHtml(pageOverlay.description.trim())}</span>` : ''}</a>
      <a class="timed-page__arrow" data-timed-page-link href="${escapeHtml(pageOverlayUrl)}" aria-label="${escapeHtml(pageOverlay.title.trim())} öffnen">→</a><button class="timed-page__dismiss" type="button" data-action="dismiss-page-overlay" aria-label="Seitenhinweis schließen">×</button>
    </aside>` : '';
    const locationGreetingTemplate = node.content.locationGreeting?.trim() || this.graph.theme.locationGreeting?.trim() || 'Servus nach {{city}}';
    const locationGreeting = profile.city ? interpolate(locationGreetingTemplate, profile) : '';
    const locationPrompt = node.content.locationPrompt?.trim() || 'Standort erlauben';
    const locationConsentText = playerCopy(this.graph, 'locationConsent', node.content.locationConsentText);
    const locationPrivacyText = playerCopy(this.graph, 'locationPrivacy', node.content.locationPrivacyText);
    const beforeFirstPlayback = this.isBeforeFirstPlayback(node);
    const personalizedStart = Boolean(video && locationGreeting && beforeFirstPlayback);
    const brandLogo = safeUrl(this.graph.theme.brandLogoUrl);
    const brandingClasses = `${this.graph.theme.panelPattern && this.graph.theme.panelPattern !== 'none' ? ` pattern-${this.graph.theme.panelPattern}` : ''}${this.graph.theme.buttonStyle !== 'brand' ? ' buttons-light' : ''} frame-${this.graph.theme.frameStyle ?? 'soft'} panel-${this.graph.theme.panelStyle ?? 'glass'} button-shape-${this.graph.theme.buttonShape ?? 'rounded'} button-motion-${this.graph.theme.buttonMotion ?? 'orbit'} control-style-${this.graph.theme.controlStyle ?? 'glass'} control-shape-${this.graph.theme.controlShape ?? 'circle'} density-${this.graph.theme.contentDensity ?? 'balanced'}`;
    const mediaFocusX = Math.min(100, Math.max(0, Number(node.content.video?.focusX ?? 50)));
    const aspectClass = `aspect-${this.effectiveAspect().replace(':', '-')}`;
    const media = video
      ? `<video class="media" style="object-fit:${node.content.video?.fit === 'contain' ? 'contain' : 'cover'}" ${poster ? `poster="${escapeHtml(poster)}"` : ''} playsinline preload="metadata"><source src="${escapeHtml(video)}" type="${escapeHtml(node.content.video?.mimeType || 'video/mp4')}">${node.content.captions ? `<track kind="captions" srclang="de" src="${escapeHtml(safeUrl(node.content.captions))}" default>` : ''}</video>`
      : `<div class="media-placeholder" ${poster ? `style="background-image:linear-gradient(90deg,rgba(3,8,15,.85),rgba(3,8,15,.1)),url('${escapeHtml(poster)}');background-size:cover;background-position:center"` : ''}></div>`;
    const endAudioMarkup = video && endAudio ? `<audio data-end-audio loop preload="none"><source src="${escapeHtml(endAudio)}"></audio>` : '';
    const endScreen = video ? `<div class="end-screen" data-end-screen ${poster ? `style="background-image:url('${escapeHtml(poster)}')"` : ''} aria-hidden="true"></div>` : '';
    const segmentDividers = Array.from({ length: Math.max(0, timelineState.segments - 1) }, (_, index) => `<i class="journey-timeline__divider" style="left:${((index + 1) / timelineState.segments) * 100}%"></i>`).join('');
    const journeyTimeline = `<div class="journey-timeline" data-journey-timeline data-segments="${timelineState.segments}" data-active-index="${timelineState.activeIndex}" role="progressbar" aria-label="Gesamtfortschritt der Videoberatung" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${Math.round(initialOverallProgress)}" aria-valuetext="Schritt ${timelineState.activeIndex + 1} von ${timelineState.segments}${video ? ', Video wird vorbereitet' : ''}" style="--overall-progress:${initialOverallProgress}%;--interaction-point:0%;--marker-visible:0"><div class="journey-timeline__track"><i class="journey-timeline__fill"></i>${segmentDividers}${video ? '<b class="journey-timeline__marker" data-interaction-marker></b>' : ''}</div><span class="sr-only" data-interaction-marker-label></span></div>`;
    const mediaToggle = video && this.graph.settings.showPlayControl !== false ? '<button class="media-toggle" type="button" data-action="toggle-video" data-playing="false">▶ Video abspielen</button>' : '';
    const soundToggle = video && this.graph.settings.showSoundControl !== false ? `<button class="sound-toggle" type="button" data-action="toggle-sound" data-muted="false" aria-label="Ton ausschalten" aria-pressed="true">${SOUND_ICON}</button>` : '';
    const startGate = video ? `<div class="start-gate${node.content.autoplay === false || personalizedStart ? ' is-visible' : ''}" data-start-gate>${personalizedStart ? `<div class="start-gate__card"><p class="start-gate__greeting">${escapeHtml(locationGreeting)}</p><button type="button" data-action="start-video"><i aria-hidden="true"></i><span>Beratung starten</span></button></div>` : '<button type="button" data-action="start-video"><i aria-hidden="true"></i><span>Beratung starten</span></button>'}</div>` : '';
    const locationPrelude = this.shouldPromptLocation(node) && (this.mode === 'inline' || this.isOpen) ? `<div class="location-prelude"><div class="location-prelude__card"><small>Optional &amp; einmalig</small><h2>Persönlich begrüßt werden?</h2><p>${escapeHtml(locationConsentText)}</p><div class="location-prelude__actions"><button class="primary" type="button" data-action="location">${escapeHtml(locationPrompt)}</button><button class="secondary" type="button" data-action="skip-location">Ohne Standort fortfahren</button></div><small class="location-prelude__privacy">${escapeHtml(locationPrivacyText)} Ortsdaten © OpenStreetMap.</small></div></div>` : '';
    const mediaStatus = video ? '<div class="media-status" data-media-status role="status" aria-live="polite"></div>' : '';
    const teaserLabel = this.interactionDismissed ? 'Antworten wieder öffnen' : node.type === 'recommendation' || node.type === 'result' ? 'Mehr anzeigen' : interactionTiming === 'before-end' ? 'Antworten folgen kurz vor Videoende' : revealAt > 0 ? `Antworten in ${formatTime(revealAt)}` : 'Antworten anzeigen';
    const teaser = video ? `<button class="interaction-teaser" type="button" data-action="reveal-interaction" aria-label="Interaktion jetzt öffnen"><i></i><span data-reveal-countdown>${escapeHtml(teaserLabel)}</span><strong class="interaction-preview">${escapeHtml(heading)}</strong></button>` : '';
    const widgetDragHandle = this.mode === 'widget' ? '<button class="widget-drag-handle" type="button" data-widget-drag aria-label="Beratungsfenster verschieben" title="Ziehen zum Verschieben">⠿</button>' : '';
    const cinemaActive = this.widgetSize === 'large';
    const cinemaToggle = `<button class="widget-size-button widget-cinema" type="button" data-action="widget-cinema" aria-pressed="${cinemaActive}" aria-label="${escapeHtml(this.cinemaLabel(cinemaActive))}" title="${escapeHtml(this.cinemaLabel(cinemaActive))}"><svg class="cinema-enter" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"></path></svg><svg class="cinema-exit" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5"></path></svg></button>`;
    const widgetSizeControls = this.mode === 'widget' && this.isLiquidGlass ? cinemaToggle : this.mode === 'widget' ? `<button class="widget-size-button" type="button" data-action="widget-smaller" aria-label="Beratungsfenster verkleinern" ${this.widgetSize === 'compact' ? 'disabled' : ''}>−</button><button class="widget-size-button" type="button" data-action="widget-larger" aria-label="Beratungsfenster vergrößern" ${this.widgetSize === 'large' ? 'disabled' : ''}>＋</button>` : '';
    const cta = node.content.ctaLabel && safeUrl(node.content.ctaUrl)
      ? `<a class="primary" data-cta="${escapeHtml(node.content.ctaLabel)}" href="${escapeHtml(safeUrl(node.content.ctaUrl))}">${escapeHtml(node.content.ctaLabel)} <span aria-hidden="true">→</span></a>` : '';
    const secondary = node.content.secondaryLabel && safeUrl(node.content.secondaryUrl)
      ? `<a class="secondary" href="${escapeHtml(safeUrl(node.content.secondaryUrl))}">${escapeHtml(node.content.secondaryLabel)}</a>` : '';
    const aspectMode = this.graph.settings.aspectMode ?? (this.graph.settings.mobileAutoReframe === false ? 'fixed' : 'responsive');
    return `<section class="stage ${aspectClass} ${layout} end-mode-${endMode}${brandingClasses}${aspectMode === 'responsive' ? ' mobile-responsive' : ' mobile-fixed'}${revealed ? ' is-interaction-revealed' : ''}${showInquiryForm ? ' has-inquiry-form' : ''}${['recommendation', 'result'].includes(node.type) ? ' has-rich-content' : ''}${this.pageCompanion ? ' has-page-companion' : ''}${this.pageCompanion?.highlightMinimize ? ' highlight-minimize' : ''}" aria-labelledby="lgsiv-title-${escapeHtml(node.id)}" style="--media-focus-x:${mediaFocusX}%">
      ${media}
      ${endAudioMarkup}
      ${endScreen}
      ${mediaStatus}
      ${startGate}
      ${locationPrelude}
      <div class="topbar">
        <div>${brandLogo ? `<img class="player-brand" src="${escapeHtml(brandLogo)}" alt="">` : ''}${soundToggle}</div>
        ${widgetDragHandle}
        <div>
          ${widgetSizeControls}
          ${mediaToggle}
          ${this.graph.settings.allowBack && this.graph.settings.showBackControl !== false && this.session.history.length ? '<button class="icon-btn" type="button" data-action="back" aria-label="Zurück">←</button>' : ''}
          ${this.mode !== 'inline' && this.graph.settings.showCloseControl !== false ? `<button class="icon-btn" type="button" data-action="close" aria-label="${this.pageCompanion ? 'Beratung zum Seitenbegleiter minimieren' : 'Videoberatung schließen'}" title="${this.pageCompanion ? 'Minimieren' : 'Schließen'}">×</button>` : ''}
        </div>
      </div>
      ${journeyTimeline}
      ${teaser}
      <div class="content" data-interaction-panel aria-hidden="${revealed ? 'false' : 'true'}" ${revealed ? '' : 'inert'}>
        <button class="collapse-interaction" type="button" data-action="collapse-interaction" aria-label="Antworten einklappen"><span aria-hidden="true">⌄</span> Einklappen</button>
        ${node.content.eyebrow ? `<p class="eyebrow">${escapeHtml(interpolate(node.content.eyebrow, profile))}</p>` : ''}
        <h2 id="lgsiv-title-${escapeHtml(node.id)}" tabindex="-1">${escapeHtml(heading)}</h2>
        ${body ? `<p class="body">${escapeHtml(body)}</p>` : ''}
        ${contextSummary}
        ${recommendations}
        ${choices}
        ${timedPage}
        ${quickLinks}
        ${inquiryForm}
        ${!showInquiryForm && (cta || secondary) ? `<div class="actions">${cta}${secondary}</div>` : ''}
        ${node.type === 'result' && !showInquiryForm ? '<div class="actions"><button class="secondary" type="button" data-action="restart">Beratung neu starten</button></div>' : ''}
      </div>
      ${video ? `<div class="wait-hint" data-wait-hint tabindex="0" role="note" title="${escapeHtml(endText)}" aria-hidden="true"><i aria-hidden="true">i</i><span>${escapeHtml(endText)}</span></div>` : ''}
    </section>`;
  }

  private renderInitialPip(widgetPositionClass: string, fromMobileTab = false): string {
    if (!this.graph) return '';
    const theme = this.graph.theme;
    const { video, poster, focusX: pipFocus } = this.launcherMedia();
    if (!hasLauncherMedia({ video, poster })) return '';
    const style = ['edge-preview', 'video-card', 'glass', 'minimal'].includes(theme.pipStyle ?? '') ? theme.pipStyle : 'edge-preview';
    const action = this.graph.settings.pipClickAction === 'show-launcher' && this.graph.settings.openingUseInfoCard === true ? 'expand-launcher' : 'open';
    const calm = this.prefersReducedMotion();
    // „Bewegung reduzieren“: kein Loop, nur das Standbild (Poster oder erstes Bild des Videos).
    const visual = video
      ? `<video muted ${calm ? '' : 'autoplay loop '}playsinline preload="metadata" data-launcher-media ${poster ? `poster="${escapeHtml(poster)}"` : ''}><source src="${escapeHtml(calm && !poster ? stillFrameUrl(video) : video)}"></video>`
      : `<img src="${escapeHtml(poster)}" alt="" loading="lazy" data-launcher-media>`;
    return `<aside class="initial-pip initial-pip--${escapeHtml(style)}${fromMobileTab ? ' initial-pip--from-tab' : ''} ${widgetPositionClass}" aria-label="Videoberatung"${this.isLiquidGlass ? ` style="--media-focus-x:${pipFocus}%"` : ''}>
      <span class="initial-pip__glow" aria-hidden="true"><span class="initial-pip__streak-runner"><span class="initial-pip__streak"></span></span></span>
      <button class="initial-pip__main" type="button" data-action="${action}" aria-label="${action === 'open' ? 'Videoberatung öffnen' : 'Information zur Videoberatung öffnen'}">
        <span class="initial-pip__media">${visual}<i aria-hidden="true"></i></span>
        <span class="initial-pip__copy"><small>${escapeHtml(theme.launcherGreeting || 'Persönliche Videoberatung')}</small><strong>${escapeHtml(theme.launcherLabel || 'Beratung starten')}</strong></span>
      </button>
      <button class="initial-pip__collapse" type="button" data-action="collapse-launcher" aria-label="Videovorschau vollständig einklappen" title="Einklappen">›</button>
    </aside>`;
  }

  private renderProjectCompanion(): string {
    if (!this.graph || !this.projectCompanion) return '';
    const theme = this.graph.theme;
    const pipStyle = ['video-card', 'glass', 'minimal'].includes(theme.pipStyle ?? '') ? theme.pipStyle : 'video-card';
    const projectRule = this.graph.catalog.find((item) => item.kind === 'portfolio-project' && String(item.targetId ?? '') === this.projectCompanion!.projectId);
    const personalize = (value: string | undefined): string => interpolate(value, this.session!.profile)
      .split('{{project}}').join(this.projectCompanion!.title)
      .split('{{client}}').join(this.projectCompanion!.client)
      .trim();
    const video = safeUrl(projectRule?.companionVideoUrl || theme.companionVideoUrl);
    const poster = safeUrl(projectRule?.companionPosterUrl || theme.companionPosterUrl || this.projectCompanion.poster);
    if (this.companionCollapsed) return `<button class="companion-side-tab" type="button" data-action="expand-project-companion" aria-label="Projektbegleiter wieder öffnen">${poster ? `<img src="${escapeHtml(poster)}" alt="">` : '<span class="launcher-orb" aria-hidden="true"></span>'}<i aria-hidden="true">‹</i></button>`;
    const eyebrow = personalize(projectRule?.companionEyebrow || theme.companionEyebrow) || 'Projektmodus';
    const message = playerCopy(this.graph, 'companion', personalize(projectRule?.companionMessage || theme.companionMessage));
    const buttonLabel = personalize(projectRule?.companionButtonLabel || theme.companionButtonLabel) || 'Beratung fortsetzen';
    const muted = theme.companionAutoplaySound === false;
    const loop = theme.companionLoop === true;
    const visual = video
      ? `<video data-companion-video ${muted ? 'muted' : ''} autoplay ${loop ? 'loop' : ''} playsinline preload="auto" ${poster ? `poster="${escapeHtml(poster)}"` : ''}><source src="${escapeHtml(video)}"></video><span class="companion-status" aria-live="polite">Für Ton einmal antippen</span><button class="companion-sound" type="button" data-action="toggle-companion-sound" data-muted="${String(muted)}" aria-label="${muted ? 'Ton des Begleitvideos einschalten' : 'Begleitvideo stummschalten'}">${SOUND_ICON}</button><button class="companion-replay" type="button" data-action="replay-companion" aria-label="Begleitvideo erneut abspielen">↻</button>`
      : poster ? `<img src="${escapeHtml(poster)}" alt="" loading="lazy">` : '<span class="launcher-orb" aria-hidden="true"></span>';
    return `<aside class="project-companion project-companion--${escapeHtml(pipStyle)}" aria-label="Videoberatung zum geöffneten Projekt">
      <div class="companion-media">${visual}</div>
      <button class="companion-copy" type="button" data-action="resume-project-companion"><small>${escapeHtml(eyebrow)}</small><strong>${escapeHtml(this.projectCompanion.title)}</strong><span>${escapeHtml(message)}</span><b>${escapeHtml(buttonLabel)} →</b></button>
      <button class="companion-dismiss" type="button" data-action="collapse-project-companion" aria-label="Projektbegleiter an den Seitenrand einklappen">›</button>
    </aside>`;
  }

  private renderPageCompanion(): string {
    if (!this.graph || !this.pageCompanion) return '';
    const theme = this.graph.theme;
    const pipStyle = ['video-card', 'glass', 'minimal'].includes(theme.pipStyle ?? '') ? theme.pipStyle : 'video-card';
    const video = safeUrl(this.pageCompanion.video || theme.companionVideoUrl);
    const poster = safeUrl(this.pageCompanion.poster || theme.companionPosterUrl);
    if (this.companionCollapsed) return `<button class="companion-side-tab" type="button" data-action="expand-project-companion" aria-label="Seitenbegleiter wieder öffnen">${poster ? `<img src="${escapeHtml(poster)}" alt="">` : '<span class="launcher-orb" aria-hidden="true"></span>'}<i aria-hidden="true">‹</i></button>`;
    const buttonLabel = theme.companionButtonLabel?.trim() || 'Beratung fortsetzen';
    const muted = theme.companionAutoplaySound === false;
    const loop = theme.companionLoop === true;
    const visual = video
      ? `<video data-companion-video ${muted ? 'muted' : ''} autoplay ${loop ? 'loop' : ''} playsinline preload="auto" ${poster ? `poster="${escapeHtml(poster)}"` : ''}><source src="${escapeHtml(video)}"></video><span class="companion-status" aria-live="polite">Für Ton einmal antippen</span><button class="companion-sound" type="button" data-action="toggle-companion-sound" data-muted="${String(muted)}" aria-label="${muted ? 'Ton des Begleitvideos einschalten' : 'Begleitvideo stummschalten'}">${SOUND_ICON}</button><button class="companion-replay" type="button" data-action="replay-companion" aria-label="Begleitvideo erneut abspielen">↻</button>`
      : poster ? `<img src="${escapeHtml(poster)}" alt="" loading="lazy">` : '<span class="launcher-orb" aria-hidden="true"></span>';
    return `<aside class="project-companion project-companion--${escapeHtml(pipStyle)}" aria-label="Videoberatung zur geöffneten Seite">
      <div class="companion-media">${visual}</div>
      <button class="companion-copy" type="button" data-action="resume-project-companion"><small>${escapeHtml(this.pageCompanion.eyebrow)}</small><strong>${escapeHtml(this.pageCompanion.title)}</strong><span>${escapeHtml(this.pageCompanion.message)}</span><b>${escapeHtml(buttonLabel)} →</b></button>
      <button class="companion-dismiss" type="button" data-action="collapse-project-companion" aria-label="Seitenbegleiter an den Seitenrand einklappen">›</button>
    </aside>`;
  }

  private syncCompanionSoundButton(video: HTMLVideoElement): void {
    const button = this.root.querySelector<HTMLButtonElement>('[data-action="toggle-companion-sound"]');
    if (!button) return;
    button.dataset.muted = String(video.muted);
    button.setAttribute('aria-label', video.muted ? 'Ton des Begleitvideos einschalten' : 'Begleitvideo stummschalten');
  }

  private bindCompanionMedia(): void {
    const video = this.root.querySelector<HTMLVideoElement>('[data-companion-video]');
    const shell = this.root.querySelector<HTMLElement>('.project-companion');
    if (!video || !shell || !this.graph) return;
    video.volume = Math.min(1, Math.max(0, Number(this.graph.theme.companionVolume ?? 0.9)));
    video.muted = this.graph.theme.companionAutoplaySound === false;
    this.syncCompanionSoundButton(video);
    video.addEventListener('play', () => {
      shell.classList.remove('is-ended');
      this.signalExclusivePlayback();
    });
    video.addEventListener('ended', () => {
      shell.classList.add('is-ended');
      this.releasePortfolioMediaAfterCompanion();
    });
    video.addEventListener('error', () => this.releasePortfolioMediaAfterCompanion());
    video.addEventListener('volumechange', () => this.syncCompanionSoundButton(video));
    void video.play().then(() => shell.classList.remove('needs-sound')).catch(() => {
      video.muted = true;
      shell.classList.add('needs-sound');
      this.syncCompanionSoundButton(video);
      void video.play().catch(() => undefined);
      if (this.graph?.theme.companionAutoplaySound !== false) this.armSoundUnlock(video, () => { shell.classList.remove('needs-sound', 'is-ended'); this.syncCompanionSoundButton(video); }, () => this.syncCompanionSoundButton(video));
    });
  }

  /** Ton erzwingen (29.10.2026): Nach einem Seitenwechsel erlauben Browser – vor allem Safari/iOS – Ton erst nach einer
   *  Berührung auf genau dieser Seite. Das Begleitvideo läuft deshalb erst stumm an; die erste Berührung, ein Klick oder
   *  eine Taste irgendwo auf der Seite schaltet den Ton ein und startet das Video von vorn, damit die Ansprache ganz
   *  zu hören ist. Wischen zum Scrollen zählt im Browser nicht als Berührung; dann greift das nächste Antippen.
   *  Klicks auf den eigenen Ton-Knopf bleiben dessen Sache. Wer selbst stummschaltet, bekommt keinen Ton aufgezwungen. */
  private armSoundUnlock(video: HTMLVideoElement, onSound: () => void, onBlocked?: () => void): void {
    const events = ['pointerup', 'touchend', 'click', 'keydown'] as const;
    let done = false;
    const stop = (): void => {
      done = true;
      events.forEach((type) => document.removeEventListener(type, unlock, true));
    };
    const unlock = (event: Event): void => {
      if (done) return;
      if (!video.isConnected) { stop(); return; }
      // eigener Ton-Knopf: der Knopf entscheidet selbst, wir greifen nicht mehr ein
      if (event.composedPath().some((node) => node instanceof Element && node.matches?.('[data-action="toggle-companion-sound"],[data-action="toggle-sound"]'))) { stop(); return; }
      if (!video.muted) { stop(); return; }
      video.muted = false;
      if (!video.ended && video.currentTime < 30) video.currentTime = 0;
      void video.play().then(() => { stop(); onSound(); }).catch(() => {
        video.muted = true;
        onBlocked?.();
        void video.play().catch(() => undefined);
      });
    };
    events.forEach((type) => document.addEventListener(type, unlock, { capture: true, passive: true }));
  }

  private prefersReducedMotion(): boolean {
    return this.reducedMotionQuery?.matches ?? (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  private readonly handleReducedMotionChange = (): void => {
    if (this.isOpen) return;
    if (this.reducedMotionQuery?.matches) this.collapseToSideTab();
    this.render();
  };

  /** Video und Poster für Vorschau und Lasche: eigenes PiP-Video, sonst das erste Journey-Video. */
  private launcherMedia(): { video: string; poster: string; focusX: number } {
    if (!this.graph) return { video: '', poster: '', focusX: 50 };
    const theme = this.graph.theme;
    const startNode = this.graph.nodes.find((node) => node.id === this.graph!.startNodeId);
    const previewNode = startNode?.content.video?.url ? startNode : this.graph.nodes.find((node) => Boolean(node.content.video?.url));
    const video = this.launcherVideoFailed ? '' : safeUrl(theme.companionVideoUrl || previewNode?.content.video?.url);
    const poster = this.launcherPosterFailed ? '' : safeUrl(theme.companionPosterUrl || previewNode?.content.poster);
    const focusX = theme.companionVideoUrl ? 50 : Math.min(100, Math.max(0, Number(previewNode?.content.video?.focusX ?? 50)));
    return { video, poster, focusX };
  }

  /** Lädt ein Vorschau-Medium nicht, wird es verworfen; ohne Ersatz verschwindet der Starter. */
  private bindLauncherMedia(): void {
    this.root.querySelectorAll<HTMLElement>('[data-launcher-media]').forEach((element) => {
      const fail = (): void => {
        if (element instanceof HTMLImageElement) this.launcherPosterFailed = true;
        else this.launcherVideoFailed = true;
        if (!this.isOpen) this.render();
      };
      if (element instanceof HTMLVideoElement) {
        element.addEventListener('error', fail, { once: true });
        element.querySelector('source')?.addEventListener('error', fail, { once: true });
      } else element.addEventListener('error', fail, { once: true });
    });
  }

  private render(): void {
    const style = `<style>${compatibleUnits(this.isLiquidGlass ? STYLE + LIQUID_GLASS_STYLE : STYLE)}</style>`;
    const shellClass = this.isLiquidGlass ? 'shell ff-liquid' : 'shell';
    if (this.loading) { this.root.innerHTML = `${style}<div class="loading"><div><div class="spinner" aria-hidden="true"></div><p>Videoberatung wird geladen …</p></div></div>`; return; }
    if (this.errorMessage) { this.root.innerHTML = `${style}<div class="error"><div><strong>${escapeHtml(this.errorMessage)}</strong><p>${escapeHtml(playerCopy(this.graph, 'reload'))}</p></div></div>`; return; }
    if (!this.graph || !this.session) { this.root.innerHTML = style; return; }
    const theme = this.graph.theme;
    const activeAspect = this.effectiveAspect();
    const ratio = activeAspect.replace(':', '/');
    const mobileAspect = this.graph.settings.mobileAspect || '9:16';
    const mobileRatio = mobileAspect.replace(':', '/');
    const ratioParts = activeAspect.split(':').map(Number);
    const mobileParts = mobileAspect.split(':').map(Number);
    this.style.setProperty('--legacy-ratio', `${100 * ratioParts[1] / ratioParts[0]}%`);
    this.style.setProperty('--legacy-mobile-ratio', `${100 * mobileParts[1] / mobileParts[0]}%`);
    const mobileWidthFactors: Record<string, number> = { '9:16': .5625, '4:5': .8, '1:1': 1, '16:9': 1.7778 };
    const mobileWidthFactor = mobileWidthFactors[mobileAspect] ?? .5625;
    const glowStrength = Math.min(40, Math.max(0, Number(theme.glowStrength ?? 18)));
    const headingScale = Math.min(1.4, Math.max(.7, Number(theme.headingScale ?? 100) / 100));
    const bodyScale = Math.min(1.25, Math.max(.8, Number(theme.bodyScale ?? 100) / 100));
    const buttonScale = Math.min(1.25, Math.max(.8, Number(theme.buttonScale ?? 100) / 100));
    const buttonSize = Math.min(1.5, Math.max(.7, Number(theme.buttonSize ?? 100) / 100));
    const responsiveButtonScale = buttonScale * (1 + ((buttonSize - 1) * .52));
    const controlScale = Math.min(1.4, Math.max(.7, Number(theme.controlScale ?? 100) / 100));
    const borderWidth = Math.min(3, Math.max(0, Number(theme.borderWidth ?? 1)));
    const vars = `--accent:${theme.accent};--accent-2:${theme.accentSecondary || theme.accent};--button-hover:${theme.buttonHoverColor || theme.accent};--bg:${theme.background};--surface:${theme.surface};--text:${theme.text};--muted:${theme.muted};--radius:${theme.radius}px;--font:${theme.fontFamily};--heading-font:${theme.headingFontFamily || theme.fontFamily};--body-font:${theme.bodyFontFamily || theme.fontFamily};--button-font:${theme.buttonFontFamily || theme.fontFamily};--heading-min:${(24 * headingScale).toFixed(2)}px;--heading-fluid:${(2.8 * headingScale).toFixed(2)}vw;--heading-max:${(38 * headingScale).toFixed(2)}px;--heading-16-min:${(19 * headingScale).toFixed(2)}px;--heading-16-fluid:${(2.15 * headingScale).toFixed(2)}vw;--heading-16-max:${(30 * headingScale).toFixed(2)}px;--body-min:${(13 * bodyScale).toFixed(2)}px;--body-fluid:${(1.25 * bodyScale).toFixed(2)}vw;--body-max:${(16 * bodyScale).toFixed(2)}px;--body-16-min:${(11 * bodyScale).toFixed(2)}px;--body-16-fluid:${bodyScale.toFixed(2)}vw;--body-16-max:${(14 * bodyScale).toFixed(2)}px;--button-size:${(15 * responsiveButtonScale).toFixed(2)}px;--button-16-min:${(12 * responsiveButtonScale).toFixed(2)}px;--choice-min-height:${(56 * buttonSize).toFixed(2)}px;--choice-pad-y:${(12 * buttonSize).toFixed(2)}px;--choice-pad-x:${(16 * buttonSize).toFixed(2)}px;--choice-16-min-height:${(42 * buttonSize).toFixed(2)}px;--choice-16-pad-y:${(7 * buttonSize).toFixed(2)}px;--choice-16-pad-x:${(12 * buttonSize).toFixed(2)}px;--choice-compact-min-height:${(48 * buttonSize).toFixed(2)}px;--choice-compact-pad-y:${(8 * buttonSize).toFixed(2)}px;--choice-compact-pad-x:${(7 * buttonSize).toFixed(2)}px;--choice-mobile-min-height:${(62 * buttonSize).toFixed(2)}px;--choice-mobile-pad-y:${(10 * buttonSize).toFixed(2)}px;--choice-mobile-pad-x:${(8 * buttonSize).toFixed(2)}px;--widget-heading-standard-min:${(17.28 * headingScale).toFixed(2)}px;--widget-heading-standard-max:${(31.16 * headingScale).toFixed(2)}px;--widget-heading-compact-min:${(13.92 * headingScale).toFixed(2)}px;--widget-heading-compact-max:${(25.84 * headingScale).toFixed(2)}px;--widget-heading-large-min:${(19.68 * headingScale).toFixed(2)}px;--widget-body-standard-min:${(10.66 * bodyScale).toFixed(2)}px;--widget-body-standard-max:${(14.72 * bodyScale).toFixed(2)}px;--widget-body-compact-min:${(9.36 * bodyScale).toFixed(2)}px;--widget-body-compact-max:${(13.12 * bodyScale).toFixed(2)}px;--widget-body-large-min:${(11.96 * bodyScale).toFixed(2)}px;--widget-button-standard-min:${(10.8 * responsiveButtonScale).toFixed(2)}px;--widget-button-standard-max:${(15 * responsiveButtonScale).toFixed(2)}px;--widget-button-compact-min:${(9.84 * responsiveButtonScale).toFixed(2)}px;--widget-button-compact-max:${(13.5 * responsiveButtonScale).toFixed(2)}px;--widget-button-large-max:${(15.6 * responsiveButtonScale).toFixed(2)}px;--widget-choice-standard:${(45.92 * buttonSize).toFixed(2)}px;--widget-choice-compact:${(39.2 * buttonSize).toFixed(2)}px;--control-size:${(50 * controlScale).toFixed(2)}px;--control-sound-size:${(44 * controlScale).toFixed(2)}px;--control-icon-size:${(22 * controlScale).toFixed(2)}px;--control-widget-size:${(46 * controlScale).toFixed(2)}px;--control-widget-sound-size:${(41 * controlScale).toFixed(2)}px;--control-compact-size:${(39 * controlScale).toFixed(2)}px;--control-compact-sound-size:${(35 * controlScale).toFixed(2)}px;--control-mobile-size:${(44 * controlScale).toFixed(2)}px;--control-mobile-sound-size:${(39 * controlScale).toFixed(2)}px;--border-width:${borderWidth}px;--ratio:${ratio};--mobile-ratio:${mobileRatio};--mobile-widget-standard:${(75 * mobileWidthFactor).toFixed(4)}dvh;--mobile-widget-compact:${(58 * mobileWidthFactor).toFixed(4)}dvh;--mobile-widget-large:${(90 * mobileWidthFactor).toFixed(4)}dvh;--glow-blur:${Math.round(4 + glowStrength * 1.1)}px`;
    const node = this.currentNode();
    if (!node) { this.root.innerHTML = `${style}<div class="error">Der aktuelle Journey-Knoten fehlt.</div>`; return; }
    const stage = this.renderNode(node);
    const portrait = ['9:16', '4:5'].includes(activeAspect);
    const configuredWidgetPosition = this.graph.settings.widgetPosition ?? 'bottom-right';
    const widgetPosition = ['bottom-right', 'bottom-left', 'top-right', 'top-left'].includes(configuredWidgetPosition) ? configuredWidgetPosition : 'bottom-right';
    const widgetPositionClass = `widget-${widgetPosition}`;
    if (this.mode === 'inline') this.root.innerHTML = `${style}<div class="${shellClass}" style="${escapeHtml(compatibleUnits(vars + (this.isLiquidGlass ? playerDesignVars('liquid-glass') : '')))}">${stage}<p class="sr-only" aria-live="polite" data-live></p></div>`;
    else {
      const cityGreeting = this.session.profile.city && this.isBeforeFirstPlayback(node) ? interpolate(theme.locationGreeting || 'Servus nach {{city}}', this.session.profile) : theme.launcherGreeting;
      const label = this.getAttribute('launcher-label') || theme.launcherLabel;
      const needsDetachedLocation = !this.isOpen && !this.projectCompanion && !this.pageCompanion && this.shouldPromptLocation(node);
      const locationPrompt = node.content.locationPrompt?.trim() || 'Standort erlauben';
      const locationConsentText = playerCopy(this.graph, 'locationConsent', node.content.locationConsentText);
      const { start: initialWidgetState, afterTab } = resolveWidgetStart(this.getAttribute('initial-widget-state'), this.graph.settings.initialWidgetState, this.prefersReducedMotion());
      const usePipStep = this.graph.settings.openingUsePip !== false;
      const useInfoStep = this.graph.settings.openingUseInfoCard === true;
      const startsAsSideTab = !this.launcherExpanded && !this.openingPipExpanded && (this.launcherCollapsed || initialWidgetState === 'side-tab');
      const sideTabAction = afterTab === 'open' ? 'open' : afterTab === 'launcher' ? 'expand-launcher' : usePipStep ? 'expand-opening-pip' : useInfoStep ? 'expand-launcher' : 'open';
      const media = this.launcherMedia();
      // Ohne Video und ohne Poster bleibt der geschlossene Berater unsichtbar (kein leeres Glasfeld).
      const showStarter = hasLauncherMedia(media);
      const sideTab = () => {
        const thumb = media.poster
          ? `<img src="${escapeHtml(media.poster)}" alt="" data-launcher-media>`
          : `<video muted playsinline preload="metadata" data-launcher-media><source src="${escapeHtml(stillFrameUrl(media.video))}"></video>`;
        return `<button class="launcher-side-tab launcher-side-tab--media ${widgetPositionClass}" type="button" data-action="${sideTabAction}" aria-label="Videoberatung öffnen" style="--media-focus-x:${media.focusX}%"><span class="launcher-side-tab__thumb" aria-hidden="true">${thumb}</span><i aria-hidden="true">‹</i></button>`;
      };
      const launcher = this.isOpen ? '' : (this.projectCompanion || this.pageCompanion)
        ? (this.projectCompanion ? this.renderProjectCompanion() : this.renderPageCompanion())
        : !showStarter
          ? ''
        : startsAsSideTab
          ? sideTab()
        : needsDetachedLocation
          ? `<aside class="location-launcher" aria-label="Optionale Standortfreigabe"><small>Optional &amp; einmalig</small><strong>Persönlich begrüßt werden?</strong><p>${escapeHtml(locationConsentText)}</p><div><button class="primary" type="button" data-action="location">${escapeHtml(locationPrompt)}</button><button class="secondary" type="button" data-action="skip-location">Ohne Standort starten</button></div></aside>`
          : usePipStep && this.openingPipExpanded && !this.launcherExpanded
            ? this.renderInitialPip(widgetPositionClass, true)
          : usePipStep && !this.launcherExpanded && initialWidgetState === 'pip'
              ? this.renderInitialPip(widgetPositionClass)
              : `<div class="launcher-shell ${widgetPositionClass}"><button class="launcher" type="button" data-action="open"><span class="launcher-orb" aria-hidden="true"></span><span class="launcher-copy"><small>${escapeHtml(cityGreeting)}</small><strong>${escapeHtml(label)}</strong></span></button><button class="launcher-collapse" type="button" data-action="collapse-launcher" aria-label="Videoberatung an den Seitenrand einklappen">›</button></div>`;
      const widgetOffsetStyle = this.mode === 'widget' && this.widgetOffset ? ` style="left:${Math.round(this.widgetOffset.left)}px;top:${Math.round(this.widgetOffset.top)}px;right:auto;bottom:auto"` : '';
      const responsiveMobile = this.mode === 'widget' && this.isMobileViewport() && (this.graph.settings.aspectMode ?? (this.graph.settings.mobileAutoReframe === false ? 'fixed' : 'responsive')) === 'responsive';
      const overlay = this.isOpen ? `<div class="overlay${this.animatePlayerOpen ? ' is-opening' : ''} mode-${escapeHtml(this.mode)} ${widgetPositionClass} widget-size-${this.widgetSize}${portrait ? ' portrait' : ''}${responsiveMobile ? ' mobile-responsive' : ' mobile-fixed'}"${widgetOffsetStyle} role="dialog" aria-modal="${this.mode === 'widget' ? 'false' : 'true'}" aria-label="Interaktive Videoberatung">${stage}<p class="sr-only" aria-live="polite" data-live></p></div>` : '';
      this.root.innerHTML = `${style}<div class="${shellClass}" style="${escapeHtml(compatibleUnits(vars + (this.isLiquidGlass ? playerDesignVars('liquid-glass') : '')))}">${launcher}${overlay}</div>`;
    }
    this.syncPortfolioPeek();
    this.syncInteractionPanelGeometry();
    this.bindChoiceSwipe();
    this.bindMediaEvents(node);
    if (this.mode === 'inline' && this.graph.settings.scrollDock && !this.previewConfig) {
      const shell = this.root.querySelector('.shell');
      const controls = document.createElement('div');
      controls.className = 'scroll-dock-controls';
      controls.innerHTML = '<button type="button">Zum großen Player ↑</button><button type="button" aria-label="Kleinen Player ausblenden">×</button>';
      controls.children[0].addEventListener('click', () => this.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' }));
      controls.children[1].addEventListener('click', () => { this.scrollDockDismissed = true; this.root.querySelector<HTMLVideoElement>('video')?.pause(); this.syncScrollDock(); });
      shell?.append(controls);
    }
    this.syncScrollDock();
    if ((this.projectCompanion || this.pageCompanion) && !this.isOpen) this.bindCompanionMedia();
    this.bindLauncherMedia();
    this.syncNodePortfolioFilter();
  }

  private syncInteractionPanelGeometry(): void {
    this.interactionPanelObserver?.disconnect();
    this.interactionPanelObserver = null;
    const stage = this.root.querySelector<HTMLElement>('.stage');
    const panel = this.root.querySelector<HTMLElement>('[data-interaction-panel]');
    if (!stage || !panel) return;
    const update = () => {
      stage.style.setProperty('--interaction-panel-height', `${Math.round(panel.offsetHeight)}px`);
      if (!CSS.supports('width', '1cqw')) stage.style.setProperty('--legacy-cqw', `${stage.clientWidth / 100}px`);
    };
    update();
    window.requestAnimationFrame(update);
    if ('ResizeObserver' in window) {
      this.interactionPanelObserver = new ResizeObserver(update);
      this.interactionPanelObserver.observe(panel);
      this.interactionPanelObserver.observe(stage);
    }
  }

  private bindMediaEvents(node: FlowNode): void {
    const video = this.root.querySelector<HTMLVideoElement>('video.media');
    if (!video) return;
    const toggle = this.root.querySelector<HTMLButtonElement>('[data-action="toggle-video"]');
    const soundButton = this.root.querySelector<HTMLButtonElement>('[data-action="toggle-sound"]');
    const gate = this.root.querySelector<HTMLElement>('[data-start-gate]');
    const countdown = this.root.querySelector<HTMLElement>('[data-reveal-countdown]');
    const mediaStatus = this.root.querySelector<HTMLElement>('[data-media-status]');
    const timeline = this.root.querySelector<HTMLElement>('[data-journey-timeline]');
    const markerLabel = this.root.querySelector<HTMLElement>('[data-interaction-marker-label]');
    const endScreen = this.root.querySelector<HTMLElement>('[data-end-screen]');
    const waitHint = this.root.querySelector<HTMLElement>('[data-wait-hint]');
    const timedPage = this.root.querySelector<HTMLElement>('[data-timed-page]');
    const endAudio = this.root.querySelector<HTMLAudioElement>('audio[data-end-audio]');
    const revealOffset = Math.max(0, Number(node.content.revealAtSeconds ?? 0));
    const interactionTiming = node.content.interactionTiming === 'before-end' ? 'before-end' : 'from-start';
    const revealAt = (): number => interactionTiming === 'before-end' && Number.isFinite(video.duration) && video.duration > 0
      ? Math.max(0, video.duration - revealOffset)
      : revealOffset;
    const syncTimedPage = () => {
      if (!timedPage || timedPage.dataset.dismissed === 'true') return;
      const showAt = Math.max(0, Number(timedPage.dataset.showAt ?? 0));
      const hideAfter = Math.max(0, Number(timedPage.dataset.hideAfter ?? 0));
      const visible = video.currentTime >= showAt && (hideAfter <= 0 || video.currentTime < showAt + hideAfter);
      const wasVisible = timedPage.classList.contains('is-visible');
      timedPage.classList.toggle('is-visible', visible);
      if (visible && !wasVisible) {
        if (timedPage.dataset.pauseVideo === 'true' && !video.paused) video.pause();
        if (timedPage.dataset.impressed !== 'true') {
          timedPage.dataset.impressed = 'true';
          this.track('page-overlay-impression', { nodeId: node.id, value: String(showAt) });
        }
      }
    };
    if (endAudio) endAudio.volume = Math.min(1, Math.max(0, Number(node.content.endBackgroundAudioVolume ?? 0.16)));
    const syncToggle = () => {
      if (!toggle) return;
      const playing = !video.paused && !video.ended;
      toggle.dataset.playing = String(playing);
      toggle.textContent = playing ? 'Ⅱ Pause' : video.ended ? '↻ Erneut ansehen' : '▶ Video abspielen';
      toggle.setAttribute('aria-label', playing ? 'Video pausieren' : video.ended ? 'Video erneut ansehen' : 'Video abspielen');
      if (soundButton) soundButton.hidden = video.ended && !endAudio;
      if (playing) gate?.classList.remove('is-visible');
    };
    const updateTimeline = () => {
      if (!timeline || !Number.isFinite(video.duration) || video.duration <= 0) return;
      const segments = Math.max(1, Number(timeline.dataset.segments ?? 1));
      const activeIndex = Math.max(0, Number(timeline.dataset.activeIndex ?? 0));
      const interactionAt = revealAt();
      const videoFraction = Math.min(1, Math.max(0, video.currentTime / video.duration));
      const interactionFraction = Math.min(1, Math.max(0, interactionAt / video.duration));
      const overallPercent = ((activeIndex + videoFraction) / segments) * 100;
      const interactionPoint = ((activeIndex + interactionFraction) / segments) * 100;
      const remaining = Math.max(0, video.duration - video.currentTime);
      timeline.style.setProperty('--overall-progress', `${overallPercent}%`);
      timeline.style.setProperty('--interaction-point', `${interactionPoint}%`);
      timeline.style.setProperty('--marker-visible', interactionAt >= 0 && interactionAt <= video.duration ? '1' : '0');
      timeline.setAttribute('aria-valuenow', String(Math.round(overallPercent)));
      timeline.setAttribute('aria-valuetext', `Schritt ${activeIndex + 1} von ${segments}, ${video.ended ? 'Video beendet' : `noch ${formatTime(remaining)}`}`);
      if (markerLabel) markerLabel.textContent = interactionAt >= 0 && interactionAt <= video.duration ? `Die Interaktion beginnt bei ${formatTime(interactionAt)}.` : '';
    };
    this.mediaLoading?.dispose();
    this.mediaLoading = bindMediaLoading(video, (loading) => {
      if (!mediaStatus) return;
      // Never erase a genuine error notice when a later media event arrives.
      if (!loading && !mediaStatus.classList.contains('is-loading')) return;
      mediaStatus.textContent = loading ? 'Video wird geladen …' : '';
      mediaStatus.classList.toggle('is-visible', loading);
      mediaStatus.classList.toggle('is-loading', loading);
    });
    const hideMediaStatus = () => this.mediaLoading?.hide();
    this.syncSoundButton(video, endAudio);
    video.addEventListener('play', syncToggle);
    video.addEventListener('play', this.signalExclusivePlayback);
    video.addEventListener('play', () => {
      this.playerStarted = true;
      this.stopEndAudio();
      this.root.querySelector<HTMLElement>('.stage')?.classList.remove('is-video-ended');
      endScreen?.setAttribute('aria-hidden', 'true');
      waitHint?.setAttribute('aria-hidden', 'true');
      this.syncSoundButton(video, endAudio);
    });
    video.addEventListener('loadedmetadata', updateTimeline);
    video.addEventListener('loadedmetadata', syncTimedPage);
    video.addEventListener('durationchange', updateTimeline);
    video.addEventListener('pause', () => {
      if (!video.ended && video.dataset.releasing !== 'true' && this.session?.currentNodeId === node.id) this.pendingChapterId = null;
    });
    video.addEventListener('pause', syncToggle);
    video.addEventListener('ended', syncToggle);
    video.addEventListener('play', () => this.track('video-play', { nodeId: node.id }), { once: true });
    video.addEventListener('timeupdate', () => {
      updateTimeline();
      syncTimedPage();
      const interactionAt = revealAt();
      const remaining = Math.max(0, interactionAt - video.currentTime);
      if (countdown && !this.interactionRevealed && !this.interactionDismissed) countdown.textContent = remaining > 0 ? `Antworten in ${formatTime(remaining)}` : 'Antworten anzeigen';
      const mayAutoReveal = interactionTiming === 'before-end' ? !this.interactionAutoTriggered : !this.interactionDismissed;
      if (!this.interactionRevealed && mayAutoReveal && video.currentTime >= interactionAt) {
        this.interactionAutoTriggered = true;
        this.revealInteraction('time');
      }
      if (!video.duration || !Number.isFinite(video.duration)) return;
      const percent = (video.currentTime / video.duration) * 100;
      [25, 50, 75].forEach((mark) => {
        const key = `${this.session?.id}:${node.id}:${mark}`;
        if (percent >= mark && !this.videoProgressKeys.has(key)) {
          this.videoProgressKeys.add(key);
          this.track(`video-${mark}`, { nodeId: node.id });
        }
      });
    });
    video.addEventListener('ended', () => {
      // A pause at natural end must not cancel the queued chapter. No sentence
      // timing is inferred: this transition is deliberately clip-end only.
      if (this.session?.currentNodeId !== node.id || this.finishRequestedChapter(node.id)) return;
      hideMediaStatus();
      updateTimeline();
      this.root.querySelector<HTMLElement>('.stage')?.classList.add('is-video-ended');
      endScreen?.setAttribute('aria-hidden', 'false');
      waitHint?.setAttribute('aria-hidden', 'false');
      if (endAudio) {
        endAudio.muted = video.muted;
        this.syncSoundButton(video, endAudio);
        this.signalExclusivePlayback();
        void endAudio.play().catch(() => { /* optional audio may be blocked by the browser */ });
      }
      if (!this.interactionDismissed) this.revealInteraction('ended');
      const key = `${this.session?.id}:${node.id}:100`;
      if (this.videoProgressKeys.has(key)) return;
      this.videoProgressKeys.add(key);
      this.track('video-complete', { nodeId: node.id });
    });
    video.addEventListener('error', () => {
      if (video.dataset.releasing === 'true' || this.session?.currentNodeId !== node.id) return;
      this.pendingChapterId = null;
      hideMediaStatus();
      if (mediaStatus) {
        mediaStatus.textContent = 'Video nicht verfügbar – die Beratung kann trotzdem fortgesetzt werden.';
        mediaStatus.classList.add('is-visible');
      }
      gate?.classList.remove('is-visible');
      this.revealInteraction('ended');
      this.announce('Das Video konnte nicht geladen werden. Die Auswahlmöglichkeiten bleiben verfügbar.');
      this.track('video-error', { nodeId: node.id });
    });
    updateTimeline();
    if (node.content.autoplay !== false && !this.shouldPromptLocation(node) && !(this.session?.profile.city && this.isBeforeFirstPlayback(node))) {
      // Ton erzwingen (29.10.2026): ohne Erlaubnis für Ton erst stumm weiterlaufen lassen und beim ersten Antippen
      // irgendwo auf der Seite mit Ton von vorn starten; nur wenn selbst das stumme Abspielen blockiert ist, bleibt das Tor.
      queueMicrotask(() => {
        void video.play().catch(() => {
          if (video.muted) { gate?.classList.add('is-visible'); return; }
          video.muted = true;
          void video.play().then(() => {
            this.armSoundUnlock(video, () => { gate?.classList.remove('is-visible'); this.syncSoundButton(video); });
          }).catch(() => { video.muted = false; gate?.classList.add('is-visible'); });
        });
      });
    }
  }

  private announce(message: string): void {
    const live = this.root.querySelector<HTMLElement>('[data-live]');
    if (live) live.textContent = message;
  }

  private publishLeadContext(emitEvent = true): void {
    if (!this.session || !this.graph) return;
    const detail = {
      flow: this.graph.slug,
      release: this.graph.releaseVersion ?? 0,
      session: this.session.id,
      path: [...this.session.history.map((entry) => entry.nodeId), this.session.currentNodeId],
      needProfile: this.session.profile,
      journeyAnswers: this.journeyAnswers(),
      portfolioReferences: this.session.integrations?.framefield ?? null,
    };
    document.querySelectorAll<HTMLFormElement>('form').forEach((form) => {
      let field = form.querySelector<HTMLInputElement>('[name="lgs_video_need_profile"]');
      if (!field) { field = document.createElement('input'); field.type = 'hidden'; field.name = 'lgs_video_need_profile'; form.append(field); }
      field.value = JSON.stringify(detail);
      field.setAttribute('value', field.value);
    });
    if (emitEvent) this.emit('lead-context', detail);
  }

  private emit(name: string, detail: Record<string, unknown>): void {
    const eventName = `lgs-video:${name}`;
    this.dispatchEvent(new CustomEvent(eventName, { bubbles: true, composed: true, detail }));
  }

  private track(event: string, extra: Record<string, unknown> = {}): void {
    if (!this.graph?.settings.analytics || !this.session || this.previewConfig) return;
    const item = {
      flowId: Number(this.graph.id) || 0,
      releaseVersion: this.graph.releaseVersion ?? 0,
      sessionId: this.session.id,
      event,
      nodeId: this.session.currentNodeId,
      occurredAt: new Date().toISOString(),
      context: { pagePath: window.location.pathname, deviceClass: deviceClass(), embedMode: this.mode },
      ...extra,
    };
    this.eventQueue.push(item);
    if (this.eventQueue.length >= 10) this.flushEvents();
    else if (!this.eventTimer) this.eventTimer = window.setTimeout(() => this.flushEvents(), 1500);
  }

  private flushEvents(): void {
    if (!this.eventQueue.length) return;
    const payload = JSON.stringify({ events: this.eventQueue.splice(0) });
    if (this.eventTimer) window.clearTimeout(this.eventTimer);
    this.eventTimer = null;
    if (navigator.sendBeacon) navigator.sendBeacon(`${this.restUrl}events`, new Blob([payload], { type: 'application/json' }));
    else fetch(`${this.restUrl}events`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true }).catch(() => {});
  }
}

if (!customElements.get('lgs-video-flow')) customElements.define('lgs-video-flow', LGSVideoFlowElement);

window.LGSVideo = {
  requestChapter(nodeId, options) {
    if (!options || typeof options.flow !== 'string' || !options.flow || !['after-current', 'immediate', undefined].includes(options.mode)) return false;
    const matches = Array.from(document.querySelectorAll<LGSVideoFlowElement>('lgs-video-flow')).filter((item) => item.flowSlug === options.flow);
    // Never pick an arbitrary duplicate embed or fall back to another journey.
    return matches.length === 1 && matches[0].requestChapter(nodeId, { mode: options.mode });
  },
  matchPortfolio(profile, slug) {
    const player = Array.from(document.querySelectorAll<LGSVideoFlowElement>('lgs-video-flow')).find(item => !slug || item.flowSlug === slug);
    return player?.getPortfolioMatches(profile) ?? null;
  },
  open(slug) { document.querySelectorAll<LGSVideoFlowElement>('lgs-video-flow').forEach((element) => { if (element.flowSlug === slug) element.open(); }); },
  close(slug) { document.querySelectorAll<LGSVideoFlowElement>('lgs-video-flow').forEach((element) => { if (element.flowSlug === slug) element.close(); }); },
  restart(slug) { document.querySelectorAll<LGSVideoFlowElement>('lgs-video-flow').forEach((element) => { if (element.flowSlug === slug) element.restart(); }); },
  setVariables(slug, variables) {
    document.querySelectorAll<LGSVideoFlowElement>('lgs-video-flow').forEach((element) => {
      if (element.flowSlug !== slug) return;
      if (variables.city) element.setAttribute('city', variables.city);
      if (variables.region) element.setAttribute('region', variables.region);
    });
  },
  getNeedProfile(slug) {
    const element = Array.from(document.querySelectorAll<LGSVideoFlowElement>('lgs-video-flow')).find((item) => item.flowSlug === slug);
    return element?.getNeedProfile() ?? null;
  },
};

export { LGSVideoFlowElement };
